/**
 * Interface language (ticket 10).
 *
 * The learner's pair decides the interface: explanations, feedback and every
 * label are in the same language (`en` for Catalan/Persian learners, `fa` for
 * English learners). The table itself lives on the server
 * (`server/src/languages.ts`) and reaches the app through `/api/health`; the
 * signed-in learner's own pair comes from `/api/auth/me`. Adding a language is
 * one row there plus one dictionary here.
 *
 * Direction: the document gets `lang`/`dir` from the interface language.
 * Target-language text keeps `dir="auto"` (or a `<bdi>`) so an English phrase
 * inside a Persian sentence still reads left-to-right, and answer inputs get
 * the target language's own direction.
 */
import { api, HttpError } from './api';
import type { LanguagePairInfo } from './types';

export type Lang = 'en' | 'fa';

const RTL = new Set(['fa', 'ar', 'he', 'ur', 'ps']);
const STORAGE_KEY = 'uiLang';

type Vars = Record<string, string | number>;

const en: Record<string, string> = {
  'research.title': 'Help improve Eaves',
  'research.explanationDefault': 'You agreed in person to take part, so Eaves records these content-free steps without asking on each new page: onboarding, the optional example, your first written or recorded moment and whether it saves, capture milestones, and when social updates are available, shown, opened and acted on. The app owner can read this evidence. Stop and clear removes it, and nothing more is collected unless you join again.',
  'research.explanation': 'If you join, Eaves records content-free steps through onboarding, the optional example and your first written or recorded moment, including whether it saves, plus capture milestones and when social updates are available, shown, opened and acted on. The app owner can read this evidence. A new page asks you to join again before collecting.',
  'research.neverCollected': 'These events never include your note, recordings, exercise answers, words, friend names, credentials, tokens or request contents. You can stop and clear research events at any time.',
  'research.agree': 'I agree to share these research events',
  'research.join': 'Join research',
  'research.stop': 'Stop and clear research events',
  'research.joining': 'Joining…',
  'research.stopping': 'Stopping…',
  'research.stopped': 'Collection is stopped on this device. The server could not confirm deletion, so evidence may remain on the server. Reconnect and try again.',
  'research.unavailable': 'Research settings could not be updated. Your learning still works normally.',
  'research.statusActive': 'Research events are on. You can stop and clear them at any time.',
  'replay.title': 'Optional screen replay',
  'replay.explanation': 'With separate permission, the app records the visible Eaves screen in this tab. Frames can include notes you wrote, lesson text and exercise screens so the owner can review the journey. They stay on this same server and only the owner can open playback. Sign-in, invitation, recovery and other token-bearing screens; passwords and credential fields; microphone audio; your desktop and other tabs; and app network request or response bodies are excluded. Playback is a simplified screen record and some interactive visuals may not look exactly as they did.',
  'replay.agree': 'I agree to record the visible screens in this tab',
  'replay.start': 'Start replay',
  'replay.stop': 'Stop recording',
  'replay.stopAndClear': 'Stop and clear replay',
  'replay.reset': 'Reset replay and continue',
  'replay.starting': 'Starting…',
  'replay.stopping': 'Stopping…',
  'replay.resetting': 'Resetting…',
  'replay.unavailable': 'Replay settings could not be updated. Recording is stopped in this tab.',
  'replay.inactive': 'Replay is off. Saved frames remain until they expire or you clear them.',
  'replay.active': 'Replay is recording visible screens in this tab. No microphone or desktop capture is active.',
  'replay.stopped': 'Replay stopped. Saved frames remain available to the owner until expiry or clear.',
  'replay.cleared': 'Replay stopped and saved frames cleared.',
  'replay.researchRequired': 'Join the learning-journey study first. Replay is a separate optional choice.',
  'replay.notActivated': 'Replay is not activated for this deployment. Nothing is recorded.',
  'replay.retention': 'Frames are kept for {days} days on this same server.',
  'replay.retentionOne': 'Frames are kept for 1 day on this same server.',
  'research.generationAgree': 'I also agree to let the owner review generation inputs, raw model attempts, generated pack and exam content, and the exact pack or exam served, for written and recorded moments.',
  'research.generationEnable': 'Allow pack and exam review',
  'research.generationDisable': 'Stop pack and exam sharing',
  'research.generationActive': 'The owner can review pack and exam generation records and served packs and exams. Turn this off at any time.',
  'research.generationUnavailable': 'Pack and exam sharing could not be updated. Try again.',
  'research.defaultNotice': 'To help improve Eaves, the app records how you use it, and the owner can review the packs and exams made for you. You agreed to this in person. You can stop and clear it any time.',
  'research.defaultNoticeReplay': 'To help improve Eaves, the app records how you use it and what is on your screen, and the owner can review the packs and exams made for you. Your notes and lesson text on screen are visible to the owner; passwords stay hidden. You agreed to this in person. You can stop and clear it any time.',
  'research.defaultStop': 'Stop',
  'research.defaultDismiss': 'OK',

  // ---- shared ----
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.sep': ', ',
  'common.dot': ' · ',
  'common.home': 'Home',
  'common.homeAria': 'Home',
  'common.retry': 'Retry',
  'common.dismiss': 'Dismiss',
  'lang.ca': 'Catalan',
  'lang.fa': 'Persian',
  'lang.en': 'English',
  'lang.es': 'Spanish',
  'lang.fr': 'French',
  'skill.read': 'reading',
  'skill.listen': 'listening',
  'skill.retrieve': 'recall',
  'skill.speak': 'speaking',

  // ---- join ----
  'join.title': 'Create your private account',
  'join.blurb': 'This invitation works once. Pick a username and password to return on any device.',
  'join.username': 'Username',
  'join.password': 'Password (at least 10 characters)',
  'join.target': 'Target language and explanation language',
  'join.pairOption': '{target} — explanations in {explain}; interface: {interface}, {direction}',
  'join.rtl': 'right to left',
  'join.ltr': 'left to right',
  'join.pairHint': 'Choosing a pair also changes the form’s language and reading direction. Your username and password stay filled in.',
  'account.pairTitle': 'Your language pair',
  'account.pair': 'Target: {target}. Explanations: {explain}. Interface: {interface}.',
  'account.pairFixed': 'This account keeps this pair for its lessons and progress. Ask the person who invited you if you chose the wrong pair.',
  'join.creating': 'Creating…',
  'join.create': 'Create account',
  'join.checking': 'Checking your invitation…',
  'join.deadHelp': 'Ask the person who invited you for a fresh link from the owner. If you already created an account, sign in.',

  // ---- sign in ----
  'signin.title': 'Sign in',
  'signin.blurb': 'Your learning history lives in your private account — sign in to pick up where you left off.',
  'signin.username': 'Username',
  'signin.password': 'Password',
  'signin.busy': 'Signing in…',
  'signin.submit': 'Sign in',
  'signin.accessHelp': 'Need an account or help getting back in?',
  'signin.invitationHelp': 'Access is by invitation. Ask the Eaves owner for an invitation link, then use it once to create your account.',
  'signin.recoveryHelp': 'Forgot your password? Contact the Eaves owner directly and ask them to create a recovery link. We do not check usernames here or send recovery email; the owner can explain when they can respond.',
  'signin.waitlist': 'Join the public waitlist',

  // ---- recover ----
  'recover.deadTitle': 'Recovery link unavailable',
  'recover.deadMsg': 'This recovery link is not valid any more.',
  'recover.deadHelp': 'Each link works once and only for a short while. Sign in if you remember your password; otherwise ask the Eaves owner directly for a new recovery link.',
  'recover.askOwner': 'The link may have expired, already been used, or been replaced. Ask the Eaves owner for a new one; this page cannot tell which happened.',
  'recover.goSignin': 'Go to sign in',
  'recover.doneTitle': 'Password set',
  'recover.returnUnavailable': "You're signed in. No saved destination was available in this browser. Saved destinations stay with the browser where they were recorded, so they may not follow recovery on another browser or device. Continue from home.",
  'recover.goHome': 'Go to your home',
  'recover.title': 'Set a new password',
  'recover.blurb': 'Your lessons, practice history and language stay exactly as they are — only your password changes. Every device that was signed in to this account will have to sign in again.',
  'recover.password': 'New password (at least 10 characters)',
  'recover.saving': 'Saving…',
  'recover.submit': 'Set password and sign in',
  'recover.offline': "Couldn't reach the server — try again in a moment.",

  // ---- onboarding ----
  'onboarding.title': 'Set up your learning',
  'onboarding.goalsTitle': 'What do you want to handle?',
  'onboarding.goalsHint': 'One minute, skippable — this tunes which phrases lessons pick first.',
  'onboarding.goalsPlaceholder': 'e.g. order at the market, chat with neighbours',
  'onboarding.priorTitle': 'Previous study',
  'prior.just starting': 'just starting',
  'prior.a few phrases': 'a few phrases',
  'prior.an evening class': 'an evening class',
  'prior.living here a while': 'living here a while',
  'onboarding.canDoTitle': 'I can already…',
  'cando.order_food': 'Order food or a coffee',
  'cando.follow_conversation': 'Follow a simple conversation',
  'cando.introduce': 'Introduce myself',
  'cando.ask_prices': 'Ask prices at the market',
  'onboarding.saveStart': 'Save goals & choose a real moment',
  'onboarding.saveCheck': 'Save goals & take the 3-minute check',
  'onboarding.checkProgress': 'Starting check · {done}/{total} · learning to place you, nothing is graded',
  'onboarding.skipRest': 'Skip the rest — start learning',
  'onboarding.play': '🔊 Play — answer from hearing',
  'onboarding.playing': '🔊 Playing…',
  'onboarding.typePlaceholder': 'Type it in the target language…',
  'onboarding.check': 'Check',
  'onboarding.nice': '✓ Nice.',
  'onboarding.notQuite': '✗ Not quite.',
  'onboarding.doneTitle': 'Starting estimate saved',
  'onboarding.doneSub': '({n} questions — provisional, not a certificate)',
  'onboarding.speakingNote': 'Speaking stays unknown until you speak — SayItBack and the voice exam feed it.',
  'onboarding.recordFirst': '● Record your first moment',
  // The two ways into a first moment (ticket 36), equal on the last screen.
  'onboarding.typeFirst': '✍️ Type your first moment',
  'ways.recordLine': 'People near you are talking. Record 30–60 seconds.',
  'ways.typeLine': "You wanted to say something and couldn't. Write it in your own words.",
  'onboarding.firstTitle': 'Your first moment',
  'onboarding.firstSub': 'Everything you learn here comes from a moment: something you heard, or something you wanted to say.',
  // Starting-estimate bands, as the learner reads them (never the raw id).
  'band.not_enough': 'not enough answers yet',
  'band.starting': 'just starting',
  'band.building': 'building up',
  'band.using': 'using it',

  // ---- home ----
  'home.setup': '🧭 Edit your learning goals',
  'home.setupNew': '🧭 Set goals and choose a first moment',
  'home.appInfo': 'App info',
  'home.estimate': 'Estimate',
  'home.version': 'Version',
  'home.mode': 'Mode',
  'home.modeMock': 'mock',
  'home.spent': 'Spent',
  'home.logout': '⎋ Log out',
  'home.progressAria': 'Learning progress',
  'home.forged': '⚒ {n} words forged: stone or gold in your town, recalled from their meaning days after you met them.',
  'home.forgedOne': '⚒ 1 word forged: stone or gold in your town, recalled from its meaning days after you met it.',
  'home.forgedNone': '⚒ No words forged yet: a word is forged when you recall it from its meaning days after you met it.',
  'home.active': '{n} active',
  'home.activeTitle': 'phrases in active review',
  // Home over the town: the top bar, its short forged count and the bottom panel's two ways in.
  'home.moments': 'Your moments',
  'home.createMoment': 'Create a moment',
  'home.tapHint': 'Tap a building to practise it',
  'home.forgedShort': '⚒ {n} forged',
  'home.forgedShortOne': '⚒ 1 forged',
  'home.draftsChip': '📝 Drafts need a look',
  'ways.record': 'Record',
  'ways.type': 'Type',
  'ways.listen': 'Listen',
  'ways.listenLine': 'Set me down near people talking — or just tell me what you wanted to say.',
  'ways.quill': 'Type instead',
  'dock.left': '{n} left today',
  'dock.leftOne': '1 left today',
  'listen.title': 'Eaves is listening',
  'listen.notice': 'Eaves listens through your microphone only while this is open. Your phone will ask once.',
  'listen.notice.ok': 'Okay, listen',
  'listen.done': 'Done',
  'listen.cancel': 'Cancel',
  'listen.dim': 'Tap to wake the screen',
  'listen.micRefused': "Eaves can't use the microphone here. Tell me what you wanted to say instead.",
  'listen.micFailed': "Couldn't open the microphone. Tell me what you wanted to say instead.",
  'listen.hearing': 'Hearing what you caught…',
  'listen.slow.q': 'This is taking a while. Who was talking?',
  'listen.who.me': 'Me, telling Eaves',
  'listen.who.near': 'People near me',
  'listen.unclear.silence': "I couldn't catch any words. Try closer, or tell me what you wanted to say.",
  'listen.unclear.music': 'I heard music, but no words. Try closer, or tell me what you wanted to say.',
  'listen.unclear.noise': 'I heard noise, but no words I could catch. Try closer, or tell me what you wanted to say.',
  'listen.again': 'Listen again',
  'listen.tell': 'Tell it instead',
  'listen.confirm.head': "I wasn't sure who was talking to whom.",
  'listen.confirm.ask': 'Was that you telling Eaves, or people near you?',
  'listen.confirm.guess': 'My guess: {makes}',
  'listen.asked.head': 'You told Eaves',
  'listen.asked.makes': 'Eaves will imagine a short scene in {target} around this.',
  'listen.eyebrow.heard': 'Eaves heard',
  'listen.tip.fix': 'Tap the words to fix them',
  'listen.tip.who': 'Tap a name to change who said it',
  'listen.retell.head': 'People talking in {language}. Make it in {target}?',
  'listen.retell.mine': 'That was you talking. Make a scene in {target} from it?',
  'listen.retell.makes': 'Eaves will retell it as a short scene in {target}.',
  'listen.overheard.many': 'Caught {n} lines from a few voices.',
  'listen.overheard.one': 'Caught {n} lines from someone talking.',
  'listen.overheard.line': 'Caught one line from people talking.',
  'listen.overheard.makes': 'Eaves will make a moment from these lines.',
  'listen.make.moment': 'Make the moment',
  'listen.make.scene': 'Make the scene',
  'listen.make.retell': 'Make it in {target}',
  'listen.notRight': 'Not right?',
  'listen.notRight.asked': 'That was me telling Eaves',
  'listen.notRight.overheard': 'That was people near me',
  'listen.line.who': 'Who said this',
  'listen.line.you': 'You',
  'listen.line.near': 'Near you',
  'listen.thread': 'What Eaves heard, in order',
  'listen.play': 'Play this part',
  'listen.wait.head': 'First hearing',
  'listen.wait.known': 'You already know {n} of these words',
  'listen.wait.known1': 'You already know 1 of these words',
  'listen.wait.making': 'Making your moment…',
  'listen.unheard.kept': "Kept on this phone. Open Listen when you're back online to hear it.",
  'listen.unheard.count': '{n} recordings are waiting to be heard.',
  'listen.unheard.dailyKept': '✓ Held on this device as a draft. It may be ready after {time} in {timezone}. Choose “Resume saved recording” on the town, then “Hear it now”.',
  'listen.unheard.betaKept': '✓ Held on this device as a draft. It may be ready when the beta’s shared AI budget resets at 00:00 UTC on {date}. Choose “Resume saved recording” on the town, then “Hear it now”.',
  'listen.unheard.aiKept': '✓ Held on this device as a draft. It may be ready when your AI allowance resets at 00:00 UTC on {date}. Choose “Resume saved recording” on the town, then “Hear it now”.',
  'listen.unheard.listenUnavailable': 'Listening is unavailable right now. Open drafts to check your saved recordings, or try again later.',
  'listen.unheard.uncertain': 'On this device; a previous reply was lost, so the moment may already exist. Choose “Resume saved recording” to continue.',
  'listen.unheard.unavailable': 'That saved recording is unavailable for this account on this device. Open drafts to check your saved recordings.',
  'listen.unheard.reviewKept': 'Your recording is saved on this device. Finish today’s review, then choose “Resume saved recording” on the town.',
  'listen.unheard.one': 'One recording is waiting to be heard.',
  'listen.unheard.hear': 'Hear it now',
  'error.hear-too-large': 'That recording is too long to hear. Try a shorter one.',
  'error.hear-limit': "That's a lot of listening for one day. Tell me what you wanted to say instead, or try again tomorrow.",
  'error.hear-failed': "Couldn't hear that just now. Try again, or tell me what you wanted to say.",
  'error.hear-gone': 'That recording is no longer held. Listen again.',
  'error.hear-start-limit': 'That recording has started too many lessons. Listen again.',
  'error.hear-start-failed': "Couldn't start the lesson just now. Try again.",
  'home.uploadedOne': 'Uploaded your saved lesson ✓',
  'home.uploadedMany': 'Uploaded {n} saved lessons ✓',
  'home.stillOffline': 'Still offline — everything stays saved on this device.',
  'home.exam': '🎭 Scenario exam ({n} words met)',
  'home.examLocked': '🎭 The scenario exam needs {needed} met words; {n} more to go.',
  'home.examLockedNext': '🎭 Another scenario exam needs {needed} met words; {n} more to go.',
  'home.examRecovery.first-look': 'A first look can meet another word now.',
  'home.examRecovery.clear-backlog': 'Clear today’s due work to make room for a first look.',
  'home.examRecovery.wait': 'No exam-ready first look is available right now. Keep practicing and check again when new material can be met.',
  'home.examRecovery.need-material': 'Make a moment to add words, then meet one in a first look.',
  'home.examRecovery.action.round': 'Open today’s round',
  'home.examRecovery.action.capture': 'Make a moment',
  'home.setupEntry': '🧭 Set up your learning: your goals and a 3-minute starting check',
  'home.setupEntryGoals': '🧭 Set up your learning: your goals, in a minute',
  'home.checkEntry': '🧭 Take the 3-minute starting check',
  'home.cantReach': "Can't reach the server.",
  'home.noMoments': 'No moments yet.',
  'home.search': 'Search lessons…',
  'home.searchAria': 'Search lessons',
  'home.filterAria': 'Lesson filter',
  'home.tabAll': 'All ({n})',
  'home.tabNew': '✨ New ({n})',
  'home.tabPracticed': "Started ({n})",
  'home.allCaught': 'All caught up — no new lessons.',
  'home.allCaughtHint': 'New recordings will land here.',
  'home.noPracticed': "No started lessons yet.",
  'home.noPracticedHint': "Choose a lesson to start.",
  'home.noMatch': 'No lessons match “{q}”.',
  'home.sectionNew': '✨ New — not practiced yet',
  'home.sectionPracticed': "Practice started",
  'home.showMore': 'Show more ({n} left)',
  'home.wantedPill': '✍️ wanted to say',
  'home.items': '{n} items',
  'home.itemsOne': '1 item',
  'home.tested': "{n} cards with saved activity",
  'home.newHere': '● new — start here',
  'home.allowanceLeft': '{remaining} of {limit} new moments left today',
  'home.allowanceNone': 'No new moments left today — practice and review stay open',
  'home.sharedPill': '🔓 shared',
  'home.sharedTitle': 'Shared with support — open this moment to stop sharing',

  // ---- monthly AI allowance (ticket 08) ----
  'ai.line': 'AI: {left} of {total} left this month',
  'ai.scope': 'Daily moment slots and monthly AI credit are separate. A free slot does not guarantee paid AI work is available.',
  'ai.allocationChanged': 'Your share of the beta’s monthly pool changed from {before} to {after}. The allocation change itself is not an AI charge.',
  'ai.reasonOwn': 'Your AI allowance for this month is used up — it resets at 00:00 UTC on {date}. Practising and reviewing what you already have stays open.',
  'ai.reasonBeta': "The beta's shared AI budget is used up — it resets at 00:00 UTC on {date}. Practising and reviewing what you already have stays open.",
  'ai.recordReason': 'Analysing a moment costs AI credit. {reason}',
  'ai.examReason': 'An exam is written fresh by the model — {reason}',
  'ai.refreshPack': '↻ New questions for this moment',
  'ai.refreshReason': 'New questions are written by the model. {reason}',
  'ai.pronReason': 'Pronunciation checks are scored by the model. {reason} Tap “I said it” on the card instead.',

  // ---- daily review requirement (ticket 09) ----
  'gate.lineOne': 'Review {n} card to unlock new moments today — {done} of {n} done',
  'gate.lineMany': 'Review {n} cards to unlock new moments today — {done} of {n} done',
  'gate.reviewNow': 'Review now',
  'gate.recordHint': 'Recording still works — what you record waits on this device until the review is done.',
  'gate.previewOne': "Today's first new moment starts with a quick review of {n} due card.",
  'gate.previewMany': "Today's first new moment starts with a quick review of {n} due cards.",
  'gate.keptAudio': "Your recording is held on this device as a draft. Finish today's review, then tap \"Check now\" in the list to see if it can be sent.",
  'gate.keptText': "Your text is held on this device as a draft. Finish today's review, then tap \"Check now\" in the list to see if it can be sent.",
  'gate.progress': "Today's review: {done} of {n} done",
  'gate.unlocked': 'Review done — new moments are unlocked for today.',
  'gate.moreToGo': '{remaining} more to go before new moments unlock today.',
  'gate.countsLabel': "Today's review counts",
  'gate.countRequirement': 'Daily requirement',
  'gate.countPending': '{done} of {required} answered · {remaining} left',
  'gate.countComplete': 'Complete · {required} of {required} answered',
  'gate.countNotFixed': 'Not set until the first new-moment request',
  'gate.countWaived': 'No review required today',
  'gate.countBatch': "Today's required batch",
  'gate.countQueue': 'Full due queue',
  'gate.countRound': "Today's displayed round",
  'gate.countCards': '{n} cards',
  'gate.localTime': 'your saved timezone',
  'gate.continue': 'Continue review',
  'gate.newMoment': 'New moment',
  'gate.noMic': 'No microphone here — this card asks the same thing another way.',

  // ---- record ----
  'record.title': 'New moment',
  'record.tabRecord': '● Record',
  'record.tabWanted': '✍️ Wanted to say',
  'record.stop': 'Stop',
  'record.rec': 'Record',
  'record.paused': 'paused — waiting for someone to talk',
  'record.recording': "Recording what's around you — up to {max}s of talk.",
  'record.skippedQuiet': ' Skipped {n}s of quiet.',
  'record.idleHint': 'Hold the phone naturally. Capture 30–60 seconds of people talking near you.',
  'record.skipSilence': 'Skip silences (pause automatically when nobody is talking)',
  'record.wantedTitle': 'What did you want to say?',
  'record.wantedHint': "Write it or say it, in English (mix in the language you're learning if you know some). I'll imagine a short scene where you say it, and take its words.",
  'record.wantedPlaceholder': 'e.g. I wanted to ask whether this train stops at Sants',
  // The example note follows the target (ticket 36): a place its learners know.
  'record.wantedPlaceholder.ca': 'e.g. I wanted to ask whether this train stops at Sants',
  'record.wantedPlaceholder.fa': 'e.g. I wanted to ask whether this metro goes to Tajrish',
  'record.wantedPlaceholder.es': 'e.g. I wanted to ask whether this train stops at Atocha',
  'record.wantedPlaceholder.en': 'e.g. I wanted to ask whether this bus goes to the airport',
  'record.wantedPlaceholder.fr': 'e.g. I wanted to ask whether this train stops at Gare du Nord',
  'record.turning': 'Turning it into phrases…',
  'record.turnIntoLesson': 'Turn it into a lesson',
  'record.makeScene': 'Make the scene',
  'record.savedHere': '✓ Saved on this device. You can leave this screen; keep the app open online in this account to send when eligible.',
  'record.leaveUnsaved': 'Your note, recording or Listen result is not saved. Discard it and leave? Cancel keeps you editing.',
  'record.recordingTitle': 'Recording',
  'record.listening': 'Listening…',
  'record.momentTitle': 'What was the moment?',
  'record.optional': '(optional)',
  'record.notePlaceholder': 'e.g. at the bakery, the owner and a regular, something about the football',
  'record.saveNote': 'This saves your moment to your town. Practice starts only when you choose it.',
  'record.noteHint': 'Anything you already know about who, where, or what helps the analysis connect the words to the situation.',
  'record.analysing': 'Analysing…',
  'record.recordAgain': 'Record again',
  'record.micNeeded': 'Microphone access is needed.',
  'record.sayIt': 'Say it instead',
  'record.sayMore': 'Say more',
  'record.sayStop': 'Done',
  'record.sayCancel': 'Cancel',
  'record.sayWriting': 'Writing down what you said…',
  'record.sayEmpty': "I couldn't make out any words — try again or type it.",
  'record.sayNoMic': "The microphone isn't available here, so type it instead.",
  'record.sayMicDenied': 'The microphone was refused, so type it instead (or allow it and try again).',
  'record.errOfflineAudio': "Couldn't reach the server — the recording is held on this device and sent while this app is open and online again.",
  'record.errServerAudio': 'The server stumbled — the recording is held on this device and retried while this app is open.',
  'record.errOfflineAudioNoSave': "No reply reached this device, so the lesson may already exist. This recording is only in this open screen; save it as a draft before leaving.",
  'record.errServerAudioNoSave': 'The server returned an uncertain result, so the lesson may already exist. This recording is only in this open screen; save it as a draft before leaving.',
  'record.errOfflineText': "Couldn't reach the server — the text is held on this device and sent while this app is open and online again.",
  'record.errServerText': 'The server stumbled — the text is held on this device and retried while this app is open.',
  'record.errOfflineTextNoSave': "No reply reached this device, so the lesson may already exist. This text is only in this open screen; save it as a draft before leaving.",
  'record.errServerTextNoSave': 'The server returned an uncertain result, so the lesson may already exist. This text is only in this open screen; save it as a draft before leaving.',
  'record.pendingAudio': '🎙 Recording',
  'record.pendingText': '✍️ Text',
  'record.tryOne': '1 try',
  'record.tryMany': '{n} tries',
  'record.seconds': '{n}s',
  'record.lastTry': 'Last try: {err}',
  'record.sending': 'Sending…',
  'record.uploadedOne': 'Uploaded your saved lesson ✓ — see Home.',
  'record.uploadedMany': 'Uploaded {n} saved lessons ✓ — see Home.',
  'record.allowanceLeft': '{remaining} of {limit} moments left today',
  'record.allowanceReason': 'Daily limit reached — new moments unlock at {time} in {timezone}. Practising and reviewing what you already have stays open.',
  'record.practiseInstead': 'Practise what you have',
  'record.allowanceSpent': "That's your new moments for today. Practice and review stay open.",

  // ---- the wait, while a new moment is analysed (v1 ticket 41) ----
  'wait.listening': 'Listening back to what you caught…',
  'wait.shared': 'Listening to the message you shared…',
  'wait.turning': 'Turning your note into {language}…',
  'wait.imagining': 'Imagining a short scene in {language} around your note…',
  'wait.long': 'Busy recordings can take half a minute.',
  'wait.longNote': 'This can take half a minute.',
  'wait.play': 'Play what you caught',
  'wait.pause': 'Pause',
  'wait.scene.who': "Imagining who you'll be talking to…",
  'wait.scene.lines': 'Writing their lines…',
  'wait.scene.turn': 'Putting your sentence at the turning point…',
  'wait.scene.translate': 'Translating each line…',
  'wait.rec.speakers': "Picking out who's speaking…",
  'wait.rec.writing': 'Writing down what they said…',
  'wait.rec.words': 'Finding the words worth keeping…',

  // ---- device drafts (ticket 12) ----
  'drafts.resumeRecording': 'Resume saved recording',
  'drafts.recordingKept': 'Your recording is saved on this device. Resume it to hear it and make your moment.',
  'drafts.title': '📝 Drafts on this device ({n} of {max})',
  'drafts.hint': 'Kept privately on this device: up to {max} drafts, recordings up to {mb} MB, text up to {chars} characters. Eaves sends them only while this app is open and online. Nothing is sent while the app is closed.',
  'drafts.hintAccount': 'In beta, sign in as the account that saved each draft to send it.',
  'drafts.count': 'Drafts on this device: {n} of {max}',
  'drafts.stateLocal': 'On this device only — not sent yet. Sending needs this app open and online. Tap "Send now".',
  'drafts.stateUnclear': 'On this device; the last answer never arrived, so the lesson may already exist. Send now settles it — one lesson at most.',
  'drafts.stateDaily': "On this device. Waiting: today's new moments are used up — it can be sent after {time} in {timezone}, while this app is open.",
  'drafts.stateReview': "On this device. Waiting for today's review — it can be sent as soon as you finish it.",
  'drafts.stateAiOwn': 'On this device. Waiting: your AI allowance for this month is used up — it can be sent after 00:00 UTC on {date}, while this app is open.',
  'drafts.stateAiBeta': "On this device. Waiting: the beta's shared AI budget is used up — it can be sent after 00:00 UTC on {date}, while this app is open.",
  'drafts.stateFailed': "On this device, and it can't become a lesson: {why}. You can delete it.",
  'drafts.whyAccount': 'it was saved under another account',
  'drafts.whyLanguage': 'it was saved for another language',
  'drafts.whyTooLarge': 'This recording exceeds the {mb} MB limit for a draft on this device.',
  'drafts.readError': "Couldn't read drafts on this device. They may still be here; try again before recording or writing something new.",
  'drafts.continueWithoutRead': 'Continue without checking drafts',
  'drafts.whyRejected': 'the server turned it down ({err})',
  'drafts.completed': '✓ Became a lesson',
  'drafts.reconciledOne': 'That lesson had already reached the server ✓ — opening it. Nothing was sent twice.',
  'drafts.reconciledMany': '{n} of these had already reached the server ✓ — opening one. Nothing was sent twice.',
  'home.reconciledOne': 'That lesson had already reached the server ✓ — nothing was sent twice.',
  'home.reconciledMany': '{n} of these had already reached the server ✓ — nothing was sent twice.',
  'drafts.removed': 'Already became a lesson that you later deleted — nothing was sent again.',
  'drafts.open': 'Open',
  'drafts.dismiss': 'Dismiss',
  'drafts.tryNow': 'Send now',
  'drafts.sendNow': 'Send now',
  'drafts.checkNow': 'Check now',
  'drafts.tryAll': 'Send all now',
  'drafts.delete': 'Delete',
  'drafts.deleteAria': 'Delete this draft',
  'drafts.confirmDelete': 'Delete this draft from this device? This cannot be undone.',
  'drafts.keep': 'Keep',
  'drafts.deleted': 'Draft deleted from this device.',
  'drafts.saveAsDraft': '💾 Save as a draft',
  'drafts.savedDaily': "✓ Held on this device as a draft. It may be ready after {time} in {timezone}; while this app is open, tap \"Check now\" to try.",
  'drafts.savedAiOwn': '✓ Held on this device as a draft. It may be ready when your AI allowance resets at 00:00 UTC on {date}; while this app is open, tap "Check now" to try.',
  'drafts.savedAiBeta': "✓ Held on this device as a draft. It may be ready when the beta's shared AI budget resets at 00:00 UTC on {date}; while this app is open, tap \"Check now\" to try.",
  'drafts.full': 'This device already holds {max} drafts for your account. Delete one below, then save this one.',
  'drafts.tooLong': 'Too long to keep as a draft — {max} characters at most.',
  'drafts.cantSave': "Couldn't keep this on the device ({err}). Keep this screen open and try again.",
  'drafts.noOwner': "Couldn't reach the server, and this device doesn't know yet who is signed in, so it can't keep a draft. Keep this screen open and try again.",
  'drafts.countPending': '{n} held on this device, not sent',
  'drafts.countUnclear': '{n} may already be lessons — Send now checks before retrying',
  'drafts.countBlocked': '{n} waiting for a limit to lift',
  'drafts.countFailed': "{n} can't be sent",
  'drafts.manage': 'Manage drafts',
  'drafts.needsAccount': 'Sign in as the account that saved these drafts to send them.',
  'drafts.stillWaiting': 'Not yet — nothing was accepted, and your drafts stay on this device.',
  'drafts.logoutOne': 'You have 1 draft on this device',
  'drafts.logoutMany': 'You have {n} drafts on this device',
  'drafts.logoutBody': "They haven't become lessons yet. Kept drafts stay hidden and unsent while you're signed out, and come back when you sign in again with this account.",
  'drafts.logoutDelete': 'Delete my drafts and sign out',
  'drafts.logoutKeep': 'Keep them on this device for when I sign back in',
  'drafts.logoutCancel': 'Cancel',

  // ---- moment ----
  'moment.delete': 'Delete',
  'moment.panelUp': 'Show the whole moment',
  'moment.panelDown': 'Show the town again',
  'moment.textPill': '✍️ wanted to say — no recording',
  'moment.practice': '✏️ Practice',
  'moment.whatGoingOn': 'What was going on',
  'moment.yourNote': 'Your note: “{note}”',
  'moment.confidence': 'confidence {n}%',
  'moment.confidenceHint': 'Confidence is how sure the analysis is that it caught what was said — lower means more guessing.',
  'moment.confidenceHintTyped': 'Confidence is how sure the analysis is that it understood what you wanted to say — lower means more guessing.',
  'moment.sample': 'sample data',
  'moment.soFar': '${usd} so far',
  'moment.replies': 'What could I have said?',
  // Seeds (v1 ticket 09): a kept reply is planted with the new words; the rest stay on the moment.
  'moment.repliesHint': 'Keep the ones you want to be able to say. A kept reply joins your new words and comes back in review; the rest stay here.',
  'moment.keepSeed': '🌱 Keep',
  'moment.keptSeed': '✓ Kept',
  'moment.keepSeedTitle': 'Keep this reply to learn it',
  'moment.unkeepSeedTitle': 'Kept. Tap to stop learning it',
  'moment.seedIsWord': 'Already one of your words',
  // The climb (v1 ticket 29): what opens a chunk, sentence or pattern held back. {words} holds target words, isolated.
  'climb.opens': 'Opens when you know {words}',
  'climb.opensSome': 'Opens when you know {n} of {words}',
  'climb.opensLines': 'Opens when {need} of your lines hold it (now {n})',
  'climb.pattern': 'Pattern: {words}, in {n} of your lines',
  'climb.comma': ', ',
  'climb.or': ' or ',
  'climb.and': ' and ',
  'moment.learnFirst': 'Learn first',
  'moment.learnFirstHint': 'Worth your attention first — the rest can wait.',
  'moment.forged': '⚒ forged',
  'moment.whatTheySaid': 'What they said',
  'moment.whatYouWanted': 'What you wanted to say',
  'moment.speakerYou': 'You',
  'moment.imaginedScene': 'An imagined scene',
  'moment.imaginedNote': 'Made up around what you asked for.',
  'moment.imaginedPill': '✍️ imagined from your note',
  'moment.playScene': 'Play the scene',
  'moment.preparingScene': 'Preparing the scene…',
  'moment.yourTurn': 'Your turn',
  'moment.turnLocked': 'Your turn opens once the words in your lines are out of the fog: {n} are still in it',
  'moment.turnLockedOne': 'Your turn opens once the last word in your lines is out of the fog',
  'moment.turnSaid': 'I said it',
  'moment.turnShow': 'Show me',
  'moment.turnAll': 'You said it all.',
  'moment.turnAgain': 'Again',
  'tend.hearScene': 'Hear the scene',
  'land.imagined': 'imagined',
  'rude.tag': 'rude',
  'rude.mild': 'rude, mild',
  'rude.strong': 'rude, strong',
  'moment.showAll': 'show all',
  'moment.hideTranslations': 'hide translations',
  'moment.tapLine': 'Tap a line to reveal its translation, 🔊 to hear it.',
  'moment.blueprintsHint': 'Your words are marked. These are the sentences your cards ask them in. A faint mark is a word still in the fog, waiting for its first look.',
  'moment.fogMarkTitle': '{meaning} (still in the fog)',
  // Lines where the learner knows every word (v1 ticket 31); never "understand".
  'known.count': 'You know every word in {n} of these {total} lines',
  'known.all': 'You know every word in all {total} of these lines',
  'known.one': 'You know every word in this line',
  'known.why': "A line counts when each word in it is out of the fog, a little word you've heard 10 times, or part of an expression you know.",
  'known.mark': 'You know every word in this line',
  'known.markNew': 'New: you now know every word in this line',
  'known.listen': 'Listen to these lines',
  'moment.noRecording': 'No recording for this one — the phrases below are what to use next time.',
  'moment.words': 'Words',
  'moment.expressions': 'Expressions',
  'moment.literally': 'literally: {text}',
  'moment.mission': '🎯 Try next time',
  'moment.tried': '✓ Tried it',
  'moment.understood': '👂 Understood the reply',
  'moment.stuck': '😅 Got stuck',
  'moment.reflectionPlaceholder': 'What happened? (optional — e.g. what they said back)',
  'moment.saveReflection': 'Save reflection',
  'moment.startNext': 'Start next lesson from this',
  'moment.reflectionPrivate': 'Personal only — reflections never affect scores or review.',
  'moment.markOne': 'Mark at least one of tried / understood / got stuck.',
  'moment.savedStuck': 'Saved — your note below can start the next lesson.',
  'moment.savedNice': 'Saved — nice work getting out there.',
  'moment.unclear': 'Unclear',
  'moment.deleteConfirm': 'Delete this whole moment: its lesson, progress and recording?',
  'moment.deleteFailed': "Couldn't delete this moment — check your connection and try again.",
  'moment.deleteRefused': "Couldn't delete this moment. The server turned it down.",
  'moment.listen': 'Listen',
  'moment.stopListen': 'Stop',
  'triage.active': 'Learn this',
  'triage.activeTitle': 'Keep in review',
  'triage.known': 'Already know it',
  'triage.knownTitle': 'Remove from review',
  'triage.later': 'Save for later',
  'triage.laterTitle': 'Park for now',
  'triage.removed': '✕ Remove',
  'triage.removedTitle': 'Wrong or not in the target language — remove from learning, including future lessons',
  'triage.isRemoved': '✕ Removed from learning.',
  'triage.bringBack': 'Bring back',
  'triage.bringBackTitle': 'Restore this phrase to learning',

  // ---- share with support ----
  'share.title': 'Share with support',
  'share.start': 'Share with support',
  'share.stop': 'Stop sharing',
  'share.since': '🔓 Shared with support since {date}',
  'share.sharedHint': 'The owner can open this one moment, including its recording, until you stop sharing.',
  'share.offerHint': 'Lets the owner open this one moment, including its recording, until you stop sharing. Nothing else in your account is visible.',

  // ---- circles (beta, v1 ticket 16) ----
  'home.circle': '👥 Friends',
  'activity.nav': 'Updates',
  'activity.navCount': 'Updates: {actionable} actionable, {unread} unread',
  'activity.title': 'Social updates',
  'activity.intro': 'Friend requests, armies, battle reports and spoils stay here until you act on them. Opening one marks it seen; it does not accept a request, defend or rebuild anything.',
  'activity.empty': 'No social updates yet.',
  'activity.return': 'Back to updates',
  'activity.open': 'Open',
  'activity.view': 'View',
  'activity.unread': 'Unread · available to act on',
  'activity.seen': 'Seen · still available to act on',
  'activity.resolved': 'Resolved',
  'activity.withdrawn': 'Withdrawn',
  'activity.expired': 'Expired',
  'activity.cancelled': 'Cancelled',
  'activity.indicatorShown': 'Update indicator shown',
  'activity.availableAt': 'Available {date}',
  'activity.friendRequest': '{name} wants to be friends',
  'activity.army': '{name} sent an army · {count} weapons',
  'activity.report': 'Battle report with {name} · {band}',
  'activity.spoilsReady': 'Spoils from {name} · ready to rebuild',
  'activity.spoilsWaiting': 'Spoils from {name} · waiting to open',
  'activity.spoilsDone': 'Spoils from {name}',
  'activity.outcome.accepted': 'Request accepted',
  'activity.outcome.declined': 'Request declined',
  'activity.outcome.defended': 'Army defended',
  'activity.outcome.auto_played': 'Army played itself',
  'activity.outcome.report_read': 'Report read',
  'activity.outcome.rebuilt': 'Word rebuilt',
  'activity.outcome.withdrawn': 'Request withdrawn',
  'activity.outcome.expired': 'Request expired',
  'activity.outcome.cancelled': 'Army cancelled',
  'activity.outcome.planted': 'Word planted',
  'activity.outcome.dropped': 'Spoils expired',
  'circle.title': 'Friends',
  'circle.loading': 'Loading…',
  'circle.introTitle': 'Learn with friends',
  'circle.intro': "A circle is up to {max} friends. You see each other's towns and glory and can raid each other's towns. Words stay private except the same-pair spoils exception in the sharing details below.",
  'circle.start': 'Start a circle',
  'circle.haveCode': 'Have a code?',
  'circle.codeLabel': 'Code',
  'circle.join': 'Continue',
  'circle.consentStart': 'Before you start a circle',
  'circle.consentJoin': 'Before you join',
  'circle.sharingSummary': 'Friends can see your username, your practice progress and battle results.',
  'circle.sharingSpoils': 'When you raid a friend learning the same language pair, a broken weapon may reveal its word, pronunciation and meaning if that word is new to them.',
  'circle.sharingPrivate': 'Your other words, lesson titles, notes and recordings stay private.',
  'circle.sharingDetails': 'See the full sharing details',
  'circle.seeTitle': 'Friends will see:',
  'circle.seeTown': 'your town, as it is drawn',
  'circle.seeGlory': 'your glory',
  'circle.seeDistricts': 'the kinds of districts in your town',
  'circle.seeName': 'your username',
  'circle.seeRaid': 'in a raid: your name, your glory, for each district how many words stand in each material, its ivy, lamps and paved streets (never its title or words), each weapon\'s material and whether it was blocked',
  'circle.seeSpoils': 'in a raid with a friend learning the same language pair: if a weapon breaks and its word is new to them, they may see that word, its pronunciation and meaning (never a line or a moment)',
  'circle.neverTitle': 'They never see:',
  'circle.neverWords': 'any other word of yours, or its meaning',
  'circle.neverTitles': 'your lesson titles or notes',
  'circle.neverRecordings': 'your recordings',
  'circle.leaveAnyTime': 'You can leave at any time, and leaving takes effect at once.',
  'circle.agreeStart': 'I agree — start the circle',
  'circle.agreeJoin': 'I agree — join',
  'circle.cancel': 'Not now',
  'circle.count': '{n} of {max} members',
  'circle.keeper': 'keeper',
  'circle.you': 'you',
  'circle.codeTitle': 'Your circle code',
  'circle.codeHint': 'Send the link to your friends, or read the code out. It is shown only now, so share it before you leave this screen.',
  'circle.share': 'Share the link',
  'circle.copyLink': 'Copy the link',
  'circle.linkCopied': 'Link copied ✓',
  'circle.copy': 'Copy the code',
  'circle.copied': 'Copied ✓',
  'circle.newCode': 'Make a new code',
  'circle.newCodeHint': 'The old code stops working at once.',
  'circle.noCode': 'There is no working code. Make one to invite friends.',
  'circle.askKeeper': 'To invite someone, ask {name} for the code.',
  'circle.leave': 'Leave the circle',
  'circle.leaveConfirm': 'Leave the circle? Friends stop seeing your town at once.',
  'circle.leaveConfirmKeep': 'Leave the circle? Its members stop seeing your town at once, except those who are also your friends directly.',
  'circle.leaveYes': 'Leave',
  'circle.leaveNo': 'Stay',
  'circle.left': 'You left the circle.',
  // ---- friends by a one-time link (beta) ----
  'friends.add': 'Add a friend',
  'friends.addTitle': 'Add a friend',
  'friends.addHint': 'Send a friend a link. When they agree, you are friends and can raid each other.',
  'friends.groupTitle': 'Learning with a group?',
  'friends.listTitle': 'Your friends',
  'friends.circleMark': 'circle',
  'friends.circleTitle': 'Your circle',
  'friends.consentTitle': 'Before you add a friend',
  'friends.agree': 'I agree',
  'friends.dontShow': "Don't show this again",
  'friends.whatSee': 'What friends see',
  'friends.agreeBe': 'I agree — be friends',
  'friends.make': 'Make a link',
  'friends.makeHint': 'The link works once, for one person, for {days} days.',
  'friends.linkTitle': 'Your friend link',
  'friends.linkHint': 'It works once, for one person, for {days} days. It is shown only now, so send it before you leave this screen.',
  'friends.linkCodeHint': 'If the link opens in the wrong place, your friend can type this code on their Friends screen.',
  'friends.copyByHand': 'Copy this link yourself:',
  'friends.ownCode': 'This is your own code. Send it to a friend.',
  'friends.linkGone': 'This link is no longer open. If your friend used it, they are in your list now.',
  'friends.openTitle': 'Your open links',
  'friends.openLine': 'Link made {made}, open until {until}',
  'friends.withdraw': 'Withdraw',
  'friends.withdrawn': 'Link withdrawn.',
  'friends.done': 'Done',
  'friends.wants': '{name} wants to be friends',
  'friends.bothWays': 'It goes both ways: you will each see this about the other.',
  'friends.nowFriends': 'You and {name} are friends now.',
  'friends.already': 'You and {name} are already friends.',
  'friends.toFriends': 'Go to Friends',
  'friends.tryAgain': 'Try again',
  'friends.leaveFresh': 'This link is shown only now. Leave without sending it?',
  'friends.withdrawLabel': 'Withdraw the link made {made}',
  'friends.yourName': 'Your username: {name}',
  'friends.askTitle': 'Ask by username',
  'friends.askHint': "Type your friend's exact username. You will see \"Request sent\" whatever you type, so this never shows who has an account.",
  'friends.askLabel': 'Their username',
  'friends.send': 'Send request',
  'friends.requestSent': 'Request sent',
  'friends.openTitleBoth': 'Your open links and requests',
  'friends.requestLine': 'Request to {name}, open until {until}',
  'friends.withdrawRequestLabel': 'Withdraw the request to {name}',
  'friends.requestsTitle': 'Friend requests',
  'friends.accept': 'Accept',
  'friends.decline': 'Decline',
  'friends.acceptLabel': 'Accept the request from {name}',
  'friends.declineLabel': 'Decline the request from {name}',
  'friends.wantMany': '{n} people want to be friends',
  'friends.seeRequests': 'See',
  'friends.requestWithdrawn': 'Request withdrawn.',
  'friends.circleMarkLabel': 'in your circle',
  'friends.endAnyTime': 'You can end a friendship at any time, and it takes effect at once.',
  'friends.moreFor': 'More for {name}',
  'friends.endConfirm': '{name} stops seeing your town at once.',
  'friends.end': 'End friendship',
  'friends.keep': 'Keep',
  'friends.ended': 'You and {name} are no longer friends.',
  'raid.title': 'Raid',
  'raid.newTitle': 'Raid on {name}',
  'raid.fromTitle': "{name}'s raid",
  'raid.glory': 'glory',
  'raid.raid': 'Raid',
  'raid.waiting': 'something waits',
  'raid.block.pending': 'Your army is already on its way to them.',
  'raid.block.full': 'Too many armies are at their gates already.',
  'raid.block.no-town': 'They have no words to defend yet.',
  'raid.block.limit': "You've sent today's armies. More tomorrow.",
  'raid.ready.title': 'Before you raid',
  'raid.ready.yours': 'You',
  'raid.ready.ok': 'Ready',
  'raid.ready.theirOk': 'Open to a raid',
  'raid.ready.you.no-words': 'No word of yours is out of the fog yet.',
  'raid.ready.you.resting': 'Every word you could use is resting after a broken weapon.',
  'raid.ready.you.limit': "You've sent today's armies. More tomorrow.",
  'raid.ready.you.pending': 'Your army is already on its way to them.',
  'raid.ready.them.full': 'Too many armies are at their gates already.',
  'raid.ready.them.no-town': 'They have no words to defend yet.',
  'raid.ready.act.capture': 'Practise a word first',
  'raid.ready.act.army': 'See your army',
  'raid.ready.act.friends': 'Pick another friend',
  'raid.preview.badge': 'Preview',
  'raid.preview.note': 'A practice battle with made-up words. No friend, glory, score or learning record is touched.',
  'raid.preview.win.addFriend': 'Add a friend',
  'friends.previewTitle': 'Try a practice battle',
  'friends.previewHint': 'See how a raid works with practice words. Nothing here counts and nobody is told.',
  'raid.atGates': 'At your gates',
  'raid.armyOf': "{name}'s army",
  'raid.scoutTitle': 'Scout {name}',
  'raid.scoutHint': "Pick a district to aim at. You see only how each one stands, never its words.",
  'raid.scoutConditionHint': 'Condition is a broad readiness signal, not a damage forecast. A correct live answer can still block a strike.',
  'raid.conditionLabel': 'Readiness',
  'raid.condition.maintained': 'Well tended',
  'raid.condition.mixed': 'Mixed readiness',
  'raid.condition.neglected': 'Needs tending',
  'raid.condition.unproven': 'Not enough history yet',
  'raid.scoutNone': 'Nothing to scout yet: they have no districts.',
  'raid.districtN': 'District {n}',
  'raid.aimedAt': 'Aimed at district {n}',
  'raid.aimedAtOwn': 'Aimed at your district {title}',
  'raid.rescout': 'Choose another district',
  'raid.ivy': '{n} with ivy',
  'raid.ivyOne': '1 with ivy',
  'raid.lamps': '{n} lamps lit',
  'raid.lampOne': '1 lamp lit',
  'raid.streetsPaved': '{paved} of {streets} streets paved',
  'raid.fort.aria': 'Fort. Stone walls: {paved} of {walls}. Towers: {towers}. Ivy: {ivy}.',
  'raid.fort.legend': 'Stone walls: lines where you know every word. Towers: words practised this week. Ivy: words overdue for review.',
  'raid.fort.legendTheirs': 'Stone walls: lines where they know every word. Towers: words they practised this week. Ivy: their words overdue for review.',
  'raid.canTake': 'can take {n} weapons',
  'raid.canTakeOne': 'can take 1 weapon',
  'raid.cantTake': 'no word to hit here yet',
  'raid.limit': '{n} of at most {max} weapons',
  'raid.limitOne': 'At most 1 weapon here',
  'raid.weapons': '{n} weapons',
  'raid.weaponsOne': '1 weapon',
  'raid.defend': 'Defend',
  'raid.onTheirWay': 'On their way',
  'raid.draft': 'Your army for {name} is not sent yet.',
  'raid.continue': 'Continue',
  'raid.armyTo': 'To {name}',
  'raid.reports': 'Reports',
  'raid.unread': 'new',
  'raid.read': 'Read',
  'raid.home.army': "{name}'s army is at your gates: {n} weapons.",
  'raid.home.armyOne': "{name}'s army is at your gates: 1 weapon.",
  'raid.home.reportOn': 'Your raid on {name} is over: the report is in.',
  'raid.home.reportFrom': '{name} raided your town: the report is in.',
  'raid.reportOn': 'Your raid on {name}',
  'raid.reportFrom': "{name}'s raid on you",
  'raid.weapon.choice': 'Arrows',
  'raid.weapon.listen': 'Dragon',
  'raid.weapon.dictation': 'Storm',
  'raid.weapon.gap': 'Catapult',
  'raid.weapon.recall': 'Spell',
  'raid.strike.choice': '{material} arrows',
  'raid.strike.listen': 'A {material} dragon',
  'raid.strike.dictation': 'A {material} storm',
  'raid.strike.gap': 'A {material} catapult',
  'raid.strike.recall': 'A {material} spell',
  'raid.mat.scaffolding': 'scaffolding',
  'raid.mat.wood': 'wooden',
  'raid.mat.stone': 'stone',
  'raid.mat.gold': 'golden',
  'raid.band.held': 'Held',
  'raid.band.draw': 'Draw',
  'raid.band.breach': 'Breach',
  'raid.band.sacked': 'Sacked',
  'raid.verdict.blocked': 'blocked',
  'raid.verdict.scratched': 'scratched',
  'raid.verdict.hit': 'hit',
  'raid.verdict.missed': 'missed',
  'raid.out.attacker.held': 'Their town held.',
  'raid.out.attacker.draw': 'A draw.',
  'raid.out.attacker.breach': 'You breached the walls.',
  'raid.out.attacker.sacked': 'You sacked the town.',
  'raid.out.defender.held': 'Your town held.',
  'raid.out.defender.draw': 'A draw.',
  'raid.out.defender.breach': 'Your walls were breached.',
  'raid.out.defender.sacked': 'Your town was sacked.',
  'raid.damageLine': '{damage} of {total} power got through.',
  'raid.auto': 'Nobody answered within a day, so the weapons left played themselves.',
  'raid.raidBack': 'Raid back',
  'raid.raidAgain': 'Raid again',
  'raid.defenceBreakdown': 'Your answers, word materials and lit lamps together prevented {prevented} power. Missed targets cause no damage.',
  'raid.damageCapped': 'The total damage is capped at half the combined health of the targeted words.',
  'gate.resetAt': 'This requirement resets at {time} in {timezone}.',
  'raid.toFriends': 'Friends',
  'raid.rulesChanged': 'Raid rules changed, so this unsent army was cancelled. Its previous answers were not counted as learning. Prepare a new army.',
  'raid.expired': 'This army preparation expired. Your learning and word rest remain saved; prepare a new army or practise.',
  'raid.gone': 'This raid was called off.',
  'raid.left.head': 'Left on the field',
  'raid.left.row': 'left on the field',
  'raid.spoils.line': '{name} left {word} on the field in {district}. Rebuild it tomorrow to keep it.',
  'raid.spoils.lineBare': '{name} left {word} on the field. Rebuild it tomorrow to keep it.',
  'raid.spoils.salvaged': 'You took {word} from {name}.',
  'raid.spoils.planted': '{word} was left in your fog.',
  'raid.home.spoilsOne': '1 word to rebuild',
  'raid.home.spoils': '{n} words to rebuild',
  'raid.spoils.go': 'Rebuild',
  'raid.spoils.title': 'Words left on the field',
  'raid.spoils.intro': 'A friend\'s broken weapon left these behind. Rebuild a word from its meaning to keep it; one try a day.',
  'raid.spoils.from': 'Left by {name}',
  'raid.spoils.in': 'in {district}',
  'raid.spoils.undo': 'Clear',
  'raid.spoils.check': 'Check',
  'raid.spoils.right': 'Rebuilt. It is yours now, and due tomorrow.',
  'raid.spoils.wrong': 'Not that order. It was {word}. Try again tomorrow.',
  'raid.spoils.empty': 'Nothing to rebuild right now.',
  'raid.spoils.later': '{n} more come later.',
  'raid.spoils.laterOne': '1 more comes later.',
  'raid.spoils.done': 'Done',
  'raid.spoils.built': 'Your word so far',
  'town.trophy': 'Taken from {name}',
  'finale.sacked': 'Sacked',
  'finale.breach': 'Breached',
  'finale.draw': 'A draw',
  'finale.held': 'Held',
  'finale.sub.sacked': "{name}'s banner flies over {town}.",
  'finale.sub.breach': "{name}'s banner is planted at your gate.",
  'finale.sub.draw': 'Neither side gave way.',
  'finale.sub.held': 'Your walls held against {name}.',
  'banner.title': "{name}'s banner",
  'banner.label': "{name}'s banner. Tap to see what it takes to bring it down.",
  'banner.sheet': "{name}'s banner flies here. Repair the cracked words to take it down.",
  'banner.words': 'Cracked words',
  'banner.repair': 'Repair the cracked words',
  'banner.strike': 'Strike back',
  'cracks.bar': 'Repair bar: how much of the district stands',
  'cracks.hit': 'Hit',
  'cracks.building': "Hit in {name}'s raid. Getting this word right repairs it.",
  'cracks.district': "Hit in {names}'s raid. Right answers on its words repair it, {pct}% stands.",
  'end.head.repaired': '{n} cracks repaired',
  'end.head.repairedOne': '1 crack repaired',
  'end.chip.repaired': 'repaired',
  'error.spoil-not-ready': 'That word is not ready to rebuild yet.',
  'error.spoil-tried-today': 'You have tried that word today. Try again tomorrow.',
  'raid.settling': 'Every card is answered. The report is being written.',
  'raid.refresh': 'Show the report',
  'raid.gates': "{name}'s army is at your gates",
  'raid.starter': 'Use a starter army',
  'raid.starterHint': 'Start with up to three available words and lighter exercises. Change any choice below before loading.',
  'raid.clearSelection': 'Clear selection',
  'raid.formLabel.choice': 'Choose meaning',
  'raid.formLabel.listen': 'Listen + choose',
  'raid.formLabel.gap': 'Fill the gap',
  'raid.formLabel.recall': 'Write the word',
  'raid.formLabel.dictation': 'Listen + write',
  'raid.defHint': 'Answer on your own word to block it.',
  'raid.landsAs.choice': 'It lands as arrows: this word is still new.',
  'raid.landsAs.listen': 'It lands as a dragon: this word is still new.',
  'raid.landsAs.recall': 'It lands as a spell: this word is still new.',
  'raid.landsAs.gap': 'It lands as a catapult: this word is still new.',
  'raid.def.blocked': 'Blocked. No damage.',
  'raid.def.scratched': 'Scratched: {damage} damage got through.',
  'raid.def.hit': 'Hit: {damage} damage.',
  'raid.def.noDamage': 'It got through, but did no damage.',
  'raid.learningSaved': 'Your answer was saved as practice.',
  'raid.recoverySaved': 'Your answer was saved. An easier recovery is ready in practice.',
  'raid.toReport': 'See the report',
  'raid.battle.walls': 'Walls {n}',
  'raid.battle.gaps': 'Gaps {n}',
  'raid.battle.towers': 'Watchtowers {n}',
  'raid.battle.raising': 'Raising your walls…',
  'raid.chip.blocked': 'blocked',
  'raid.chip.scratched': 'scratched −{damage}',
  'raid.chip.hit': 'hit −{damage}',
  'raid.wasIt': 'It was:',
  'raid.absorbed': 'Walls took most of it',
  'raid.wallsTook': 'Your walls took the rest.',
  'raid.nextWeapon': 'Next weapon',
  'raid.missedOne': '1 weapon missed: nothing to answer for it.',
  'raid.missedNote': '{n} weapons missed: nothing to answer for them.',
  'raid.kicker': 'Weapon {n} of {total}',
  'raid.loadHint': 'Answer it to load this weapon.',
  'raid.loaded': 'Loaded.',
  'raid.loadedScratch': 'Loaded, with a spelling slip.',
  'raid.loadedScratchPractice': 'Full power counts. A spelling card is waiting in practice.',
  'raid.broke': 'It broke, and stays home.',
  'raid.recoveryPractice': 'Practise the easier recovery',
  'raid.practise': 'Practise',
  'raid.expiry': 'This preparation expires within {hours} hours of its first successful proof, at {time}.',
  'raid.newPreparation': 'Prepare a new army',
  'raid.spellingPractice': 'Open spelling practice',
  'raid.toSummary': 'See the army',
  'raid.summaryTitle': 'Your army for {name}',
  'raid.noneLoaded': 'No weapon loaded, so there is no army to send.',
  'raid.summaryOne': 'Your one weapon is loaded.',
  'raid.summaryCount': '{n} of {total} weapons loaded.',
  'raid.row.loaded': 'loaded',
  'raid.row.broken': 'broken',
  'raid.send': 'Send the army',
  'raid.rebuild': 'Build another army',
  'raid.edit': 'Change the army',
  'raid.editNote': 'Weapons you already loaded stay loaded.',
  'watch.marches': "Your army marches on {name}'s town",
  'watch.lands': 'It lands when they defend, or plays itself in about {n} hours.',
  'watch.landsOne': 'It lands when they defend, or plays itself in about an hour.',
  'watch.landsSoon': 'It lands when they defend, or plays itself any moment now.',
  'watch.watch': 'Watch the battle',
  'watch.skip': 'Tap to skip',
  'watch.auto': "They didn't come: it played itself.",
  'watch.sacked': "You sacked {name}'s town!",
  'watch.breach': 'You broke through!',
  'watch.draw': 'A draw',
  'watch.held': 'Their walls held',
  'watch.glory': '+{n} glory',
  'watch.noGlory': 'No glory this time.',
  'watch.standin': "Their town stays theirs: the fort you see is a stand-in, drawn from the report's counts only.",
  'raid.onWay': 'Your army is on its way to {name}.',
  'raid.sentLineOne': '1 weapon, power {power}.',
  'raid.sentLine': '{weapons} weapons, power {power}.',
  'raid.waitNote': 'They have a day to defend. The report comes back here.',
  'raid.allGoldTitle': 'Your mastered words protect your town',
  'raid.allGoldHint': 'Gold words have graduated from attacking. Take first looks or add a new moment to learn new weapons, or try a practice battle. You can still defend incoming armies with your own words.',
  'raid.allGoldLearn': 'First looks and new moments',
  'raid.allGoldDefend': 'Friends and incoming armies',
  'raid.potentialSpentTitle': 'Initial attack potential spent',
  'raid.potentialSpentHint': 'This is spent attacking potential, not gold mastery. Keep learning these words or meet new ones to prepare fresh weapons.',
  'raid.potentialSpentOne': 'Initial attack tier spent: {n} of {total}',
  'raid.potentialSpentMany': 'Initial attack tiers spent: {n} of {total}',
  'raid.emptyTitle': 'No attack weapons are ready yet',
  'raid.emptyHint': 'Take a first look or add a moment to prepare a learning weapon. You can practise a battle or defend an incoming army while you build your arsenal.',
  'raid.emptyPractice': 'Try a practice battle',
  'raid.loadingRestUntil': 'Loaded successfully. Available again at {time}; practise this word meanwhile.',
  'raid.challengeHint': 'Start with a recommended challenge, or choose a harder one. Only challenges that meet your learning requirement are shown.',
  'raid.recommended': 'Recommended',
  'raid.optionalHarder': 'Optional challenge',
  'raid.context.familiar': 'Familiar line',
  'raid.context.meaning': 'From the meaning',
  'raid.context.new': 'New context',
  'raid.context.audio': 'By ear',
  'raid.preparationPreview': 'You can explore this army. Sending it will be available when the new battle rules are ready.',
  'error.raid-preview-only': 'Sending this army is not available yet. You can still defend incoming armies.',
  'error.raid-recovery-pending': 'Finish this word’s recovery in practice before loading it again.',
  'error.raid-proof-pending': 'Your answer is saved, but this weapon is not loaded yet. Answer this card again to finish loading it.',
  'error.raid-defence-pending': 'Your answer is saved, but the defence is not recorded yet. Answer this card again to finish it.',
  'error.raid-rebuild-pending': 'Your answer is saved, but the word is not rebuilt yet. Try again to finish.',
  'error.raid-rebuild-failed': 'The word could not be rebuilt just now, and nothing was saved for it. Try again in a moment.',
  'error.raid-preparation-expired': 'This army preparation expired. Your learning and word rest remain saved; prepare a new army or practise.',
  'raid.noWords': 'No word of yours is out of the fog yet. Take a first look in your town, then come back.',
  'raid.weight': 'Weight {n} of {budget}',
  'raid.power': 'Power {n}',
  'raid.muster.power': 'power',
  'raid.muster.full': 'Full strength',
  'raid.muster.potential': 'of {n} at full strength',
  'raid.muster.aria': 'Army power {n} of {total}',
  'raid.meter.label': 'Army power',
  'raid.meter.of': 'of {n}',
  'raid.fill.left': 'Room for {n} more weight',
  'raid.fill.full': 'The army is full',
  'raid.fill.none': 'No more words to add',
  'raid.fill.aria': 'Weight {n} of {budget} filled, {left} left',
  'raid.armouryHint': 'Pick a weapon for a word: 🏹 arrows (pick the meaning), 🐉 dragon (hear it, pick the meaning), 🪨 catapult (fill the line), 🔮 spell (type it), 🌪 storm (hear it, type it). Each shows its weight, then its power.',
  'raid.pick': 'Pick your weapons',
  'raid.loadOne': 'Load 1 weapon',
  'raid.load': 'Load {n} weapons',
  'raid.shaky': 'shaky',
  'raid.shakyHint': 'Due for review: you may miss it.',
  'raid.restingUntil': 'Available again at {time}. Until then, choose another word.',
  'raid.resting': 'Resting after a miss. Choose another available word.',
  'raid.chipAria': '{weapon}: weight {weight}, power {power}',
  'raid.tour.title': 'How raids work',
  'raid.tour.skip': 'Skip',
  'raid.tour.step': '{n} of {of}',
  'raid.tour.practice': 'practice word',
  'raid.tour.next': 'Next',
  'raid.tour.duel.title': 'A raid is a quiz duel.',
  'raid.tour.duel.body': "You and a friend send each other quiz questions about words. It's practice for both of you.",
  'raid.tour.weapons.title': 'Weapons are words you know.',
  'raid.tour.weapons.body': 'Pick a word, then prove it by answering its question. Right: your weapon is loaded. Wrong: it breaks and rests for a day, so pick another word.',
  'raid.tour.weapons.tap': 'Tap 🏹 to load it.',
  'raid.tour.weapons.try': 'Try it with a practice word.',
  'raid.tour.weapons.loaded': 'Loaded! The better you know a word (wood, stone, gold), the harder it hits.',
  'raid.tour.weapons.broke': "It broke. That's how raids make sure you only send words you really know. Try again.",
  'raid.tour.weapons.again': 'Try again',
  'raid.tour.weapons.more': 'Other weapons ask you to listen, fill a line or type the word. The harder, the stronger.',
  'raid.tour.defend.title': 'When an army comes, you defend.',
  'raid.tour.defend.body': 'Each weapon aims at one of your own words. Answer to block it.',
  'raid.tour.defend.blocked': 'Blocked! Lit towers and paved streets make your words harder to hit.',
  'raid.tour.defend.hit': 'It got through and a house cracked. Answering that word later repairs it.',
  'raid.tour.defend.either': 'Either way you just practised your own word.',
  'raid.help.defend.open': 'How to defend',
  'raid.help.defend.title': 'How to defend this town',
  'raid.help.defend.body': 'Each weapon targets one of your words. Answer its card to block it; this also practises that word.',
  'raid.help.defend.detail': 'This help keeps the army you opened. When you return, you will see its defence if it is still waiting, its report if it has finished, or its status if it was withdrawn.',
  'raid.help.defend.return': 'Return to this defence',
  'raid.tour.win.title': 'Who wins? Whoever learned more.',
  'raid.tour.win.stronger': 'Know more words → send stronger armies.',
  'raid.tour.win.walls': 'Practise your words → lit towers and paved streets make your walls strong.',
  'raid.tour.win.calm': "A raid never takes a word away from you, and an army you don't answer plays itself after a day, so there is no rush.",
  'raid.tour.win.continue': 'Continue',
  'raid.tour.win.pick': 'Pick a friend to raid',
  'raid.tour.win.learnFirst': "First, learn a word. A word becomes a weapon once you've taken its first look.",
  'raid.tour.win.practise': 'Go practise',
  'raid.tour.win.replay': 'Replay the tour',
  'raid.answerIs': 'The answer:',
  'raid.ask.choice': 'What does it mean?',
  'raid.ask.listen': 'Listen, then pick what it means.',
  'raid.ask.dictation': 'Listen, then type what you hear.',
  'raid.ask.gap': 'Type the missing word.',
  'raid.ask.recall': 'Type it from its meaning.',
  'raid.typeAria': 'Your answer',
  'raid.typeHere': 'Type here',
  'raid.check': 'Check',

  // ---- original recording retention (ticket 13) ----
  'audio.expiresOn': 'The original recording is deleted automatically on {date}.',
  'audio.delete': 'Delete recording',
  'audio.deleteConfirm': 'Delete only the original recording? The transcript, exercises and your progress stay.',
  'audio.deleted': 'Original recording deleted on {date}.',
  'audio.expired': 'Original recording expired on {date} — recordings are kept for 30 days.',
  'drafts.hintAge': 'Drafts older than 30 days are removed the next time the app opens.',
  'drafts.expiredOne': '1 draft older than 30 days was removed from this device.',
  'drafts.expiredMany': '{n} drafts older than 30 days were removed from this device.',

  // ---- practice / review / exam (shared lesson chrome) ----
  'lesson.play': '🔊 Play',
  'lesson.stopPlay': '⏹ Stop',
  'lesson.loadingCard': 'Loading card…',
  'lesson.sayCheck': '🎙 Say it & check',
  'lesson.stopCheck': '⏹ Stop & check',
  'lesson.scoring': 'Scoring…',
  'lesson.pronPass': '✓ Recognised — nice.',
  'lesson.pronFail': 'Not quite: {tip}',
  'lesson.pronErrorTail': '{err} — or tap “I said it” on the card.',
  'lesson.scoringFailed': 'Scoring failed',
  'lesson.saveFailed': "Your answer wasn't saved — check your connection and try again.",
  'lesson.saveFailedMany': "{n} answers weren't saved — check your connection and try again.",
  'lesson.saving': 'Saving…',
  'lesson.saveRefused': "This answer couldn't be saved. The server turned it down.",
  'lesson.saveRefusedMany': "{n} answers couldn't be saved. The server turned them down.",
  'lesson.leaveUnsaved': "Some answers aren't saved yet and will be lost. Leave anyway?",
  'lesson.skip': 'Skip',
  'lesson.next': 'Next',
  'lesson.finish': 'Finish',
  'lesson.whySkip': 'Why skip?',
  'lesson.wrongCard': '✕ Wrong card — remove for good',
  'lesson.removeAction': 'Remove from learning…',
  'lesson.undoRemoval': 'Undo removal',
  'lesson.undoFailed': "Couldn't undo removal ({err}).",
  'lesson.undoConflict': "That item's status changed after removal. Undo expired, and its current choice was kept.",
  'lesson.statusConflict': "That item's status changed elsewhere, so this removal was not applied. Reopen Practice, Review or Tending to see its current status.",
  'lesson.triageConflict': "That item's status changed elsewhere, so this choice was not applied. Reload the moment before choosing again.",
  'lesson.removeConfirm': 'Remove “{label}” from learning? It will leave review and future lessons. Undo stays available for this open activity. In practice, choosing Again clears it; in review or tending, answering or skipping another card closes the Undo window.',
  'lesson.removeFailed': "Couldn't remove that card ({err}) — it stays in learning for now.",
  'lesson.cardError': "Couldn't show card {n}.",
  'lesson.worthLook': 'Worth another look',
  'lesson.worthLookHint': '— seeing them now is part of learning',
  'lesson.flagged': '⚑ you flagged “{given}” — kept for tuning, no penalty',
  'skip.defer': "Can't speak now",
  'skip.unclear': 'Unclear question',

  'practice.stopEarly': "End this session",
  'practice.endedEarly': "Practice stopped early",
  'practice.endedEarlySub': "You reached {visited} of {total} cards.",
  'practice.sessionSaved': "Saved activity: {saved} of {total} cards; {revealed} reveals; {hinted} with hints; {skipped} skips; {selfReported} self-reports.",
  'practice.sessionStudy': "Study: {visited} of {total} cards viewed; browsing does not save answers.",
  'practice.doneMixedSub': "Reached the end of {n} cards across Study and Test.",
  'practice.progressUnavailable': "Saved progress couldn't refresh just now. Counts here include only answers confirmed by the server.",
  'practice.title': 'Practice',
  'practice.newPill': 'new',
  'practice.newPillTitle': 'Freshly generated for this moment',
  'practice.loadError': "Couldn't load practice.",
  'practice.noTasks': 'This moment has no practice tasks yet.',
  'practice.study': 'Study',
  'practice.test': 'Test',
  'practice.frame': 'Practising the words from this moment. Leaving early keeps what is already saved and does not mark the practice finished.',
  'practice.studyHint': 'Study shows the answers freely — nothing is recorded.',
  'practice.testHint': 'Answer each card to move on — every answer counts toward your progress.',
  'practice.testIntro': 'Test saves each answer to your progress and uses it to plan your reviews; Study is there to browse the cards and answers without saving anything.',
  'practice.listenHint': 'Listen (clear model voice) — no text, answer from hearing. The original recording is on the moment page.',
  'practice.forgedNow': '⚒ Forged: {list}',
  'practice.doneTitle': "Session finished",
  'practice.doneSub': '({n} tasks, {mode} mode).',
  'practice.progress': "Saved activity so far: {saved} cards ({tested} scored); {forged} words from this moment are forged.",
  'practice.loadingProgress': 'Loading progress…',
  'practice.forgedRun': 'Forged this run: {list}',
  'practice.reflect': 'Reflect on the moment page',
  'practice.moment': 'Moment',
  'practice.returnMoments': 'Moments',
  'practice.again': 'Again',

  'review.title': 'Review',
  'review.titleDue': 'Review ({n} due)',
  'review.loadError': "Couldn't load the review queue.",
  'review.nothingDue': 'Nothing due.',
  'review.nothingDueHint': 'New words from your moments come out a few a day; come back tomorrow.',
  'review.cardOf': 'Card {i} of {n} · {seen}, {right}',
  'review.cardOfNew': 'Card {i} of {n} · first time',
  'review.seenOnce': 'seen once',
  'review.seenTwice': 'seen twice',
  'review.seenMany': 'seen {n} times',
  'review.rightNone': 'not right yet',
  'review.rightOnce': 'right once',
  'review.rightTwice': 'right twice',
  'review.rightMany': 'right {n} times',
  'review.listenHint': 'Listen (clear model voice) — no text, answer from hearing.',
  'review.complete': 'Review complete',
  // Ticket 06: each review card named by what it asks (its ladder step), never by box.
  'review.card.firstLook': 'First look',
  'review.card.rebuild': 'Rebuild it',
  'review.card.byEar': 'Catch it by ear',
  'review.card.soundAlike': 'Hear the difference',
  'review.card.recall': 'Recall it',
  'review.card.sayIt': 'Say it from the meaning',
  'review.card.somewhereNew': 'Use it somewhere new',
  'review.card.dictation': 'Write what you hear',
  'review.card.spelling': 'Spell it',
  // Ticket 04: the easier card right after a miss (one step down, the same word), never by box.
  'retreat.card': 'A step easier',
  'retreat.hint': 'You missed this word, so here it is on an easier card. Get it right and the word keeps its place; miss it and it steps back a little. Either way it comes back tomorrow.',

  'exam.title': 'Scenario exam',
  'exam.checkingReadiness': 'Checking whether the exam is ready…',
  'exam.readinessUnavailable': 'The exam’s current requirements could not be checked. Try again before starting.',
  'exam.readinessChanged': 'The exam could not start. Its current readiness does not match the last check.',
  'exam.buildError': "Couldn't build the exam.",
  'exam.intro': 'A short story using the words you have met, the shakiest first — then questions on them: meaning, spelling, plus missions set in the story (hear a line, pick a reply, recall a phrase). Your answers count like any test.',
  'exam.writing': 'Writing your story…',
  'wait.voice': 'Getting the voice ready…',
  'wait.practice.words': 'Writing questions from your words…',
  'wait.practice.lines': 'Setting them in the lines they said…',
  'wait.newPack': 'Writing a fresh set of questions…',
  'wait.exam.words': 'Picking the words you need most…',
  'wait.exam.story': 'Writing a short story around them…',
  'wait.exam.questions': 'Weaving the questions in…',
  'wait.exam.long': 'A story can take half a minute.',
  'wait.pron': 'Listening to how you said it…',
  'exam.start': 'Start exam',
  'exam.sceneTitle': 'A scene to read',
  'exam.startQuestions': 'Start questions ({n})',
  'exam.question': 'Question {i} of {n}',
  'exam.loading': 'Loading…',
  'exam.seeScore': 'See score',
  'exam.easierIntro': "Before your score: an easier card for each word you missed. These don't change the score.",
  'exam.easierCount': 'Easier card {i} of {n}',
  'exam.score': '{correct}/{n} correct ({pct}%)',
  'exam.passed': 'passed!',
  'exam.passMark': 'pass mark is 80%.',
  'exam.missedNote': 'Words you missed come back in review soon.',
  'exam.newExam': 'New exam',
  'exam.questionError': "Couldn't show question {n}.",
  'exam.pastTitle': 'Past exams',
  'exam.pastNone': 'No finished exams yet.',
  'exam.pastLine': '{date}: {score}/{total} ({pct}%)',
  'exam.pastWork': 'To work on:',
  'exam.pastError': "Couldn't load your past exams.",

  // ---- end-of-lesson feedback ----
  'feedback.pickFirst': 'Pick a difficulty or usefulness first.',
  'feedback.saved': 'Saved — the next lesson adapts to this.',
  'feedback.title': 'How was this lesson?',
  'feedback.sub': '— tunes the next one',
  'diff.too_easy': 'Too easy',
  'diff.just_right': 'Just right',
  'diff.too_hard': 'Too hard',
  'useful.5': '👍 Useful',
  'useful.3': '😐 OK',
  'useful.1': '👎 Not useful',
  'feedback.notePlaceholder': 'Anything to change? (optional)',
  'feedback.savedBtn': '✓ Saved',
  'feedback.save': 'Save feedback',

  // ---- exercise cards ----
  'ex.flashcard': 'Flashcard',
  'ex.tapReveal': 'tap to reveal',
  'ex.gradeSelf': 'did you know it? grade yourself',
  'ex.tapFront': 'tap to see the front again',
  'ex.missed': '✗ Missed',
  'ex.gotIt': '✓ Got it',
  'ex.gapfill': 'Fill the gap · try it first, hints are free',
  'ex.startsWith': 'Starts with “{s}”',
  'ex.typeWord': 'type the missing word',
  'ex.check': 'Check',
  'ex.hint': '💡 Hint',
  'ex.hintN': '💡 Hint ({n})',
  'ex.showMe': 'Show me',
  'ex.dontKnow': "I don't know — show me",
  // Ticket 32: after Show me the card names the answer; the answer follows in its own direction.
  'ex.answerIs': 'The answer:',
  'ex.expectedAnswer': 'Expected answer',
  'ex.leaveOut': 'Leave out',
  'ex.wordOrderChanged': 'Same words, a different order.',
  'ex.correctIs': 'Correct — {answer}',
  'ex.notQuiteHint': 'Not quite — try again, or take a hint above',
  // Ticket 05: a scratch is a pass with a spike missed; the marked spot follows.
  'ex.scratch': 'Passed, with a scratch — mind the marked spot:',
  'ex.spelling': 'Spelling · mind the {spikes}',
  'ex.spellingMissed': 'Almost — mind the marked spot:',
  'spike.accent': 'accent',
  'spike.diaeresis': 'diaeresis',
  'spike.cedilla': 'cedilla',
  'spike.apostrophe': 'apostrophe',
  'spike.hyphen': 'hyphen',
  'spike.middot': 'middle dot',
  'spike.ligature': 'joined letters (œ, æ)',
  'ex.shouldCount': '⚑ My answer should count',
  'ex.flagged': "⚑ Flagged — kept for tuning, doesn't affect your score.",
  'ex.choose': 'Choose',
  'ex.correct': 'Correct',
  'ex.answerWas': 'The answer was: {a}',
  // Ticket 11: spot it (tap the word in its line, then its meaning) and dictation (type what you hear).
  'ex.spotit': 'Spot it · find this word in the line',
  'ex.spotitFound': 'Found it.',
  'ex.spotitMissed': 'Not that one — here it is, marked in the line.',
  'ex.dictation': 'Dictation · play it, then type what you hear',
  'ex.typeHeard': 'type what you hear',
  'ex.reorder': 'Put the words in order',
  'ex.notQuiteReorder': 'Not quite — tap words to move them back',
  'ex.sayit': 'Say it out loud',
  'ex.isaidit': 'I said it',
  'ex.listening': 'Listening…',
  // Ticket 14: the sound-alike pair (pick the word you heard) and say it from the meaning.
  'ex.soundalike': 'Play it, then pick the word you heard',
  'ex.sayfrommeaning': 'Say the word for this out loud',
  'review.sayItOff': 'No listening check can be made now — this card asks the same thing another way.',

  // ---- server errors, by the `code` the server sends next to its English `error` ----
  'error.generic': 'Something went wrong. Please try again.',
  'error.server': 'The server had a problem. Please try again in a moment.',
  'error.offline': "Couldn't reach the server — check your connection and try again.",
  'error.invitation-not-found': "This invitation link isn't valid. Ask for a new one.",
  'error.invitation-used': 'This invitation has already been used. Sign in, or ask for a new one.',
  'error.invitation-expired': 'This invitation has expired. Ask for a new one.',
  'error.invalid-username': 'Usernames are 3–24 letters, numbers, _ or -.',
  'error.password-too-short': 'The password needs at least 10 characters.',
  'error.invalid-target': "That language isn't available.",
  'error.username-taken': 'That username is taken — try another.',
  'error.account-unreadable': "Your account was created but couldn't be opened. Please sign in.",
  'error.wrong-credentials': 'Wrong username or password.',
  'error.too-many-attempts': 'Too many tries — please wait a few minutes and try again.',
  'error.recovery-invalid': 'This recovery link is not valid any more.',
  'error.sign-in-required': 'Please sign in again.',
  'error.not-found': "This couldn't be found — it may have been deleted.",
  'error.history-unavailable': "Your learning history can't be opened right now. Please let us know.",
  'error.delete-failed': "Couldn't delete this moment — nothing was removed. Please try again.",
  'error.no-recording': "There's no recording for this moment any more.",
  'error.no-scene': 'This moment has no imagined scene to play.',
  'error.scene-audio-unavailable': "The scene's voices aren't available here; the lines are read one by one.",
  'error.scene-audio-failed': "The scene's voices couldn't be made just now; the lines are read one by one.",
  'error.moment-deleted': 'This moment was deleted.',
  'error.draft-account': 'This draft was saved under another account.',
  'error.draft-language': 'This draft was saved for another language.',
  'error.draft-expired': 'This recording is older than 30 days and is no longer kept.',
  'error.daily-allowance': "That's all your new moments for today. They unlock at {time} in {timezone}. Practice and review stay open.",
  'error.daily-review': "Finish today's review first ({done} of {required} done) to unlock new moments. Practice and review stay open.",
  'error.ai-allowance': 'Your AI allowance for this month is used up — it resets at 00:00 UTC on {date}. Practising and reviewing what you already have stays open.',
  'error.beta-budget': "The beta's shared AI budget is used up — it resets at 00:00 UTC on {date}. Practising and reviewing what you already have stays open.",
  'error.audio-required': 'No recording came through. Please record again.',
  'error.audio-too-large': 'This recording is too large (15 MB max).',
  'error.shared-too-long': 'This voice message is longer than 30 seconds. Eaves takes voice messages of up to 30 seconds.',
  'error.shared-unsupported': "Eaves can't read this kind of audio yet. Voice messages from WhatsApp and Telegram work.",
  'shared.title': 'A voice message',
  'shared.menu': 'Install and share',
  'shared.capability.android': 'To receive voice messages, install Eaves from Chrome’s menu, then choose Eaves in the sender app’s Share menu. Android share support varies by browser and phone; if no file arrives, record or type a moment here instead. A message already received needs no further installation.',
  'shared.capability.ios': 'On iPhone or iPad, Safari’s Share → Add to Home Screen installs Eaves for supported reminders. Sharing voice messages into Eaves is not supported on iOS; record or type a moment here instead. A message already received is still available below.',
  'shared.capability.other': 'Receiving voice messages requires a supported Android browser and installed Eaves app. On this device, record or type a moment instead. A message already received is still available below.',
  'shared.offline': 'You’re offline. A received message can be kept as a draft on this device; it becomes a lesson after you reconnect in this account.',
  'shared.sub': 'Shared with Eaves. It becomes a moment, like a recording.',
  'shared.none': 'Nothing arrived, or it expired. Eaves receives voice messages up to 30 seconds from WhatsApp or Telegram through its installed Android app. Share again, or record or type a new moment here.',
  'shared.unsupported': "Eaves can't use this audio type yet. Share a voice message from WhatsApp or Telegram, or record or type a new moment here.",
  'shared.recordInstead': 'Record a new moment',
  'shared.typeInstead': 'Type a moment instead',
  'shared.tooLong': 'This voice message is {length} long. Eaves takes voice messages of up to 30 seconds.',
  'shared.pill': 'message',
  'error.note-audio-too-large': 'That spoken note is too long. Keep it under a minute, or type it.',
  'error.transcribe-limit': "That's a lot of spoken notes for one day. Type this one, or say it again tomorrow.",
  'error.transcribe-failed': "Couldn't write that down just now. Try again, or type it.",
  'error.note-too-long': 'That note is too long (2,000 characters max).',
  'error.note-too-short': 'Tell me what you wanted to say first.',
  'error.analysis-failed': "Couldn't analyse this moment. Please try again in a moment.",
  'error.content-blocked': "We couldn't make a lesson from this.",
  'error.newcomers-gone': "These newcomers have moved on. Your town will have others another day.",
  'error.newcomers-failed': "The newcomers couldn't make it today. Someone else will come another day.",
  'error.moment-save-failed': "Couldn't save this moment. Please try again.",
  'error.seed-not-found': "This reply isn't on this moment any more.",
  'error.seed-not-kept': "This reply isn't kept, so it isn't practised. Keep it on its moment first.",
  'error.town-fog-word': 'This word is still in the fog. Give it its first look in its own district first.',
  'error.town-name-invalid': 'Your town’s name needs 1 to 24 characters, in any script, and no hidden control marks.',
  'error.heard-invalid': "That line couldn't be counted as heard.",
  'error.comeback-invalid': "That recording couldn't be marked as shown.",
  'error.seed-save-failed': "Couldn't save that. Please try again.",
  // The three below are shown inside lesson.pronErrorTail, so no closing full stop.
  'error.scoring-unavailable': "Pronunciation checks aren't available here",
  'error.snippet-too-large': 'That recording is too long to check',
  'error.scoring-failed': "Couldn't check that. Try again",
  'error.attempt-save-failed': "Couldn't save your answer.",
  'error.attempt-item-removed': 'This item was removed elsewhere. Undo its removal before answering again.',
  'error.exam-few-words': 'Meet a few more words first.',
  'error.placement-unavailable': "The starting check isn't available for this language yet.",
  'error.circle-consent-required': 'Please agree to what friends will see first.',
  'error.already-in-circle': "You're already in a circle. Leave it first to join another.",
  'error.consent-seen-invalid': 'That choice could not be saved. Try again.',
  'error.circle-code-invalid': "That code doesn't work. Check it, or ask your friend for the current one: a replaced code stops working.",
  'error.circle-full': 'This circle is full: it already has 10 members.',
  'error.not-in-circle': "You're not in a circle.",
  'error.circle-not-keeper': 'Only the circle keeper can make a new code.',
  'error.friend-offer-invalid': 'This link no longer works. Ask your friend for a new one.',
  'error.friend-own-link': 'This is your own link. Send it to a friend.',
  'error.friends-full': "This friendship can't be made right now.",
  'error.friends-own-full': 'You already have 10 friends. End a friendship to add another.',
  'error.friend-offers-full': 'You have 5 open links and requests. Withdraw one, or wait for one to be used.',
  'error.friend-limit': 'You have sent many requests today. Try again tomorrow.',
  'error.raid-invalid': "That army can't be sent as it is. Build it again.",
  'error.raid-not-friends': 'You can only raid a friend.',
  'error.raid-pending': 'Your army is already on its way to them.',
  'error.raid-full': 'Too many armies are at their gates already. Try later.',
  'error.raid-no-town': 'They have no words to defend yet.',
  'error.raid-limit': "You've sent today's armies. More tomorrow.",
  'error.raid-empty': 'No weapon is loaded, so there is no army to send.',
  'error.raid-not-found': "That raid isn't here any more.",
  'error.raid-over': 'That raid is already over.',
  // ---- the town (both looks) and the town phone check (/town-check) ----
  'town.nightLabel': 'Your town at night',
  'town.nightConditionLabel': 'Your town at night: {ivy} due for review; {cracks} with raid damage.',
  'town.dioramaLabel': 'Your town as a lit diorama',
  'town.help': 'What am I seeing?',
  'town.credit': 'Word sounds and families from',
  'town.zoomIn': 'Zoom in',
  'town.zoomOut': 'Zoom out',
  'town.fitLabel': 'Fit the whole town',
  'town.fit': 'fit',
  'town.noWebgl': 'This view needs WebGL2, which this browser does not offer.',
  'town.homeTitle': 'Your town',
  'town.actions': 'Town places and learning actions',
  'town.openDistrict': 'Open district {name}',
  'town.openBuilding': 'Open word {name}, meaning {meaning}',
  'town.openBridge': 'Open bridge between {a} and {b}',
  'town.bridge': 'Bridge',
  'town.bannerHint': 'Banners: one per raid whose cracks are still open. Open a banner to see what it takes to bring it down.',
  'town.openBanner': "Open {name}'s banner. Repair the cracked words to bring it down.",
  'town.baysHint': 'Parts of the building, one per syllable. Pick a part to read its syllable.',
  'town.pickBay': 'Read the syllable of part {i} of {n}{syllable}',
  'town.detailsLoading': 'Loading town details',
  'town.detailsReady': 'Town details are ready',
  'town.homeHint': 'Tap a building, a district or a bridge to tend it.',
  'town.homeMaterials': '{fog} in the fog · {scaffolding} scaffolding · {wood} wood · {stone} stone · {gold} gold',
  'town.maintenanceExplain': 'Lamps stay ready until each word’s next review. Overdue words gather ivy and dim over three review intervals. Remember without help at the current step to restore condition. Neglect keeps the material; assessed recovery can change it. Raid cracks are separate.',
  'town.conditionSummary': 'Town readiness',
  'town.conditionDistrictHint': 'Readiness summarizes its buildings. Tend this district to review; repair raid cracks separately.',
  'town.maintenanceLights': 'Lamps lit: {lamps} · Due for maintenance: {ivy}',
  'town.maintenanceFading': '{n} lamps dimming as review comes due',
  'town.maintenanceFadingOne': '1 lamp dimming as review comes due',
  'tend.condition.maintained': 'Maintained until its next review. Remember unaided to keep it ready.',
  'tend.condition.neglected': 'Review overdue: protection is wearing. Remember unaided to restore condition; raid cracks need their own repair.',
  'tend.condition.unproven': 'Remember this word unaided at its current step to maintain its protection.',
  'town.homeLights': '{lamps} lit this week · {ivy} overgrown',
  'town.knownOnly': 'Words you already know stand here; they need no upkeep.',
  'town.restingOnly': 'The words here are known or set aside, so they need no upkeep.',
  'town.homeLanes': 'Each line said in a moment runs as a chalk street between its words\' houses. Know every word of a line and its street is paved, with a lamp at each end.',
  'town.homeExplain': 'Every word you meet is a building. The first time you get it right it leaves the fog as scaffolding, then it turns to wood, stone and gold each time you remember it after a longer gap. A lit lamp: you got it right in the last 7 days. Overgrowth: it is due for review. Tap a building, a district or a bridge to tend it with the same cards as review.',
  // ---- the town's props (v1 ticket 40): the signpost's board before the town has a name; the town check's row ----
  'props.yourTown': 'Your town',
  'props.checkLand': 'The empty land',
  'props.checkLandIntro': 'A town with no moment yet, as on the first night: the land with its signpost and lantern, opened in each look and panned for {seconds} s.',
  // ---- the town's "What am I seeing?" panel (the renderers' chrome; the kit picks the lines by the target) ----
  'townLegend.head': 'Each island is a moment you recorded; each lot on it is a word or phrase you heard there.',
  'townLegend.stats': 'Tonight: {lit} lit · {weathered} overgrown · {fog} in the fog · {forged} forged.',
  'townLegend.fog': 'Fog covers words you heard whose first look is still to come; only their outlines show.',
  'townLegend.ivy': 'Ivy climbs a word that is overdue for review, up the poles of a scaffold or the walls of the rest; the later it is, the higher it climbs.',
  'townLegend.bridge': 'A word heard in more than one moment bridges them with a rope walk; its lamps are lit while you practised a shared word this week.',
  'townLegend.guess': 'Rough guesses: what a building is used for (verbs are workshops, nouns homes, others gardens) and the small sign by the door (a topic like food or money) come from guessing at the English meaning.',
  'townLegend.syllables': 'One bay per syllable, and the tallest bay is where the stress falls.',
  'townLegend.windows': "Window shapes follow the vowel's mouth shape: a arch, e square, i slit, o round, u small circle.",
  'townLegend.accent': 'A written accent puts a keystone over the window; a silent letter adds a blind window.',
  'townLegend.lights': 'Warm lights are words you practised this week; the brighter, the more recent. The rest sleeps in moonlight.',
  'townLegend.moon': 'The moon waxes with the share of your words out of the fog that you practised this week; it is full when every lamp is lit.',
  'townLegend.day': 'The town is by day when every word out of the fog was practised this week and none is overdue, with at least {min} of them, and at least half of the words you have heard are out of the fog. The first lamp to go out brings the night back.',
  'townLegend.grow': 'Buildings grow as you practise: scaffolding (pale poles and planks round the foundations, as high as the walls to come), wood (boarded timber walls), stone (walls), then gold (a roof with a gilded ridge, coping or finial).',
  'townLegend.walls': 'Wall textures follow the consonants: stone for p/t/k, brick for s/f, arches for m/n, ironwork for l/r.',
  'townLegend.phrase': 'A phrase is a row of buildings: little words become the porches, plaques, gateways and arches that join them.',
  'townLegend.together': 'Words that belong together are built together: shared walls, one roofline, walkways and lantern strings; an arch over the lane joins two linked words once both are well built.',
  'townLegend.gold': 'Gold trim and a weathervane mark words that took many tries; moss and green copper mark old words you keep up.',
  'townLegend.lanes': 'Streets: each is a line of that moment, chalked between its words\' houses, and paved once you know every word of it.',
  'townLegend.fa.lights': 'Amber lanterns and glowing orosi (sash windows of coloured glass) are words you practised this week; the brighter, the more recent. The rest sleeps in moonlight.',
  'townLegend.fa.grow': 'Buildings grow as you practise: scaffolding (poplar poles and planks), wood (poplar walls with terrace edges, pillared porches and lattice screens), stone (bare khesht, sun-dried brick, with vaulted entrances), then gold (kahgel render, finished roofs and decorated portals, gilded along the coping, ridge or finial).',
  'townLegend.fa.syllables': 'One bay per syllable, and the tallest bay is where the stress falls; on a house it opens as an iwan, a vaulted porch behind a pishtaq (portal screen) with muqarnas in its head.',
  'townLegend.fa.openings': "Openings follow the vowel's mouth shape: a a pointed arch, e a wooden lattice, i a narrow arched slit, o an orosi, u a star-shaped roozan.",
  'townLegend.fa.walls': 'Wall work follows the consonants: fired brick for p/t/k, hazarbaf (patterned brick) for s/f, blind arched niches for m/n, slim talar columns for l/r.',
  'townLegend.fa.accent': 'A written accent puts a tile medallion over the window; a silent letter adds a blind niche.',
  'townLegend.fa.phrase': 'A phrase is a row of buildings: little words become a brick step with benches, tile plaques, a pointed gateway, an arch over the lane or a studded gate between them.',
  'townLegend.fa.together': 'Words that belong together are built together: shared walls, one tile frieze, brick paths and strings of coloured lanterns; an arch over the lane joins two linked words once both are well built.',
  'townLegend.fa.use': 'By use: bazaar vaults with skylit domes (market), a chahar bagh with channels, a howz and cypresses (garden), a tiled pishtaq with a dome or minarets (landmark). Some verb workshops an asbad (vertical windmill) that turns, others a flue that smokes, when practised this week.',
  'townLegend.fa.gold': 'Gold trim and a gilt finial mark words that took many tries; sea-green bloom on the tiles and settled dust mark old words you keep up.',
  'townLegend.fa.lanes': 'Streets: each is a line of that moment, chalked between its words\' houses, and paved in brick once you know every word of it.',
  // fr kit
  'townLegend.fr.lights': 'Warm lamps, lit French windows and a café\'s glow are words you practised this week; the brighter, the more recent. The rest sleeps in moonlight.',
  'townLegend.fr.grow': 'Buildings grow as you practise: scaffolding (pale poles and planks round a plinth of cut stone blocks, as high as the walls to come), wood (boarded walls), stone (rough grey limestone; a café already has its striped awning), then gold (dressed cream stone with a grooved ground floor, window hoods and an iron balcony, under a zinc and slate mansard roof with dormers, gilded along its balcony rail, ribs or finial).',
  'townLegend.fr.syllables': 'One bay per syllable, read from the dictionary\'s pronunciation, and the tallest bay is where the stress falls: in French, the last syllable you say. A word the dictionary can\'t say keeps a house fixed by the word itself.',
  'townLegend.fr.windows': 'Window shapes follow the vowel\'s mouth shape: a a round-headed French window, e square-headed, i a slit, o a low arch, u an œil-de-bœuf (a small round window).',
  'townLegend.fr.walls': 'Wall work follows the consonants: corner stones for p/t/k, brick between stone edges for s/f, blind niches for m/n, cast-iron pilasters for l/r.',
  'townLegend.fr.accent': 'A written accent (é, è, ê, à…) puts a carved keystone over the window, edged in gilt at gold; letters you don\'t say change nothing.',
  'townLegend.fr.phrase': 'A phrase is a row of buildings: little words become a stone or cobbled step, blue enamel plates, a carriage entrance, an arch at the first floor or a wrought-iron gate between them, and a question hangs a bell under a slate-capped cote.',
  'townLegend.fr.together': 'Words that belong together are built together: shared walls, one mansard roofline, walkways and lantern strings; an arch over the lane joins two linked words once both are well built.',
  'townLegend.fr.use': 'By use: cafés under striped awnings (market), a square with plane trees, a Wallace fountain and a Morris column (garden), a slate dome or spire (landmark). Some verb workshops get a windmill, others chimney pots that smoke when practised this week.',
  'townLegend.fr.gold': 'A band a floor up (bronze at stone, gilt at gold) and a gilt finial or a pennant mark words that took many tries; dark rain streaks down the walls mark old words you keep up.',
  'townLegend.fr.lanes': 'Streets: each is a line of that moment, chalked between its words\' houses, and paved with cobbles once you know every word of it.',
  // es kit
  'townLegend.es.lights': 'Warm faroles and glowing fanlight grilles are words you practised this week; the brighter, the more recent. The rest sleeps in moonlight.',
  'townLegend.es.grow': 'Buildings grow as you practise: scaffolding (pine poles and planks round a sandstone plinth, as high as the walls to come), wood (boarded pine walls), stone (bare rubble and golden sandstone), then gold (cal, a warm whitewash, over an ochre zócalo, with a teja roof or a flat azotea behind a low parapet, gilded along its ridge, coping or finial).',
  'townLegend.es.syllables': 'One bay per syllable, and the tallest bay is where the stress falls; a house has its heavy studded door there, under a fanlight grille.',
  'townLegend.es.openings': 'Openings follow the vowel\'s mouth shape: a a horseshoe arch, e a deep window behind a reja (iron grille) with geranium pots, i a tall balcony door behind a reja, o a round-arched window, u an oculus.',
  'townLegend.es.walls': 'Wall work follows the consonants: ochre pilasters for p/t/k, mudéjar brick for s/f, blind horseshoe niches for m/n, wrought-iron columns for l/r.',
  'townLegend.es.accent': 'A written accent puts an azulejo (painted tile) medallion over the window; a silent letter adds a blind window.',
  'townLegend.es.phrase': 'A phrase is a row of buildings: little words become a stone step with geranium pots, azulejo plaques, a horseshoe passage, an arch over the lane or an iron cancela (gate) between them, and a question hangs a bell in an espadaña (bell gable).',
  'townLegend.es.together': 'Words that belong together are built together: shared walls, one band of azulejos, stone walkways and strings of feria lanterns; an arch over the lane joins two linked words once both are well built.',
  'townLegend.es.bridge': 'A word heard in more than one moment bridges them with a stone bridge on round arches; its iron farolas are lit while you practised a shared word this week.',
  'townLegend.es.use': 'By use: soportales (an arcade of shops under a striped awning) for the market, a patio with orange trees and a tiled fountain for the garden, an azulejo dome or a mudéjar tower for a landmark. Some verb workshops get windmill sails that turn, others a whitewashed chimney that smokes, when practised this week.',
  'townLegend.es.gold': 'Gold trim round the openings and a giraldillo (gilt weathervane) or a pennant mark words that took many tries; lime grime and moss mark old words you keep up.',
  'townLegend.es.lanes': 'Streets: each is a line of that moment, chalked between its words\' houses, and paved in stone once you know every word of it.',
  // en kit
  'townLegend.en.lights': 'Gas-lamp amber in the sashes and at the doors marks words you practised this week; the brighter, the more recent. The rest sleeps in moonlight.',
  'townLegend.en.grow': 'Buildings grow as you practise: scaffolding (pale poles and planks round a dark brick plinth, as high as the walls to come), wood (boarded timber walls), stone (bare London-stock brick with plain openings), then gold (the finished terrace: Flemish-bond brick over a white stucco ground floor, white sashes, a panelled door under a fanlight, and a slate roof behind a parapet gilded along its ridge, coping or finial).',
  'townLegend.en.syllables': 'One bay per syllable, and the tallest bay is where the stress falls; on a house it puts out a bay window.',
  'townLegend.en.openings': "Windows follow the vowel's mouth shape: a a round-headed sash, e a six-over-six sash, i a narrow sash, o a round window, u a small bullseye.",
  'townLegend.en.walls': 'Wall work follows the consonants: stone quoins for p/t/k, a red-brick pilaster for s/f, a blind arch for m/n, a cast-iron downpipe for l/r.',
  'townLegend.en.accent': 'A written accent puts a white keystone over the window; a silent letter adds a blind window, painted on.',
  'townLegend.en.phrase': 'A phrase is a terrace: little words become the stone steps and railings, street plaques, carriage arches, rooms over the lane and iron gates between its houses.',
  'townLegend.en.together': 'Words that belong together are built together: party walls, one parapet line, York-stone pavements and strings of lamps; an arch over the lane joins two linked words once both are well built.',
  'townLegend.en.use': 'By use: the pub on the corner in bottle-green tile (market), a village green with oaks and a red pillar box (garden), a church spire or a lead cupola (landmark), homes in terraces. Some verb workshops have sails that turn, others a chimney stack with clay pots that smokes, when practised this week.',
  'townLegend.en.gold': 'A gilt band and a weathervane or a flag mark words that took many tries; soot and rain streaks on the brick mark old words you keep up.',
  'townLegend.en.lanes': "Streets: each is a line of that moment, chalked between its words' houses, and paved in York stone once you know every word of it.",
  'townLegend.night.moor': 'The first moment sits in the middle; each new one moors beside the moment it shares the most words with. Tap a building to see its word, a sign to see the moment.',
  'townLegend.night.mist': 'Mist and a faint outline: heard, not practised yet. This is your frontier; the outline is the building the word will become.',
  'townLegend.night.bridge': "A bridge: the same word came up in both moments. A wider bridge means more shared words. The word flies a small flag in the other moment's colour. Moments too far apart for a bridge get a string of lights.",
  'townLegend.night.ivy': 'Ivy: due for review. It climbs the poles of a scaffold or the walls of the rest, and the later the word is, the higher it climbs.',
  'townLegend.night.guess': 'Rough guesses: what a building is used for and the small sign by the door (a topic like food or money) come from guessing at the English meaning.',
  'townLegend.night.stars': 'Stars: the more answers you give, the fuller the sky. The moon is fuller when more of your practised words were practised this week.',
  'townLegend.night.stats': 'Tonight: {lit} lit · {weathered} overgrown · {fog} in the mist · {forged} forged.',
  'townLegend.night.grow': 'Buildings grow as you practise: scaffolding (pale poles and planks round the foundations, as high as the walls to come), wood (boarded timber walls), stone (walls), then gold (plaster and a roof with a gilded ridge, coping or finial). From stone up a word is forged, ready for battle.',
  'townLegend.night.walls': 'Wall textures follow the consonants: stone for p/t/k, brick for s/f, arches for m/n, ironwork for l/r; a doubled consonant doubles its strip.',
  'townLegend.night.phrase': 'A phrase is a row of buildings: little words become the porches, plaques, gateways, arches and gates that join them, and a question hangs a bell.',
  'townLegend.night.use': 'By use: verbs are workshops (a chimney or a windmill), nouns homes, the rest gardens; expressions are landmarks with a dome or a spire, heard lines row houses with their gables to the street, replies market stalls.',
  'townLegend.night.lights': 'Warm windows and lanterns: practised in the last 7 days; the more recent, the more windows glow. Stop practising and the town goes dark.',
  'townLegend.night.gold': 'Gold trim and a weathervane or a banner mark words that took many tries.',
  'townLegend.night.fa.grow': 'Buildings grow as you practise: scaffolding (poplar poles and planks), wood (poplar walls with terrace edges, pillared porches and lattice screens), stone (bare khesht, sun-dried brick, with vaulted entrances), then gold (kahgel render, finished roofs and decorated portals, gilded along the coping, ridge or finial). From stone up a word is forged, ready for battle.',
  'townLegend.night.fa.syllables': 'One bay per syllable, and the tallest bay is where the stress falls; on a house it opens as an iwan, a vaulted porch behind a pishtaq (portal screen).',
  'townLegend.night.fa.openings': "Openings follow the vowel's mouth shape: a a pointed arch, e a wooden lattice, i a narrow arched slit, o an orosi (a sash window of coloured glass), u a star-shaped roozan.",
  'townLegend.night.fa.walls': 'Wall work follows the consonants: fired brick for p/t/k, hazarbaf (patterned brick) for s/f, blind arched niches for m/n, slim talar columns for l/r; a doubled consonant doubles it.',
  'townLegend.night.fa.phrase': 'A phrase is a row of buildings: little words become a brick step with benches, tile plaques, a pointed gateway, an arch over the lane or a studded gate between them, and a question hangs a bell.',
  'townLegend.night.fa.use': 'By use: bazaar vaults with domes (market), a chahar bagh with channels, a howz and cypresses (garden), a tiled pishtaq with a dome or minarets (landmark); verb workshops get an asbad (windmill) or a flue.',
  'townLegend.night.fa.lights': 'Amber lanterns and glowing orosi: practised in the last 7 days; the more recent, the more windows glow. Stop practising and the town goes dark.',
  'townLegend.night.fa.gold': 'Gold trim and a gilt finial or a pennant mark words that took many tries.',
  // fr kit
  'townLegend.night.fr.grow': 'Buildings grow as you practise: scaffolding (pale poles and planks round the foundations, as high as the walls to come), wood (boarded walls), stone (grey limestone walls), then gold (dressed cream stone and a slate roof with a gilded ridge, coping or finial). From stone up a word is forged, ready for battle.',
  // es kit
  'townLegend.night.es.grow': 'Buildings grow as you practise: scaffolding (pine poles and planks round a sandstone plinth, as high as the walls to come), wood (boarded pine walls), stone (bare rubble and golden sandstone), then gold (a warm whitewash over an ochre band, with a teja roof or a flat azotea, gilded along its ridge, coping or finial). From stone up a word is forged, ready for battle.',
  'townLegend.night.es.syllables': 'One bay per syllable, and the tallest bay is where the stress falls; a house has its heavy door there.',
  'townLegend.night.es.openings': 'Openings follow the vowel\'s mouth shape: a a horseshoe arch, e a window behind a reja (iron grille), i a tall balcony door, o a round-arched window, u an oculus.',
  'townLegend.night.es.walls': 'Wall work follows the consonants: ochre pilasters for p/t/k, mudéjar brick for s/f, blind horseshoe niches for m/n, wrought-iron columns for l/r; a doubled consonant doubles it.',
  'townLegend.night.es.phrase': 'A phrase is a row of buildings: little words become a stone step with geranium pots, azulejo plaques, a horseshoe passage, an arch over the lane or an iron gate between them, and a question hangs a bell.',
  'townLegend.night.es.use': 'By use: an arcade of shops (market), a patio with orange trees and a fountain (garden), an azulejo dome or a tower (landmark); verb workshops get windmill sails or a chimney.',
  'townLegend.night.es.lights': 'Warm faroles and glowing windows: practised in the last 7 days; the more recent, the more windows glow. Stop practising and the town goes dark.',
  'townLegend.night.es.gold': 'Gold trim and a gilt weathervane or a pennant mark words that took many tries.',
  // en kit
  'townLegend.night.en.grow': 'Buildings grow as you practise: scaffolding (pale poles and planks round a dark brick plinth, as high as the walls to come), wood (boarded timber walls), stone (bare London-stock brick), then gold (fired brick, Portland-stone trim and a slate roof gilded along its ridge, coping or finial). From stone up a word is forged, ready for battle.',
  'townLegend.night.en.walls': 'Wall work follows the consonants: stone quoins for p/t/k, a red-brick pilaster for s/f, a blind arch for m/n, a cast-iron downpipe for l/r; a doubled consonant doubles it.',
  'townLegend.night.en.phrase': 'A phrase is a terrace: little words become the steps, plaques, carriage arches and iron gates between its houses, and a question hangs a bell.',
  'townLegend.night.en.use': 'By use: the pub on the corner (market), a village green (garden), a church spire or a lead cupola (landmark), homes in terraces; verb workshops get sails or a chimney stack.',
  'townLegend.night.en.lights': 'Gas-lamp amber in the windows and at the doors: practised in the last 7 days; the more recent, the more windows glow. Stop practising and the town goes dark.',
  'townLegend.night.en.gold': 'Gold trim and a weathervane or a flag mark words that took many tries.',
  'townLegend.night.neutral.grow': 'Buildings grow as you practise: scaffolding (pale poles and planks round the foundations, as high as the walls to come), wood (boarded timber walls), stone (walls), then gold (whitewash and a slate roof with a gilded ridge, coping or finial). From stone up a word is forged, ready for battle.',
  'townLegend.night.neutral.seeded': "Every word has its own building. Until this language's sounds are read, its bays, windows and wall textures come from a pattern fixed by the word itself, so a word always keeps the same house; the tallest bay still marks the stress.",
  'townLegend.night.neutral.phrase': 'A phrase is a row of buildings sharing their walls.',
  'townLegend.night.neutral.use': 'By use: verbs are workshops, nouns homes, the rest gardens; expressions are landmarks with a dome or a spire, heard lines row houses, replies market stalls.',
  'tend.sheetLabel': 'A place in your town',
  'tend.close': 'Close',
  'tend.untitled': 'A moment',
  'tend.buildingLabel': 'The building, drawn large',
  'tend.tapBay': 'Tap a part of the building to see its syllable.',
  'tend.syllable': 'Syllable:',
  'tend.syllableStressed': 'Stressed syllable:',
  'tend.noSyllables': "This building's syllables are not known.",
  'tend.syllableUnknown': "This part's syllable can't be shown.",
  'tend.material.fog': "In the fog: you have heard it, but haven't met it yet.",
  'tend.material.scaffolding': 'Scaffolding: you can pick it out in its line.',
  'tend.material.wood': 'Wood: you can rebuild it in its own line.',
  'tend.material.stone': 'Stone: you can recall it from its meaning.',
  'tend.material.gold': 'Gold: you can use it somewhere new.',
  'tend.next.fog': 'Next: its first look.',
  'tend.next.scaffolding': 'Next, once it is due: rebuild it in its own line.',
  'tend.next.wood': 'Next, once it is due: recall it from its meaning.',
  'tend.next.stone': 'Next, once it is due: use it somewhere new.',
  'tend.next.gold': 'Next: keep it, with a check every couple of weeks.',
  'tend.state.fog': 'No lamp yet: it is still in the fog.',
  'tend.state.lamp': 'Lamp lit: you got it right in the last 7 days.',
  'tend.state.dark': 'Lamp dark: no right answer in the last 7 days.',
  'tend.state.known': 'You know this one: it needs no review.',
  'tend.state.later': 'Set aside for later: no review until you tap "Learn this" on its moment.',
  'tend.state.ivy': 'Overgrown: it is due for review.',
  'tend.tendIt': 'Tend it',
  'tend.firstLook': 'Take its first look',
  'tend.firstLookHint': "Get its first look right and it leaves the fog as scaffolding and joins tomorrow's review.",
  'tend.firstLookWait': 'This word has no first look available right now.',
  'tend.openMoment': 'Open the moment',
  'tend.alsoHeard': 'Also heard in:',
  'tend.roads': 'Roads:',
  'tend.roadsHint': "Cross with a shared word: it is asked in the other moment's line.",
  'tend.roadTo': 'To {title}',
  'tend.roadFog': '(in the fog)',
  'tend.roadTitle': 'A road',
  'tend.roadHint': "Words heard in both moments. Cross with one: it is asked in the other moment's line.",
  'tend.roadEmpty': 'No word is heard in both of these moments yet.',
  'tend.count.ivy': 'Overgrown, due for review: {n}',
  'tend.count.dark': 'Dark, no right answer this week: {n}',
  'tend.count.fog': 'In the fog, not met yet: {n}',
  'tend.districtHint': 'Tending asks up to 12 cards: any easier card still waiting, then overgrown buildings, dark ones and up to {n} first looks.',
  'tend.districtHintOne': 'Tending asks up to 12 cards: any easier card still waiting, then overgrown buildings, dark ones and 1 first look.',
  'tend.districtHintNone': 'Tending asks up to 12 cards: any easier card still waiting, then overgrown buildings and dark ones.',
  'tend.tendDistrict': 'Tend this district',
  'tend.titleWord': 'Tend a building',
  'tend.titleRoad': 'Across a road',
  'tend.titleDistrict': 'Tend a district',
  'tend.titleDistrictOf': 'Tend: {title}',
  'tend.nothing': 'Nothing to tend here now.',
  'tend.nothingDistrictHint': 'Every lamp here is lit and nothing is overgrown. Come back when a lamp goes dark or a building is due.',
  'tend.nothingHint': 'This building has no card for now. Come back tomorrow.',
  'tend.backToTown': 'Back to your town',
  'tend.done': 'Done',
  'tend.nearby': '{n} more houses nearby need tending',
  'tend.nearbyOne': 'One more house nearby needs tending',
  'tend.nextHouse': 'Next house',
  'tend.glintDue': 'An overgrown house: tend it next',
  'tend.glintDark': 'A house with its lamp out: tend it next',
  'tend.allTended': 'No more houses need tending right now.',
  'tend.retryNext': 'Try again',
  'townCheck.title': 'Town phone check',
  'townCheck.intro': "This page draws two made-up towns, never anyone's lessons, in both of the town's looks, and measures how fast this phone opens and pans them. It needs no account. It takes about {minutes} minutes: keep the screen on and don't touch it while it runs.",
  'townCheck.sizes': 'Small town: {smallD} moments, {smallB} buildings. Large town: {largeD} moments, {largeB} buildings.',
  'townCheck.notes': "Cold load: the first time a town opens in a look, from building the town to its first frame (the first one also loads the look's code). Warm load: opening the same town again. Median and 1% low: frames per second during a {seconds}-second automatic pan; the 1% low is the average of the slowest 1% of frames. Frames: how many frames the town drew during the pan. Missed: the share of frames that came late, more than 1.5 times the screen's own frame time (estimated from the run's steadiest frames), so it showed the old frame again. Longest frame: the longest gap between two frames. The long pan zooms the large town in and sweeps across all of it and back.",
  'townCheck.screen': 'Screen',
  'townCheck.screenValue': '{w} × {h} px',
  'townCheck.dpr': 'Device pixel ratio',
  'townCheck.dRatio': 'Lit diorama (D) pixel ratio',
  'townCheck.browser': 'Browser',
  'townCheck.panLength': 'Pan length: {seconds} s',
  'townCheck.present': 'present',
  'townCheck.missing': 'missing',
  'townCheck.noWebgl2': "This browser has no WebGL2, so the lit diorama (D) can't be drawn. The check draws only the night town (B).",
  'townCheck.dFailed': "The lit diorama (D) couldn't start on this device, so the check draws only the night town (B).",
  'townCheck.start': 'Start the check',
  'townCheck.again': 'Run it again',
  'townCheck.running': 'Measuring {look}, {size}: {step}…',
  'townCheck.stepCold': 'cold load',
  'townCheck.stepWarm': 'warm load',
  'townCheck.stepPan': 'panning for {seconds} s',
  'townCheck.stepSweep': 'long pan for {seconds} s',
  'townCheck.done': 'Done. Copy the results and send them to the owner.',
  'townCheck.stageLabel': 'The town being measured',
  'townCheck.look': 'Look',
  'townCheck.size': 'Town',
  'townCheck.cold': 'Cold load',
  'townCheck.warm': 'Warm load',
  'townCheck.median': 'Median fps',
  'townCheck.low': '1% low fps',
  'townCheck.frames': 'Frames',
  'townCheck.missed': 'Missed',
  'townCheck.longest': 'Longest frame',
  'townCheck.lookD': 'Lit diorama (D)',
  'townCheck.lookB': 'Night town (B)',
  'townCheck.small': 'Small town',
  'townCheck.large': 'Large town',
  'townCheck.longPan': '{size}, long pan',
  'townCheck.seconds': '{n} s',
  'townCheck.ms': '{n} ms',
  'townCheck.percent': '{n}%',
  'townCheck.noFrame': 'no frame',
  'townCheck.skipped': 'not drawn',
  'townCheck.skippedLine': '{look}, {size}: not drawn',
  'townCheck.line': '{look}, {size}: cold {cold}, warm {warm}, median {median} fps, 1% low {low} fps, {frames} frames, {missed} missed, longest frame {longest}',
  'townCheck.copy': 'Copy results',
  'townCheck.copied': 'Copied',
  'townCheck.copyFailed': "Couldn't copy. Select the text below and copy it.",
  'townCheck.effectsOff': '{size}, effects off',
  'townCheck.stepPrice': 'pricing each effect',
  'townCheck.effects': 'What each effect costs',
  'townCheck.effectsIntro': "The large town in the lit diorama (D), each frame timed until this phone has drawn it: panning a couple of pixels, and redrawing the whole town as a zoom does, at the fitted view and zoomed in {zoom}×. Every effect on and every effect off show the whole frame; each effect shows what it adds to a frame with every effect on, so a minus is a saving. Soft shadows and the wide glow are priced against the hard shadows and narrow glow a slow phone falls back to.",
  'townCheck.effect': 'Effect',
  'townCheck.fitPan': 'Pan',
  'townCheck.fitWhole': 'Redraw',
  'townCheck.zoomPan': 'Pan at {zoom}×',
  'townCheck.zoomWhole': 'Redraw at {zoom}×',
  'townCheck.fx.all': 'Every effect on',
  'townCheck.fx.softShadows': 'Soft shadows',
  'townCheck.fx.shadows': 'Moon shadows and shading',
  'townCheck.fx.wideGlow': 'Wide glow',
  'townCheck.fx.glow': 'Glow',
  'townCheck.fx.smooth': 'Smooth edges',
  'townCheck.fx.walls': 'Clean walls',
  'townCheck.fx.skip': "Skip what can't be seen",
  'townCheck.fx.off': 'Every effect off',
  'townCheck.msFine': '{n} ms',
  'townCheck.msSigned': '{n} ms',
  'townCheck.shadowMap': 'Moon shadow map, drawn once when the town opens: {ms}',
  'townCheck.fallback': 'On this phone the lit diorama would turn down: {effects}.',
  'townCheck.fallbackNone': 'On this phone the lit diorama keeps every effect.',
  'townCheck.effectLine': '{effect}: pan {fitPan}, redraw {fitWhole}, zoomed pan {zoomPan}, zoomed redraw {zoomWhole}',
  'townCheck.landing': 'A moment landing',
  'townCheck.landingIntro': 'The large town without its newest moment, then that moment lands as it does in the app: the camera flies to the open ground, its new words fall as lights onto their lots, its district rises from the fog and the camera flies to it. Every frame is timed, from the first flight to the end of the last, whatever the pan length.',
  'townCheck.landingBar': "The bar, the one the lit diorama holds a phone's pans to: a median of at least {median} fps and a 1% low above {low} fps.",
  'townCheck.landingSize': '{size}, a moment landing',
  'townCheck.stepLanding': 'a moment landing',
  'townCheck.bar': 'Bar',
  'townCheck.pass': 'passes',
  'townCheck.fail': 'fails',
  'townCheck.landingLine': '{look}, {size}: median {median} fps, 1% low {low} fps, {frames} frames, {missed} missed, longest frame {longest}; bar: {verdict}',
  'townCheck.landingStill': 'This phone asks for reduced motion, so the landings cut instead of flying and faded in instead of rising, as they do in the app.',

  // ---- the fog: new words wait for their first look (v1 ticket 07) ----
  'fog.waiting': '🌫 {n} new words waiting',
  'fog.waitingOne': '🌫 1 new word waiting',
  'fog.waitingTitle': 'Heard in your moments, not met yet. A few come out in each day’s review, and more when you reopen a lesson.',
  // A lesson cut short because reviews are waiting (v1 ticket 30); never a box number.
  'fog.fewer': 'Fewer new words today: {n} words are waiting for review.',
  'fog.fewerOne': 'Fewer new words today: 1 word is waiting for review.',
  'fog.fewerNone': 'Fewer new words today: reviews came first.',

  // ---- the daily round: the town at night, one queue, the payoff (v1 ticket 15); materials, never a box ----
  'round.title': "Today's round",
  'round.cardsToday': '{n} cards today',
  'round.cardsTodayOne': '1 card today',
  'round.start': "Start today's round ({n} cards)",
  'round.startOne': "Start today's round (1 card)",
  'round.continue': "Continue today's round ({n} left)",
  'round.continueOne': "Continue today's round (1 left)",
  'round.doneToday': "Today's round is done. Come back tomorrow.",
  'round.fading': '{n} lamps going out',
  'round.fadingOne': '1 lamp going out',
  'round.fadingTitle': 'Words you last got right 6 or 7 days ago: their lamps go out soon. A right answer on one lights it again.',
  'round.stop': 'Stop here',
  'round.bank': 'Bricks this round',
  'round.brick': '{word} → {material}',
  'round.material.fog': 'fog',
  'round.material.scaffolding': 'scaffolding',
  'round.material.wood': 'wood',
  'round.material.stone': 'stone',
  'round.material.gold': 'gold',
  'sign.inFog': 'in the fog',
  'sign.outOfFog': 'Out of the fog',
  'sign.find': 'Find {word} in what they said',
  'sign.notThat': 'Not that one: here it is, marked in the line.',
  'sign.means': '{word} means…',
  'sign.of': '{i} / {n}',
  'sign.play': 'Hear the line',
  'sign.stop': 'Stop the line',
  'sign.gap': 'Type the missing word',
  'sign.heard': 'Type what you hear',
  'sign.listen': 'Play it, then pick',
  'round.build': 'Build what I earned',
  'round.building': 'Building…',
  'round.andMore': 'and {n} more',
  'round.stopped': "Stopped here. The rest of today's round waits for you.",
  'round.complete': "Today's round is done",
  'round.nothing': "Nothing in today's round.",

  // ---- every moment lands in the town (v1 ticket 41): the landing, its sign, the first lesson there; never a box ----
  'land.lines': 'What was said',
  'land.streets': 'The lines they said, chalked as streets.',
  'land.skip': 'Skip',
  'land.skipTitle': 'Land every waiting moment at once and go home.',
  'land.fogWords': '{n} words wait in the fog.',
  'land.fogWordsOne': '1 word waits in the fog.',
  'land.fogNone': 'No new words wait in the fog here.',
  'land.firstLook': 'Take your first look',
  'land.startPractice': 'Start practice',
  'land.choiceNote': 'Your moment is saved. Start practice to try its words, or open the moment to see its details.',
  'land.lessonLoading': 'Getting the cards ready…',
  'land.opening': 'Opening your town…',
  'land.lessonAiOut': "This month's allowance is spent, so the cards can't be made now. These words will come in your daily round and your town.",
  'land.lessonFailed': "The cards couldn't be made just now. These words will come in your daily round and your town.",
  'land.lessonDone': 'No more cards here today.',
  'land.lessonTitle': 'First look',
  'land.lessonTitleOf': 'First look: {title}',
  'land.cardOf': 'Card {i} of {n}',
  'land.stopped': 'Stopped here. Your answers are saved, and the district keeps the rest for you today.',
  'land.lessonComplete': 'First look done',
  'land.continue': 'Continue the first look',
  'land.left': '{n} cards left today',
  'land.leftOne': '1 card left today',

  // ---- newcomers come to a quiet town (v1 ticket 42): the offer, the wait, the landing, Answer them, the wild ----
  'newcomers.offer': 'Someone new is at the gate: {title}, near {place}.',
  'newcomers.meet': 'Meet them',
  'newcomers.else': 'Someone else',
  'newcomers.notNow': 'Not now',
  'newcomers.failed': "The newcomers couldn't make it this time.",
  'newcomers.readiness.need-word': 'A newcomer needs a word you have met first.',
  'newcomers.readiness.fog-open': 'Meet a word from the fog to make room for a newcomer.',
  'newcomers.readiness.fog-waits': 'Your first looks are waiting for a later day.',
  'newcomers.readiness.landing': 'Finish the moment waiting to land first.',
  'newcomers.readiness.allowance': 'A newcomer is waiting for your next analysis allowance.',
  'newcomers.readiness.met-today': 'You met someone today. Another newcomer can come tomorrow.',
  'newcomers.readiness.not-now': 'You asked to wait today. The gate opens again tomorrow.',
  'newcomers.readiness.action.capture': 'Make a moment',
  'newcomers.readiness.action.round': 'Open today’s round',
  'newcomers.readiness.action.landing': 'Land the moment',
  'learning.roundNext': 'Open today’s round',
  'learning.roundWait': 'No first look or useful review card is available now. Check again later.',
  'learning.roundUnavailable': 'Today’s learning routes could not be checked. Refresh to try again.',
  'learning.roundChecking': 'Checking today’s learning routes…',
  'newcomers.gated': "They'll wait at the gate until today's review is done.",
  'newcomers.onTheWay': 'Newcomers are on their way to {town}…',
  'newcomers.onTheWayHome': 'Newcomers are on their way to your town…',
  'newcomers.settle': 'Newcomers settle in {town}.',
  'newcomers.settleHome': 'Newcomers settle in your town.',
  'newcomers.signTitle': 'Newcomers: {title}',
  'newcomers.pill': 'newcomers',
  'newcomers.answerPill': 'your answer',
  'newcomers.answeredLink': 'You answered {name}: open your answer',
  'newcomers.answeredLinkPlain': 'Answered: open your answer',
  'newcomers.sceneNote': 'Newcomers came to your town: a scene made up around words you know.',
  'newcomers.answer': 'Answer them',
  'newcomers.wild': 'You heard it in the wild, in {title}.',
  // The revamp: the person who comes, named through the flow (docs/tickets/newcomers-revamp).
  'newcomers.offerNamed': '{name} is at the gate: {title}, near {place}.',
  'newcomers.arriving': '{name} is coming through the gate…',
  'newcomers.waiting': '{name} is still waiting for your answer.',
  'newcomers.answerNamed': 'Answer {name}',
  'newcomers.settleNamed': '{name} settles in {town}.',
  'newcomers.settleNamedHome': '{name} settles in your town.',
  'newcomers.signNamed': '{name}, {role}',
  'land.newcomerWith': '{name}, {role}',
  'land.fogWordsAsk': "{n} words wait in the fog, and {name}'s question with them.",
  'land.fogWordsAskOne': "1 word waits in the fog, and {name}'s question with it.",
  'record.answerTitleNamed': '{name} asked',
  'record.answerSend': 'Answer {name}',
  'tend.personWaiting': '{name} is waiting for your answer.',
  'tend.personHome': '{name} lives here.',
  'record.answerTitle': 'Answer them',
  'record.answerAsked': '{name} asked:',
  'record.answerPlaceholder': "e.g. Yes, there's one next to the station",
  'record.answerHint': "Write or say your answer, in English or with the words you know. I'll imagine the scene going on, and take its words.",
  'land.loadError': "This moment couldn't reach your town just now.",
  'land.hearScene': 'Hear the scene',
  'land.justRead': 'Just read it',
  'land.hearAgain': 'Hear it again and catch the new words',
  'land.sceneContinue': 'Continue',
  'land.sceneStop': 'Stop',
  'land.sceneWith': 'With {name}',
  'land.sceneLines': '{n} lines',
  'land.sceneLinesOne': '1 line',
  'land.sceneYouSay': 'you say {n}',
  'land.sceneYouSayOne': 'you say 1',

  // ---- the first week (v1 ticket 37): the honest promise, the firsts, the effects' switch ----
  'week.promise': 'By this time tomorrow in your town: {list}.',
  'week.next': 'Next in your town: {list} on {day}.',
  'week.promiseIn': 'By this time tomorrow in {town}: {list}.',
  'week.nextIn': 'Next in {town}: {list} on {day}.',
  'week.houses': '{n} houses can become {material}',
  'week.house': '1 house can become {material}',
  'week.more': '{n} can become {material}',
  'week.sep': ', ',
  'week.and': ' and ',
  'week.calendar': 'Pass {word} tomorrow and it becomes wood, then stone about 3 days later, then gold a week after that.',
  'week.first.wood': 'Your first wooden house: {word}.',
  'week.first.stone': 'Your first forged word: {word}.',
  'week.first.gold': 'Your first gold house: {word}.',
  'week.first.line': 'Your first line where you know every word: {word}.',
  'week.comeback': 'You now know every word in {n} lines of {title}, from {ago}.',
  'week.comebackOne': 'You now know every word in 1 line of {title}, from {ago}.',
  'week.comebackOpen': 'Open it',
  'end.head.fogFirst': 'Your first word out of the fog: {word}.',
  'end.head.fog': '{n} words came out of the fog',
  'end.head.fogOne': '1 word came out of the fog',
  'end.head.climbed': '{n} houses climbed',
  'end.head.climbedOne': '1 house climbed',
  'end.head.lamps': '{n} lamps lit again',
  'end.head.lampsOne': '1 lamp lit again',
  'end.head.ivy': 'Ivy cleared from {n} houses',
  'end.head.ivyOne': 'Ivy cleared from 1 house',
  'end.again': 'Worth another look:',
  'end.againMore': '+{n}',
  'end.head.none': "Nothing moved this time. A word climbs only on a right answer when it's due.",
  'end.score': '{right} of {n} right',
  'end.chip.new': 'new',
  'end.chip.again': 'again',
  'end.chip.lamp': 'lamp lit',
  'end.chip.climbed': '↑ {material}',
  'end.chipsLabel': 'What changed in the town',
  'feel.switch': 'Sounds and vibration',
  'feel.on': 'On',
  'feel.off': 'Off',
  'music.switch': 'Town music',
  'music.credit': 'The town\'s music is played with notes from',
  // ---- the first night: the opening and the map (ticket 40) ----
  'opening.tagline': 'Learn a language from the moments around you.',
  'opening.begin': 'Tap to begin',
  'opening.beginHint': 'Best with sound on. About half a minute.',
  'opening.skipIntro': 'Skip the intro',
  'opening.skip': 'Skip',
  'opening.soundOn': 'Sound on',
  'opening.soundOff': 'Sound off',
  'opening.tapLot': 'Tap the lot',
  'opening.cap.out': 'Out late. Above you, people are talking.',
  'opening.cap.quick': 'Too quick to follow.',
  'opening.cap.stay': 'But a few words stay with you.',
  'opening.cap.why': 'Like the moon they just pointed at. Words heard where they happen are the ones that stick.',
  'opening.cap.lands': 'Each word you catch lands in the valley, in the fog.',
  'opening.cap.house': 'Learn a word, and its house goes up.',
  'opening.cap.glimpse': 'Only a glimpse. In your town, no house goes up until you learn its word.',
  'opening.cap.map': 'Every eavesdropper keeps a map.',
  'map.keeps': 'Every eavesdropper keeps a map.',
  'map.yours': 'This one is yours.',
  'map.nameTitle': 'Name your town',
  'map.askNote': 'Your town has no name yet. Give it one and lay its first stone.',
  'map.placeholder': 'or any name',
  'map.hint': 'Tap a name, or write your own.',
  'map.lay': 'Lay the first stone',
  'map.laying': 'Laying the stone…',
  'map.later': 'Name it later',
  'map.unnamedHelp': 'You can name it any time from the town menu.',
  'map.notNow': 'Not now',
  'map.nameInvalid': 'A name needs 1 to 24 characters, with no hidden marks.',
  'map.founded': 'Founded on the night of {date}',
  'map.firstStone': 'The first stone of {name}.',
  'map.firstStoneUnnamed': 'The first stone of your town.',
  'map.stoneNote': 'A stone, not a house: houses go up only for words you learn.',
  'map.unnamed': 'Your town',
  'map.toLand': 'Continue',
  'map.example': 'See a short, temporary example',
  'map.skipExample': 'Skip the example and choose a real moment',
  'map.backToTown': 'Back to your town',
  'land.of': 'The land of {name}.',
  'land.empty': 'Nothing is saved here yet. Record a conversation or write what you wanted to say to start a real moment.',
  'land.namingDeferred': 'You left your town unnamed. You can name it any time from the town menu.',
  'land.legendEmpty': 'Your town starts with a stone. Record people talking or write what you wanted to say; words from your real moments become houses as you learn them.',
  'home.watchOpening': 'Watch the opening',
  'home.renameTown': 'Rename your town',
  'map.hereBeWords': 'here be words',
  'map.slot': 'the first stone goes here',
  'map.north': 'N',
  'map.est': 'est. {year}',
  // ---- the first night's example (after naming; nothing is kept) ----
  'example.listening': 'Listening…',
  'example.recNote': 'Only an example: nothing is being recorded.',
  'example.status': 'Listening back to what you caught…',
  'example.find': 'Picking out the words worth keeping…',
  'example.who.Customer': 'Customer',
  'example.who.Waiter': 'Waiter',
  'example.try': 'Try it once with an example.',
  'example.badge': 'Temporary example · nothing is saved',
  'example.temporary': 'Optional example: this recording and note are pretend; nothing is saved.',
  'example.skip': 'Skip example',
  'example.skipToReal': 'Skip the temporary example and choose a real moment.',
  'example.stopAfter': 'Stop and send (after {n} s)',
  'example.stop': 'Stop and send',
  'example.heard': '{n} words heard.',
  'example.heardTyped': '{n} words in what you wanted to say.',
  'example.outline': 'Each word waits in the fog as a house. Nothing is built yet.',
  'example.district': '{n} words wait in the fog. Each house goes up only when you pass its first look.',
  'example.readThey': 'Read what they said',
  'example.readYou': 'Read what you wanted to say',
  'example.card.head': 'First look',
  'example.card.q': 'What does it mean?',
  'example.card.right': 'Right.',
  'example.card.means': 'It means “{meaning}”.',
  'example.card.pass': 'The fog lifts, the scaffolding goes up and its lamp is lit.',
  'example.card.miss': 'The fog stays. It comes back for another first look.',
  'example.card.listen': 'Listen',
  'example.next': 'Next first look',
  'example.seeTown': 'See the town tonight',
  'example.chron.of': 'The chronicle of {town}',
  'example.chron.title': 'The first night',
  'example.chron.up': '{n} houses went up in scaffolding, and {n} lamps are lit.',
  'example.chron.upOne': 'One house went up in scaffolding, and one lamp is lit.',
  'example.chron.fog': '{n} still wait in the fog.',
  'example.chron.fogOne': 'One still waits in the fog.',
  'example.chron.only': 'This was only an example: the café fades now, and nothing of it is kept. Your own moments will stay.',
  'example.chron.calendar': 'In your town, a word passed again the next day becomes wood, then stone about {n} days later, then gold a week after that.',
  'example.nothingUp': 'Nothing went up tonight. The fog keeps its houses until you pass their first look.',
  'example.yours': "Now it's yours",
  'example.chooseReal': 'Start a real moment now:',
  'example.realRecord': 'Record a real moment',
  'example.realType': 'Write a real moment',
  'example.type.title': 'What did you want to say?',
  'example.type.hint': 'In your own words. English is fine.',
  'example.type.make': 'Make it a moment',
  'example.type.note': 'Only an example: any note becomes the same made-up moment.',
  'example.who.You': 'You',
  'example.bannerHint': 'tap to read what they said',
  'example.bannerHintTyped': 'tap to read what you wanted to say',
  'example.theySaid': 'What they said',
  'example.youWanted': 'What you wanted to say',
  'example.yourNote': 'Your note',
  'example.madeUp': 'A made-up moment. The words with a dashed line wait in the fog of {town}.',
  // ---- inviting a friend (the owner, beta) ----
  'invite.menu': 'Invite a friend',
  'invite.title': 'Invite a friend',
  'invite.once': 'Each link works once, for {days} days. Send it privately.',
  'invite.make': 'Make an invitation link',
  'invite.share': 'Share',
  'invite.copy': 'Copy',
  'invite.copied': 'Copied',
  'invite.shownOnce': 'This link is shown only now. If you lose it, revoke it and make another.',
  'app.name': 'Eaves',
  'invite.shareTitle': 'Eaves',
  'invite.shareText': 'Join me on Eaves: this link works once.',
  'invite.openTitle': 'Open invitations',
  'invite.none': 'No open invitations.',
  'invite.until': 'Works until {date}',
  'invite.revoke': 'Revoke',
  'invite.another': 'Make another link',
  'invite.close': 'Close',
  'error.owner-only': 'Only the owner can invite people.',
  'error.owner-invite-limit': "That's enough invitations for today. Try again tomorrow.",
  'error.forbidden': "You don't have access to that.",
  'error.json-required': 'That request was refused. Reload the app and try again.',
  'error.cross-site': 'That request came from another site, so it was refused. Open the app itself and try again.',
  'error.research-too-large': 'The research request was too large. Try again.',
  'error.research-inactive': 'Research participation is not active.',
  'error.research-invalid': 'That research request is not valid. Try again.',
  'error.research-subject-mismatch': 'That event does not belong to this research participant.',
  'error.research-consent-changed': 'Research consent changed in another tab. Check the setting and try again.',
  'error.research-unavailable': 'Research evidence could not be saved. Your learning still works normally.',
  'error.research-range': 'That research date range is not valid.',
  'error.recall-filter': 'That recall report request is not valid.',
  'error.recall-range': 'That recall date range is not valid.',
  'error.recall-unknown': 'That transfer task is not known.',
  // ---- field signals: the report sheet, the switch and the privacy line (ticket 43) ----
  'report.menu': "Something's wrong here",
  'report.title': "Something's wrong here",
  'report.lede': 'A problem, an idea or a question for the people making Eaves. They read every report but rarely reply in the app — a question stays on this device until it is sent.',
  'report.kind': 'What is it?',
  'report.problem': 'A problem',
  'report.idea': 'An idea',
  'report.question': 'A question',
  'report.note': 'What happened, or what would help?',
  'report.noteHint': 'Read by the people making Eaves. Recent errors and the screens you opened go with it, never what you recorded or typed.',
  'report.count': '{n} / {max}',
  'report.include': 'Include this moment',
  'report.includeHint': 'Shares this moment with support, so we can read it and nothing else. You can stop sharing on the moment.',
  'report.send': 'Send',
  'report.sent': 'Sent — thank you.',
  'report.saved': "Saved; it will go when you're back online.",
  'report.kept': 'The note above is kept as you wrote it — send it again when you can.',
  'report.close': 'Close',
  'diag.switch': 'Send error reports',
  'privacy.line': 'Eaves sends error reports and usage counts, never what you recorded or typed. You can turn this off in ⋯.',
  'error.report-limit': "That's enough reports for today. Thank you: try again tomorrow.",
  'error.report-invalid': "That report couldn't be sent. Write a short note and try again.",
  'error.activity-not-found': "That update isn't here any more. The list is up to date.",
  'error.invitation-check-too-large': 'That invitation check was too large. Try again.',
  // ---- one reminder a day at most (ticket 38): the sheet, the offer, and the text the service worker puts together ----
  'reminder.menu': 'Reminders',
  'reminder.title': 'Reminders',
  'reminder.close': 'Close',
  'reminder.privacy': "Reminders go through your browser's push service (Google's, Apple's or Mozilla's), which sees when one arrives, never what it says.",
  'reminder.when': 'When do you have five minutes?',
  'reminder.preset.morning': 'With morning coffee',
  'reminder.preset.lunch': 'At lunch',
  'reminder.preset.home': 'On the way home',
  'reminder.preset.dinner': 'After dinner',
  'reminder.preset.other': 'Another time',
  'reminder.timeAria': 'Time of the reminder',
  'reminder.card': 'Ask me one of my words',
  'reminder.cardHint': 'A reminder may show the meaning of one of your words on your lock screen.',
  'reminder.state.unsupported': "This browser can't deliver reminders. Turn them on from another supported device.",
  'reminder.state.ios': 'On iPhone or iPad, open Eaves in Safari, choose Share → Add to Home Screen, then open its icon to turn reminders on.',
  'reminder.state.on': 'On for this phone.',
  'reminder.state.onSync': "On for this phone, through the browser's daily check.",
  'reminder.state.stopped': 'Reminders stopped on this phone. Turn them on again?',
  'reminder.state.paused': 'Paused: the last few went unopened.',
  'reminder.state.denied': "Notifications are blocked for Eaves. Open this site's settings in your browser, allow notifications, then return and check again.",
  'reminder.state.checkingCapabilities': 'Checking whether this browser can send reminders…',
  'reminder.setupAfterCapability': 'Set a reminder time after reminders are enabled on a supported device. Your saved preferences have not changed.',
  'reminder.state.checking': 'Checking that a reminder reaches this phone…',
  'reminder.state.fellBack': "Push didn't reach this phone. Reminders will come from the browser's own daily check instead, at a less exact time.",
  'reminder.turnOn': 'Turn on',
  'reminder.turnOff': 'Turn off',
  'reminder.turnBack': 'Turn back on',
  'reminder.tryAgain': 'Try again',
  'reminder.offline': 'You’re offline. Reconnect before turning on reminders or changing their settings. Your saved settings are unchanged.',
  'reminder.checkAgain': 'Check again',
  'reminder.offer.line': 'One nudge a day when houses are ready?',
  'reminder.offer.button': 'Reminders',
  'reminder.offer.dismiss': 'Not now',
  'reminder.push.test.title': 'Reminders are on.',
  'reminder.push.test.body': "This is how they'll arrive.",
  'reminder.push.raid.title': 'An army waits at your gates.',
  'reminder.push.raid.body': 'From {name}. Defend before it plays itself, in about {hours} hours.',
  'reminder.push.raid.bodyOne': 'From {name}. Defend before it plays itself, in about 1 hour.',
  'reminder.push.newcomers.title': "Someone new is at your town's gate.",
  'reminder.push.houses.cardTitle': 'How do you say “{meaning}”?',
  'reminder.push.houses.cardBody': '{count} houses are ready to build.',
  'reminder.push.houses.cardBodyOne': '1 house is ready to build.',
  'reminder.push.houses.title': '{count} houses are ready to build in {town}.',
  'reminder.push.houses.titleOne': '1 house is ready to build in {town}.',
  'reminder.push.houses.titleNoTown': '{count} houses are ready to build.',
  'reminder.push.houses.titleNoTownOne': '1 house is ready to build.',
  'reminder.push.houses.any': 'Some houses are ready to build.',
  'reminder.push.report.title': "Your army is back from {name}'s town.",
  'reminder.push.report.titleNoName': 'Your army is back.',
  'reminder.push.raidIn.title': "{name}'s army is at your gates.",
  'reminder.push.raidIn.titleNoName': 'An army is at your gates.',
  'reminder.push.raidIn.body': 'Defend before it plays itself, in about {hours} hours.',
  'reminder.push.raidIn.bodyOne': 'Defend before it plays itself, in about 1 hour.',
  'reminder.push.raidDone.title': "Your raid on {name}'s town: {band}",
  'reminder.push.raidDone.titleNoName': 'Your raid is over: {band}',
  'reminder.push.raidDone.titleOnly': "Your raid on {name}'s town is over.",
  'reminder.push.raidDone.titleOnlyNoName': 'Your raid is over.',
  'reminder.push.raidDone.held': 'Held.',
  'reminder.push.raidDone.draw': 'A draw.',
  'reminder.push.raidDone.breach': 'Breached!',
  'reminder.push.raidDone.sacked': 'Sacked!',
  'reminder.push.raidDone.glory': '+{glory} glory',
  'reminder.raidAlerts': 'Raid alerts',
  'reminder.raidAlertsHint': 'A notice when an army arrives at your gates and when your own raid is over. Never between 10 pm and 8 am.',
  'error.reminder-endpoint': "This browser's push service isn't supported, so reminders can't reach this phone.",
  'error.reminder-invalid': "That setting couldn't be saved. Try again.",
  'error.reminder-limit': "That's enough tests for today. Try again tomorrow.",
};

const fa: Record<string, string> = {
  'research.title': 'کمک به بهتر شدن فال‌گوش',
  'research.explanationDefault': 'تو حضوری پذیرفته‌ای که شرکت کنی، برای همین فال‌گوش بدون پرسیدن در هر صفحه‌ی تازه این مرحله‌های بدون محتوا را ثبت می‌کند: راه‌اندازی، نمونه‌ی اختیاری، اولین لحظه‌ی نوشتاری یا ضبط‌شده‌ات و این‌که ذخیره شده یا نه، مرحله‌های ثبت لحظه، و زمان در دسترس بودن، نمایش، باز شدن و انجام عمل در به‌روزرسانی‌های اجتماعی. صاحب برنامه می‌تواند این شواهد را ببیند. «توقف و پاک کردن» آن‌ها را پاک می‌کند و تا دوباره عضو نشی چیزی جمع‌آوری نمی‌شود.',
  'research.explanation': 'اگر عضو بشی، فال‌گوش فقط مرحله‌های بدون محتوا در مسیر راه‌اندازی، نمونه‌ی اختیاری و اولین لحظه‌ی نوشتاری یا ضبط‌شده‌ات را ثبت می‌کند؛ از جمله این‌که ذخیره شده یا نه، و همچنین مرحله‌های ثبت لحظه و زمان در دسترس بودن، نمایش، باز شدن و انجام عمل در به‌روزرسانی‌های اجتماعی را. صاحب برنامه می‌تواند این شواهد را ببیند. با باز کردن صفحه‌ای تازه، پیش از جمع‌آوری دوباره ازت اجازه می‌گیرد.',
  'research.neverCollected': 'این رویدادها هیچ‌وقت یادداشت، صدا، جواب تمرین، واژه، نام دوست، اطلاعات ورود، توکن یا محتوای درخواستت را شامل نمی‌شوند. هر زمان بخواهی می‌توانی جمع‌آوری را متوقف کنی و داده‌ها را پاک کنی.',
  'research.agree': 'با اشتراک‌گذاری این رویدادهای پژوهشی موافقم',
  'research.join': 'پیوستن به پژوهش',
  'research.stop': 'توقف و پاک کردن داده‌های پژوهشی',
  'research.joining': 'در حال پیوستن…',
  'research.stopping': 'در حال توقف…',
  'research.stopped': 'جمع‌آوری در این دستگاه متوقف شده است. سرور حذف را تأیید نکرده، پس ممکن است شواهدی روی سرور مانده باشد. دوباره آنلاین شو و تلاش کن.',
  'research.unavailable': 'تنظیم پژوهش تغییر نکرد. یادگیری‌ات همچنان کار می‌کند.',
  'research.statusActive': 'ثبت رویدادهای پژوهشی روشن است. هر زمان بخواهی می‌توانی متوقفش کنی و داده‌ها را پاک کنی.',
  'replay.title': 'بازپخش تصویری اختیاری',
  'replay.explanation': 'با اجازه‌ای جداگانه، صفحهٔ قابل‌دیدن فال‌گوش را در همین زبانه ثبت می‌کنیم. فریم‌ها ممکن است یادداشت‌هایی که نوشتی، متن درس و صفحه‌های تمرین را داشته باشند تا صاحب برنامه مسیر را بررسی کند. فریم‌ها روی همین سرور می‌مانند و فقط صاحب برنامه می‌تواند بازپخش را ببیند. صفحه‌های ورود، دعوت، بازیابی و صفحه‌های دارای توکن؛ گذرواژه و اطلاعات ورود؛ صدای میکروفن؛ دسکتاپ و زبانه‌های دیگر؛ و بدنهٔ درخواست‌ها و پاسخ‌های شبکه ثبت نمی‌شوند. بازپخش یک نسخهٔ ساده‌شده از صفحه است و ممکن است بعضی تصویرهای تعاملی دقیقاً مثل زمان استفاده دیده نشوند.',
  'replay.agree': 'با ثبت صفحه‌های قابل‌دیدن در همین زبانه موافقم',
  'replay.start': 'شروع بازپخش',
  'replay.stop': 'توقف ثبت',
  'replay.stopAndClear': 'توقف و پاک کردن بازپخش',
  'replay.reset': 'پاک‌کردن و ادامهٔ بازپخش',
  'replay.starting': 'در حال شروع…',
  'replay.stopping': 'در حال توقف…',
  'replay.resetting': 'در حال پاک‌کردن…',
  'replay.unavailable': 'تنظیم بازپخش تغییر نکرد. ثبت در این زبانه متوقف است.',
  'replay.inactive': 'بازپخش خاموش است. فریم‌های ذخیره‌شده تا زمان انقضا یا پاک کردن می‌مانند.',
  'replay.active': 'صفحه‌های قابل‌دیدن در همین زبانه ثبت می‌شوند. میکروفن یا دسکتاپ ضبط نمی‌شود.',
  'replay.stopped': 'بازپخش متوقف شد. فریم‌ها تا زمان انقضا یا پاک کردن برای صاحب برنامه می‌مانند.',
  'replay.cleared': 'بازپخش متوقف و فریم‌های ذخیره‌شده پاک شدند.',
  'replay.researchRequired': 'اول به مطالعهٔ مسیر یادگیری بپیوند. بازپخش انتخابی جداگانه و اختیاری است.',
  'replay.notActivated': 'بازپخش در این استقرار فعال نشده است. چیزی ثبت نمی‌شود.',
  'replay.retention': 'فریم‌ها تا {days} روز روی همین سرور نگه داشته می‌شن.',
  'replay.retentionOne': 'فریم‌ها فقط یه روز روی همین سرور نگه داشته می‌شن.',
  'research.generationAgree': 'همچنین موافقم که صاحب برنامه ورودی‌های تولید، تلاش‌های خام مدل، محتوای بسته و آزمون تولیدشده، و بسته یا آزمونی را که دقیقاً نمایش داده شده بررسی کند، برای یادداشت‌های نوشتاری و ضبط‌شده.',
  'research.generationEnable': 'اجازه‌ی بررسی بسته و آزمون',
  'research.generationDisable': 'توقف اشتراک‌گذاری بسته و آزمون',
  'research.generationActive': 'صاحب برنامه می‌تواند سوابق تولید بسته و آزمون و بسته‌ها و آزمون‌های نمایش‌داده‌شده را بررسی کند. هر زمان بخواهی خاموشش کن.',
  'research.generationUnavailable': 'تنظیم اشتراک‌گذاری بسته و آزمون تغییر نکرد. دوباره تلاش کن.',
  'research.defaultNotice': 'برای بهتر شدن فال‌گوش، ثبت می‌شود چطور از برنامه استفاده می‌کنی و صاحب برنامه می‌تواند بسته‌ها و آزمون‌هایی را که برایت ساخته شده بررسی کند. این را حضوری پذیرفته‌ای. هر زمان بخواهی می‌توانی متوقفش کنی و داده‌ها را پاک کنی.',
  'research.defaultNoticeReplay': 'برای بهتر شدن فال‌گوش، ثبت می‌شود چطور از برنامه استفاده می‌کنی و صفحه‌ات هم ضبط می‌شود، و صاحب برنامه می‌تواند بسته‌ها و آزمون‌هایی را که برایت ساخته شده بررسی کند. یادداشت‌ها و متن درسی که روی صفحه است برای صاحب برنامه دیده می‌شود؛ گذرواژه‌ها پنهان می‌مانند. این را حضوری پذیرفته‌ای. هر زمان بخواهی می‌توانی متوقفش کنی و داده‌ها را پاک کنی.',
  'research.defaultStop': 'توقف',
  'research.defaultDismiss': 'باشه',

  // ---- shared ----
  'common.back': 'برگشت',
  'common.cancel': 'لغو',
  'common.sep': '، ',
  'common.dot': '، ',
  'common.home': 'خانه',
  'common.homeAria': 'خانه',
  'common.retry': 'دوباره',
  'common.dismiss': 'بستن',
  'lang.ca': 'کاتالانی',
  'lang.fa': 'فارسی',
  'lang.en': 'انگلیسی',
  'lang.es': 'اسپانیایی',
  'lang.fr': 'فرانسوی',
  'skill.read': 'خواندن',
  'skill.listen': 'شنیدن',
  'skill.retrieve': 'به یاد آوردن',
  'skill.speak': 'حرف زدن',

  // ---- join ----
  'join.title': 'حساب خصوصی خودت رو بساز',
  'join.blurb': 'این دعوت‌نامه فقط یک بار کار می‌کنه. یک نام کاربری و رمز انتخاب کن تا از هر دستگاهی بتونی برگردی.',
  'join.username': 'نام کاربری',
  'join.password': 'رمز عبور (حداقل ۱۰ کاراکتر)',
  'join.target': 'زبان یادگیری و زبان توضیح‌ها',
  'join.pairOption': '{target} — توضیح‌ها به {explain}؛ رابط: {interface}، {direction}',
  'join.rtl': 'راست به چپ',
  'join.ltr': 'چپ به راست',
  'join.pairHint': 'انتخاب این جفت، زبان و جهت خواندن فرم رو هم عوض می‌کنه. نام کاربری و رمزت توی فرم می‌مونه.',
  'account.pairTitle': 'جفت‌زبان تو',
  'account.pair': 'زبان یادگیری: {target}. توضیح‌ها: {explain}. رابط: {interface}.',
  'account.pairFixed': 'درس‌ها و پیشرفت این حساب با همین جفت‌زبان می‌مونه. اگر جفت اشتباهی انتخاب کردی، از کسی که دعوتت کرده کمک بگیر.',
  'join.creating': 'در حال ساختن…',
  'join.create': 'ساختن حساب',
  'join.checking': 'دارم دعوت‌نامه رو بررسی می‌کنم…',
  'join.deadHelp': 'از کسی که دعوتت کرده یک لینک تازه از صاحب برنامه بخواه. اگر قبلاً حساب ساختی، وارد شو.',

  // ---- sign in ----
  'signin.title': 'ورود',
  'signin.blurb': 'همه‌ی درس‌ها و پیشرفتت توی حساب خصوصی خودته — وارد شو و از همون‌جا که بودی ادامه بده.',
  'signin.username': 'نام کاربری',
  'signin.password': 'رمز عبور',
  'signin.busy': 'در حال ورود…',
  'signin.submit': 'ورود',
  'signin.accessHelp': 'حساب می‌خوای یا برای ورود دوباره کمک لازم داری؟',
  'signin.invitationHelp': 'دسترسی با دعوت‌نامه است. از مالک ایوز لینک دعوت بگیر و با آن یک بار حسابت را بساز.',
  'signin.recoveryHelp': 'رمزت یادت نیست؟ مستقیم به مالک ایوز پیام بده و لینک بازیابی بخواه. اینجا نام کاربری بررسی نمی‌شود و ایمیلی برای بازیابی فرستاده نمی‌شود؛ مالک می‌تواند بگوید چه زمانی پاسخ می‌دهد.',
  'signin.waitlist': 'عضویت در فهرست انتظار عمومی',

  // ---- recover ----
  'recover.deadTitle': 'لینک بازیابی در دسترس نیست',
  'recover.deadMsg': 'این لینک بازیابی دیگه معتبر نیست.',
  'recover.deadHelp': 'هر لینک فقط یک بار و برای مدت کوتاهی کار می‌کند. اگر رمزت یادت است وارد شو؛ وگرنه مستقیم از مالک ایوز لینک بازیابی تازه بخواه.',
  'recover.askOwner': 'ممکن است لینک منقضی شده، قبلاً استفاده شده یا جایگزین شده باشد. از مالک ایوز لینک تازه بخواه؛ این صفحه نمی‌تواند بگوید کدام اتفاق افتاده.',
  'recover.goSignin': 'رفتن به صفحه‌ی ورود',
  'recover.doneTitle': 'رمز تازه ثبت شد',
  'recover.returnUnavailable': 'وارد حسابت شدی. هیچ مقصد ذخیره‌شده‌ای در این مرورگر در دسترس نبود. مقصدها در همان مرورگری می‌مانند که ذخیره شده‌اند؛ پس ممکن است هنگام بازیابی در مرورگر یا دستگاه دیگری همراهت نیایند. می‌توانی از خانه ادامه بدهی.',
  'recover.goHome': 'رفتن به خانه',
  'recover.title': 'یک رمز تازه بگذار',
  'recover.blurb': 'درس‌ها، تمرین‌ها و زبانت دقیقاً سر جاشون می‌مونن — فقط رمزت عوض می‌شه. هر دستگاهی که با این حساب وارد شده بود باید دوباره وارد بشه.',
  'recover.password': 'رمز تازه (حداقل ۱۰ کاراکتر)',
  'recover.saving': 'در حال ذخیره…',
  'recover.submit': 'ثبت رمز و ورود',
  'recover.offline': 'به سرور نرسیدیم — یک لحظه دیگه دوباره امتحان کن.',

  // ---- onboarding ----
  'onboarding.title': 'تنظیم مسیر یادگیری',
  'onboarding.goalsTitle': 'دوست داری از پس چه موقعیت‌هایی بربیای؟',
  'onboarding.goalsHint': 'یک دقیقه وقت می‌بره و می‌تونی ردش کنی — با همین مشخص می‌شه درس‌ها اول سراغ کدوم عبارت‌ها برن.',
  'onboarding.goalsPlaceholder': 'مثلاً: چونه زدن توی بازار، گپ زدن با همسایه‌ها',
  'onboarding.priorTitle': 'تا حالا چقدر خوندی؟',
  'prior.just starting': 'تازه شروع کردم',
  'prior.a few phrases': 'چندتا عبارت بلدم',
  'prior.an evening class': 'یک کلاس رفتم',
  'prior.living here a while': 'مدتیه اینجا زندگی می‌کنم',
  'onboarding.canDoTitle': 'همین الان می‌تونم…',
  'cando.order_food': 'غذا یا قهوه سفارش بدم',
  'cando.follow_conversation': 'یک گفت‌وگوی ساده رو دنبال کنم',
  'cando.introduce': 'خودم رو معرفی کنم',
  'cando.ask_prices': 'قیمت بپرسم',
  'onboarding.saveStart': 'هدف‌ها رو ذخیره کن و یه لحظه‌ی واقعی انتخاب کن',
  'onboarding.saveCheck': 'هدف‌ها رو ذخیره کن و آزمون ۳ دقیقه‌ای رو بده',
  'onboarding.checkProgress': 'آزمون اولیه — {done} از {total} — فقط برای اینکه بفهمیم کجایی، نمره‌ای در کار نیست',
  'onboarding.skipRest': 'بقیه‌اش رو رد کن — بریم سراغ یادگیری',
  'onboarding.play': '🔊 پخش — از روی شنیدن جواب بده',
  'onboarding.playing': '🔊 در حال پخش…',
  'onboarding.typePlaceholder': 'به زبانی که یاد می‌گیری بنویس…',
  'onboarding.check': 'بررسی',
  'onboarding.nice': '✓ آفرین.',
  'onboarding.notQuite': '✗ نشد.',
  'onboarding.doneTitle': 'برآورد اولیه ذخیره شد',
  'onboarding.doneSub': '({n} سؤال — موقتیه، مدرک نیست)',
  'onboarding.speakingNote': 'حرف زدنت تا وقتی حرف نزنی معلوم نمی‌شه — کارت‌های «بلند بگو» و آزمون صوتی همین رو می‌سنجن.',
  'onboarding.recordFirst': '● اولین لحظه‌ات رو ضبط کن',
  'onboarding.typeFirst': '✍️ اولین لحظه‌ات رو بنویس',
  'ways.recordLine': 'آدم‌های دوروبرت دارن حرف می‌زنن. ۳۰ تا ۶۰ ثانیه ضبط کن.',
  'ways.typeLine': 'می‌خواستی چیزی بگی و نتونستی. با زبون خودت بنویسش.',
  'onboarding.firstTitle': 'اولین لحظه‌ات',
  'onboarding.firstSub': 'هر چی این‌جا یاد می‌گیری از یه لحظه میاد: چیزی که شنیدی، یا چیزی که می‌خواستی بگی.',
  'band.not_enough': 'هنوز جواب کافی نیست',
  'band.starting': 'تازه شروع کردی',
  'band.building': 'داری جلو می‌ری',
  'band.using': 'به کارش می‌بری',

  // ---- home ----
  'home.setup': '🧭 ویرایش هدف‌های یادگیری',
  'home.setupNew': '🧭 هدف‌هات رو بنویس و اولین لحظه‌ات رو انتخاب کن',
  'home.appInfo': 'درباره‌ی برنامه',
  'home.estimate': 'برآورد',
  'home.version': 'نسخه',
  'home.mode': 'حالت',
  'home.modeMock': 'نمایشی',
  'home.spent': 'هزینه',
  'home.logout': '⎋ خروج',
  'home.progressAria': 'پیشرفت یادگیری',
  'home.forged': '⚒ {n} کلمه آب‌دیده شده: توی شهرت سنگی یا طلایی‌ان، چون چند روز بعد از آشنایی از روی معنی‌شون یادت اومدن.',
  'home.forgedOne': '⚒ ۱ کلمه آب‌دیده شده: توی شهرت سنگی یا طلاییه، چون چند روز بعد از آشنایی از روی معنیش یادت اومد.',
  'home.forgedNone': '⚒ هنوز هیچ کلمه‌ای آب‌دیده نشده: کلمه وقتی آب‌دیده می‌شه که چند روز بعد از آشنایی از روی معنیش یادت بیاد.',
  'home.active': '{n} در جریان',
  'home.activeTitle': 'عبارت‌هایی که توی مرور فعال‌اند',
  'home.moments': 'لحظه‌هات',
  'home.createMoment': 'ساختن یک لحظه',
  'home.tapHint': 'روی یه ساختمون بزن تا تمرینش کنی',
  'home.forgedShort': '⚒ {n} آب‌دیده',
  'home.forgedShortOne': '⚒ ۱ آب‌دیده',
  'home.draftsChip': '📝 پیش‌نویس‌ها یه نگاه لازم دارن',
  'ways.record': 'ضبط کن',
  'ways.type': 'بنویس',
  'ways.listen': 'گوش بده',
  'ways.listenLine': 'من رو کنار آدم‌هایی که دارن حرف می‌زنن بذار، یا فقط بگو چی می‌خواستی بگی.',
  'ways.quill': 'به‌جاش بنویس',
  'dock.left': 'امروز {n} تا مونده',
  'dock.leftOne': 'امروز یکی مونده',
  'listen.title': 'فال‌گوش داره گوش می‌ده',
  'listen.notice': 'فال‌گوش فقط تا وقتی این باز باشه از میکروفونت گوش می‌ده. گوشیت یک بار اجازه می‌خواد.',
  'listen.notice.ok': 'باشه، گوش بده',
  'listen.done': 'تموم شد',
  'listen.cancel': 'لغو',
  'listen.dim': 'برای روشن شدن صفحه لمس کن',
  'listen.micRefused': 'فال‌گوش اینجا نمی‌تونه از میکروفون استفاده کنه. به‌جاش بگو چی می‌خواستی بگی.',
  'listen.micFailed': 'میکروفون باز نشد. به‌جاش بگو چی می‌خواستی بگی.',
  'listen.hearing': 'دارم می‌شنوم چی گرفتی…',
  'listen.slow.q': 'یه کم طول کشید. کی داشت حرف می‌زد؟',
  'listen.who.me': 'من، دارم به فال‌گوش می‌گم',
  'listen.who.near': 'آدم‌های کنار من',
  'listen.unclear.silence': 'کلمه‌ای نتونستم بگیرم. نزدیک‌تر امتحان کن، یا بگو چی می‌خواستی بگی.',
  'listen.unclear.music': 'موسیقی شنیدم، ولی کلمه‌ای نبود. نزدیک‌تر امتحان کن، یا بگو چی می‌خواستی بگی.',
  'listen.unclear.noise': 'سروصدا شنیدم، ولی کلمه‌ای نتونستم بگیرم. نزدیک‌تر امتحان کن، یا بگو چی می‌خواستی بگی.',
  'listen.again': 'دوباره گوش بده',
  'listen.tell': 'به‌جاش بگو',
  'listen.confirm.head': 'مطمئن نبودم کی با کی حرف می‌زد.',
  'listen.confirm.ask': 'این تو بودی که به فال‌گوش می‌گفتی، یا آدم‌های کنارت؟',
  'listen.confirm.guess': 'حدسم: {makes}',
  'listen.asked.head': 'تو به فال‌گوش گفتی',
  'listen.asked.makes': 'فال‌گوش یه صحنه‌ی کوتاه به {target} دور و بر این تصور می‌کنه.',
  'listen.eyebrow.heard': 'فال‌گوش شنید',
  'listen.tip.fix': 'برای درست کردن کلمه‌ها، روشون بزن',
  'listen.tip.who': 'برای عوض کردن گوینده، روی اسمش بزن',
  'listen.retell.head': 'آدم‌ها به زبان {language} حرف می‌زدن. به {target} بسازمش؟',
  'listen.retell.mine': 'اون تو بودی که حرف می‌زدی. ازش یه صحنه به {target} بسازم؟',
  'listen.retell.makes': 'فال‌گوش اون رو به‌صورت یه صحنه‌ی کوتاه به {target} دوباره تعریف می‌کنه.',
  'listen.overheard.many': '{n} جمله از چند صدا گرفتم.',
  'listen.overheard.one': '{n} جمله از کسی که حرف می‌زد گرفتم.',
  'listen.overheard.line': 'یک جمله از آدم‌هایی که حرف می‌زدن گرفتم.',
  'listen.overheard.makes': 'فال‌گوش از این جمله‌ها یه لحظه می‌سازه.',
  'listen.make.moment': 'لحظه رو بساز',
  'listen.make.scene': 'صحنه رو بساز',
  'listen.make.retell': 'به {target} بساز',
  'listen.notRight': 'درست نیست؟',
  'listen.notRight.asked': 'این من بودم که به فال‌گوش می‌گفتم',
  'listen.notRight.overheard': 'این آدم‌های کنار من بودن',
  'listen.line.who': 'کی گفت',
  'listen.line.you': 'تو',
  'listen.line.near': 'کنار تو',
  'listen.thread': 'چیزی که فال‌گوش شنید، به ترتیب',
  'listen.play': 'این تیکه رو پخش کن',
  'listen.wait.head': 'اولین شنیدن',
  'listen.wait.known': 'از این کلمه‌ها {n} تاش رو از قبل می‌دونی',
  'listen.wait.known1': 'از این کلمه‌ها یکی‌ش رو از قبل می‌دونی',
  'listen.wait.making': 'دارم لحظه‌ات رو می‌سازم…',
  'listen.unheard.kept': 'روی این گوشی نگه داشته شد. وقتی دوباره آنلاین شدی، «گوش بده» را باز کن تا شنیده شود.',
  'listen.unheard.count': '{n} ضبط منتظر شنیده شدنه.',
  'listen.unheard.dailyKept': '✓ به‌عنوان پیش‌نویس روی همین دستگاه موند. بعد از ساعت {time} به وقت {timezone} ممکنه آماده باشه. توی شهر «ادامهٔ ضبط ذخیره‌شده» رو بزن، بعد «همین حالا بشنو» رو انتخاب کن.',
  'listen.unheard.betaKept': '✓ به‌عنوان پیش‌نویس روی همین دستگاه موند. وقتی بودجه‌ی بتا ساعت ۰۰:۰۰ به وقت UTC در {date} برگرده، ممکنه آماده باشه. توی شهر «ادامهٔ ضبط ذخیره‌شده» رو بزن، بعد «همین حالا بشنو» رو انتخاب کن.',
  'listen.unheard.aiKept': '✓ به‌عنوان پیش‌نویس روی همین دستگاه موند. وقتی سهمیه‌ی هوش مصنوعی ساعت ۰۰:۰۰ به وقت UTC در {date} برگرده، ممکنه آماده باشه. توی شهر «ادامهٔ ضبط ذخیره‌شده» رو بزن، بعد «همین حالا بشنو» رو انتخاب کن.',
  'listen.unheard.listenUnavailable': 'گوش دادن الان در دسترس نیست. پیش‌نویس‌ها رو باز کن تا ضبط‌های ذخیره‌شده‌ات رو بررسی کنی، یا بعداً دوباره امتحان کن.',
  'listen.unheard.uncertain': 'روی همین دستگاهه؛ جواب قبلی نرسید، پس ممکنه لحظه‌اش قبلاً ساخته شده باشه. برای ادامه، «ادامهٔ ضبط ذخیره‌شده» رو بزن.',
  'listen.unheard.unavailable': 'این ضبط ذخیره‌شده برای این حساب روی این دستگاه در دسترس نیست. پیش‌نویس‌ها رو باز کن تا ضبط‌های ذخیره‌شده‌ات رو بررسی کنی.',
  'listen.unheard.reviewKept': 'ضبطت روی همین دستگاه ذخیره شده. مرور امروز رو تموم کن، بعد توی شهر «ادامهٔ ضبط ذخیره‌شده» رو بزن.',
  'listen.unheard.one': 'یک ضبط منتظر شنیده شدنه.',
  'listen.unheard.hear': 'همین حالا بشنو',
  'error.hear-too-large': 'این ضبط برای شنیدن خیلی بلنده. یه ضبط کوتاه‌تر امتحان کن.',
  'error.hear-limit': 'برای امروز گوش دادن زیاد شد. به‌جاش بگو چی می‌خواستی بگی، یا فردا دوباره امتحان کن.',
  'error.hear-failed': 'الان نتونستم بشنوم. دوباره امتحان کن، یا بگو چی می‌خواستی بگی.',
  'error.hear-gone': 'این ضبط دیگه نگه داشته نمی‌شه. دوباره گوش بده.',
  'error.hear-start-limit': 'این ضبط خیلی درس شروع کرده. دوباره گوش بده.',
  'error.hear-start-failed': 'الان نتونستم درس رو شروع کنم. دوباره امتحان کن.',
  'home.uploadedOne': 'درس ذخیره‌شده‌ات فرستاده شد ✓',
  'home.uploadedMany': '{n} درس ذخیره‌شده فرستاده شد ✓',
  'home.stillOffline': 'هنوز آفلاینی — همه چی روی همین دستگاه محفوظه.',
  'home.exam': '🎭 آزمون موقعیتی ({n} کلمه‌ی آشناشده)',
  'home.examLocked': '🎭 برای آزمون موقعیتی باید با {needed} کلمه آشنا شده باشی؛ {n} کلمه دیگه مونده.',
  'home.examLockedNext': '🎭 برای آزمون موقعیتی بعدی باید با {needed} کلمه آشنا شده باشی؛ {n} کلمه دیگه مونده.',
  'home.examRecovery.first-look': 'الان می‌تونی با یک نگاه اول با یک کلمه آشنا بشی.',
  'home.examRecovery.clear-backlog': 'مرورهای امروز رو انجام بده تا جا برای نگاه اول باز شه.',
  'home.examRecovery.wait': 'الان نگاه اولی برای آزمون در دسترس نیست. به تمرین ادامه بده و وقتی بشه با کلمه‌های تازه آشنا شد دوباره بررسی کن.',
  'home.examRecovery.need-material': 'یک لحظه بساز تا کلمه‌ی بیشتری داشته باشی؛ بعد با نگاه اول باهاش آشنا شو.',
  'home.examRecovery.action.round': 'رفتن به دور امروز',
  'home.examRecovery.action.capture': 'یک لحظه بساز',
  'home.setupEntry': '🧭 مسیر یادگیریت رو تنظیم کن: هدف‌هات و یه آزمون اولیه‌ی ۳ دقیقه‌ای',
  'home.setupEntryGoals': '🧭 مسیر یادگیریت رو تنظیم کن: هدف‌هات، توی یه دقیقه',
  'home.checkEntry': '🧭 آزمون اولیه‌ی ۳ دقیقه‌ای رو بده',
  'home.cantReach': 'به سرور نمی‌رسیم.',
  'home.noMoments': 'هنوز هیچ لحظه‌ای نداری.',
  'home.search': 'جست‌وجوی درس‌ها…',
  'home.searchAria': 'جست‌وجوی درس‌ها',
  'home.filterAria': 'فیلتر درس‌ها',
  'home.tabAll': 'همه ({n})',
  'home.tabNew': '✨ تازه ({n})',
  'home.tabPracticed': "شروع‌شده ({n})",
  'home.allCaught': 'همه چی مرتبه — درس تازه‌ای نمونده.',
  'home.allCaughtHint': 'ضبط‌های بعدی همین‌جا می‌آن.',
  'home.noPracticed': "هنوز درسی شروع نکرده‌ای.",
  'home.noPracticedHint': "برای شروع، یک درس انتخاب کن.",
  'home.noMatch': 'درسی با «{q}» پیدا نشد.',
  'home.sectionNew': '✨ تازه — هنوز تمرین نشده',
  'home.sectionPracticed': "تمرین شروع‌شده",
  'home.showMore': 'بیشتر ({n} تای دیگه)',
  'home.wantedPill': '✍️ می‌خواستم بگم',
  'home.items': '{n} مورد',
  'home.itemsOne': '۱ مورد',
  'home.tested': "{n} کارت با فعالیت ذخیره‌شده",
  'home.newHere': '● تازه — از این شروع کن',
  'home.allowanceLeft': '{remaining} لحظه‌ی تازه از {limit} تا برای امروز مونده',
  'home.allowanceNone': 'برای امروز لحظه‌ی تازه‌ای نمونده — تمرین و مرور بازه',
  'home.sharedPill': '🔓 به‌اشتراک‌گذاشته',
  'home.sharedTitle': 'با پشتیبانی به اشتراک گذاشته شده — برای قطع اشتراک، این لحظه رو باز کن',

  // ---- monthly AI allowance (ticket 08) ----
  'ai.line': 'هوش مصنوعی: {left} از {total} این ماه مونده',
  'ai.scope': 'سهم لحظه‌های روزانه و اعتبار ماهانه‌ی هوش مصنوعی جدا هستن. داشتن سهم روزانه به معنی در دسترس بودن اعتبار پولی هوش مصنوعی نیست.',
  'ai.allocationChanged': 'سهم تو از بودجه‌ی ماهانه‌ی مشترک بتا از {before} به {after} تغییر کرده. خودِ این تغییر سهم، هزینه‌ی استفاده از هوش مصنوعی نیست.',
  'ai.reasonOwn': 'سهمیه‌ی هوش مصنوعیت برای این ماه تموم شد — ساعت ۰۰:۰۰ به وقت UTC در {date} دوباره شارژ می‌شه. تمرین و مرور چیزهایی که داری همچنان بازه.',
  'ai.reasonBeta': 'بودجه‌ی مشترک هوش مصنوعی بتا تموم شده — ساعت ۰۰:۰۰ به وقت UTC در {date} دوباره شارژ می‌شه. تمرین و مرور چیزهایی که داری همچنان بازه.',
  'ai.recordReason': 'تحلیل هر لحظه از اعتبار هوش مصنوعی کم می‌کنه. {reason}',
  'ai.examReason': 'آزمون رو مدل از نو می‌نویسه — {reason}',
  'ai.refreshPack': '↻ سؤال‌های تازه برای این لحظه',
  'ai.refreshReason': 'سؤال‌های تازه رو مدل می‌نویسه. {reason}',
  'ai.pronReason': 'بررسی تلفظ رو مدل انجام می‌ده. {reason} به‌جاش روی کارت «گفتمش» رو بزن.',

  // ---- daily review requirement (ticket 09) ----
  'gate.lineOne': 'برای باز شدن لحظه‌های تازه‌ی امروز، اول {n} کارت رو مرور کن — {done} از {n} انجام شده',
  'gate.lineMany': 'برای باز شدن لحظه‌های تازه‌ی امروز، اول {n} تا کارت رو مرور کن — {done} از {n} تا انجام شده',
  'gate.reviewNow': 'همین الان مرور کن',
  'gate.recordHint': 'ضبط کردن همچنان کار می‌کنه — هرچی ضبط کنی تا مرورت تموم شه روی همین دستگاه منتظر می‌مونه.',
  'gate.previewOne': 'اولین لحظه‌ی تازه‌ی امروز با مرور کوتاه {n} کارتِ رسیده شروع می‌شه.',
  'gate.previewMany': 'اولین لحظه‌ی تازه‌ی امروز با مرور کوتاه {n} تا کارتِ رسیده شروع می‌شه.',
  'gate.keptAudio': 'ضبطت به‌عنوان پیش‌نویس روی همین دستگاه موند. مرور امروز رو تموم کن، بعد توی فهرست «ببین آماده‌ست یا نه» رو بزن تا معلوم شه می‌شه فرستادش یا نه.',
  'gate.keptText': 'متنت به‌عنوان پیش‌نویس روی همین دستگاه موند. مرور امروز رو تموم کن، بعد توی فهرست «ببین آماده‌ست یا نه» رو بزن تا معلوم شه می‌شه فرستادش یا نه.',
  'gate.progress': 'مرور امروز: {done} از {n} تا انجام شده',
  'gate.unlocked': 'مرور تموم شد — لحظه‌های تازه‌ی امروز باز شدن.',
  'gate.moreToGo': '{remaining} تای دیگه مونده تا لحظه‌های تازه‌ی امروز باز شن.',
  'gate.countsLabel': 'شمارش‌های مرور امروز',
  'gate.countRequirement': 'الزام مرور روزانه',
  'gate.countPending': '{done} از {required} تا پاسخ داده شده و {remaining} تای دیگه مونده',
  'gate.countComplete': 'تموم شد، {required} از {required} تا پاسخ داده شده',
  'gate.countNotFixed': 'با اولین درخواست لحظه‌ی تازه مشخص می‌شه',
  'gate.countWaived': 'امروز مروری اجباری نیست',
  'gate.countBatch': 'بسته‌ی اجباری امروز',
  'gate.countQueue': 'کل صف مرورهای رسیده',
  'gate.countRound': 'کل مرور نمایش‌داده‌شده‌ی امروز',
  'gate.countCards': '{n} کارت',
  'gate.localTime': 'منطقه‌ی زمانی ذخیره‌شده‌ی تو',
  'gate.continue': 'ادامه‌ی مرور',
  'gate.newMoment': 'لحظه‌ی تازه',
  'gate.noMic': 'اینجا میکروفون نیست — همین کارت رو یه جور دیگه ازت می‌پرسیم.',

  // ---- record ----
  'record.title': 'لحظه‌ی تازه',
  'record.tabRecord': '● ضبط',
  'record.tabWanted': '✍️ می‌خواستم بگم',
  'record.stop': 'توقف',
  'record.rec': 'ضبط',
  'record.paused': 'مکث — منتظریم کسی حرف بزنه',
  'record.recording': 'داریم صدای دور و برت رو ضبط می‌کنیم — تا {max} ثانیه حرف.',
  'record.skippedQuiet': ' {n} ثانیه سکوت رد شد.',
  'record.idleHint': 'گوشی رو عادی دستت بگیر. ۳۰ تا ۶۰ ثانیه از حرف زدن آدم‌های دور و برت رو ضبط کن.',
  'record.skipSilence': 'رد کردن سکوت‌ها (وقتی کسی حرف نمی‌زنه خودش مکث می‌کنه)',
  'record.wantedTitle': 'چی می‌خواستی بگی؟',
  'record.wantedHint': 'به فارسی بنویس یا بگو (اگه چند کلمه از زبانی که یاد می‌گیری بلدی، قاطیش کن). من یه صحنهٔ کوتاه تصور می‌کنم که توش همینو می‌گی، و کلمه‌هاش رو برمی‌دارم.',
  'record.wantedPlaceholder': 'مثلاً: می‌خواستم بپرسم این قطار توی ایستگاه بعدی وایمیسته یا نه',
  'record.wantedPlaceholder.ca': 'مثلاً: می‌خواستم بپرسم این قطار توی ایستگاه سانتس وایمیسته یا نه',
  'record.wantedPlaceholder.fa': 'مثلاً: می‌خواستم بپرسم این مترو تا تجریش می‌ره یا نه',
  'record.wantedPlaceholder.es': 'مثلاً: می‌خواستم بپرسم این قطار توی ایستگاه آتوچا وایمیسته یا نه',
  'record.wantedPlaceholder.en': 'مثلاً: می‌خواستم بپرسم این اتوبوس تا فرودگاه می‌ره یا نه',
  'record.wantedPlaceholder.fr': 'مثلاً: می‌خواستم بپرسم این قطار توی ایستگاه گار دو نور وایمیسته یا نه',
  'record.turning': 'دارم تبدیلش می‌کنم به عبارت…',
  'record.turnIntoLesson': 'تبدیلش کن به درس',
  'record.makeScene': 'صحنه رو بساز',
  'record.savedHere': '✓ روی این دستگاه ذخیره شد. می‌تونی از این صفحه بری؛ برای ارسال، برنامه رو آنلاین با همین حساب باز نگه دار.',
  'record.leaveUnsaved': 'یادداشت، ضبط یا نتیجهٔ شنیدن هنوز ذخیره نشده. دورش بندازم و خارج بشم؟ لغو یعنی ادامهٔ ویرایش.',
  'record.recordingTitle': 'ضبط',
  'record.listening': 'دارم گوش می‌دم…',
  'record.momentTitle': 'ماجرا چی بود؟',
  'record.optional': '(اختیاری)',
  'record.notePlaceholder': 'مثلاً: توی نانوایی، فروشنده و یک مشتری همیشگی، حرف سر فوتبال بود',
  'record.saveNote': 'این کار لحظه‌ات رو توی شهرت ذخیره می‌کنه. تمرین فقط وقتی خودت بخوای شروع می‌شه.',
  'record.noteHint': 'هر چی از آدم‌ها، جا یا موضوع می‌دونی کمک می‌کنه تحلیل، کلمه‌ها رو به موقعیت وصل کنه.',
  'record.analysing': 'در حال تحلیل…',
  'record.recordAgain': 'دوباره ضبط کن',
  'record.micNeeded': 'به میکروفون دسترسی لازم داریم.',
  'record.sayIt': 'به‌جاش بگو',
  'record.sayMore': 'بیشتر بگو',
  'record.sayStop': 'تموم شد',
  'record.sayCancel': 'لغو',
  'record.sayWriting': 'دارم چیزی رو که گفتی می‌نویسم…',
  'record.sayEmpty': 'هیچ کلمه‌ای نشنیدم — دوباره امتحان کن یا تایپش کن.',
  'record.sayNoMic': 'اینجا میکروفون در دسترس نیست، پس تایپش کن.',
  'record.sayMicDenied': 'اجازه‌ی میکروفون داده نشد، پس تایپش کن (یا اجازه بده و دوباره امتحان کن).',
  'record.errOfflineAudio': 'به سرور نرسیدیم — ضبط روی همین دستگاه موند و تا وقتی این برنامه باز و دوباره آنلاین باشه فرستاده می‌شه.',
  'record.errServerAudio': 'سرور لنگید — ضبط روی همین دستگاه موند و تا وقتی این برنامه بازه دوباره تلاش می‌شه.',
  'record.errOfflineAudioNoSave': 'جوابی به این دستگاه نرسید، پس شاید درس ساخته شده باشه. این ضبط فقط توی همین صفحه‌ست؛ قبل از رفتن، به‌عنوان پیش‌نویس ذخیره‌ش کن.',
  'record.errServerAudioNoSave': 'جواب سرور روشن نیست، پس شاید درس ساخته شده باشه. این ضبط فقط توی همین صفحه‌ست؛ قبل از رفتن، به‌عنوان پیش‌نویس ذخیره‌ش کن.',
  'record.errOfflineText': 'به سرور نرسیدیم — متن روی همین دستگاه موند و تا وقتی این برنامه باز و دوباره آنلاین باشه فرستاده می‌شه.',
  'record.errServerText': 'سرور لنگید — متن روی همین دستگاه موند و تا وقتی این برنامه بازه دوباره تلاش می‌شه.',
  'record.errOfflineTextNoSave': 'جوابی به این دستگاه نرسید، پس شاید درس ساخته شده باشه. این متن فقط توی همین صفحه‌ست؛ قبل از رفتن، به‌عنوان پیش‌نویس ذخیره‌ش کن.',
  'record.errServerTextNoSave': 'جواب سرور روشن نیست، پس شاید درس ساخته شده باشه. این متن فقط توی همین صفحه‌ست؛ قبل از رفتن، به‌عنوان پیش‌نویس ذخیره‌ش کن.',
  'record.pendingAudio': '🎙 ضبط',
  'record.pendingText': '✍️ متن',
  'record.tryOne': '۱ تلاش',
  'record.tryMany': '{n} تلاش',
  'record.seconds': '{n} ثانیه',
  'record.lastTry': 'آخرین تلاش: {err}',
  'record.sending': 'در حال ارسال…',
  'record.uploadedOne': 'درس ذخیره‌شده‌ات فرستاده شد ✓ — توی خانه ببینش.',
  'record.uploadedMany': '{n} درس ذخیره‌شده فرستاده شد ✓ — توی خانه ببینشون.',
  'record.allowanceLeft': '{remaining} لحظه از {limit} تا برای امروز مونده',
  'record.allowanceReason': 'سهمیه‌ی امروز تموم شد — لحظه‌ی تازه ساعت {time} به وقت {timezone} باز می‌شه. تمرین و مرور چیزهایی که داری همچنان بازه.',
  'record.practiseInstead': 'بریم سراغ تمرین چیزهایی که داری',
  'record.allowanceSpent': 'سهمیه‌ی لحظه‌های تازه‌ی امروزت همین بود. تمرین و مرور بازه.',

  // ---- the wait, while a new moment is analysed (v1 ticket 41) ----
  'wait.listening': 'دارم دوباره به چیزی که ضبط کردی گوش می‌دم…',
  'wait.shared': 'دارم به پیامی که به اشتراک گذاشتی گوش می‌دم…',
  'wait.turning': 'دارم یادداشتت رو به {language} برمی‌گردونم…',
  'wait.imagining': 'دارم یه صحنهٔ کوتاه به {language} دور یادداشتت تصور می‌کنم…',
  'wait.long': 'ضبط‌های شلوغ ممکنه تا نیم دقیقه طول بکشن.',
  'wait.longNote': 'این ممکنه تا نیم دقیقه طول بکشه.',
  'wait.play': 'پخش چیزی که ضبط کردی',
  'wait.pause': 'مکث',
  'wait.scene.who': 'دارم تصور می‌کنم با کی قراره حرف بزنی…',
  'wait.scene.lines': 'دارم جمله‌های اون رو می‌نویسم…',
  'wait.scene.turn': 'دارم جملهٔ تو رو می‌ذارم سر نقطهٔ عطف…',
  'wait.scene.translate': 'دارم هر جمله رو ترجمه می‌کنم…',
  'wait.rec.speakers': 'دارم تشخیص می‌دم کی داره حرف می‌زنه…',
  'wait.rec.writing': 'دارم چیزی رو که گفتن می‌نویسم…',
  'wait.rec.words': 'دارم کلمه‌هایی رو پیدا می‌کنم که ارزش نگه‌داشتن دارن…',

  // ---- device drafts (ticket 12) ----
  'drafts.resumeRecording': 'ادامهٔ ضبط ذخیره‌شده',
  'drafts.recordingKept': 'ضبطت روی همین دستگاه ذخیره شده. ضبط ذخیره‌شده رو ادامه بده تا شنیده بشه و لحظه‌ات رو بسازی.',
  'drafts.title': '📝 پیش‌نویس‌های این دستگاه ({n} از {max})',
  'drafts.hint': 'فقط روی همین دستگاه نگه داشته می‌شن: حداکثر {max} پیش‌نویس، ضبط تا {mb} مگابایت، متن تا {chars} نویسه. فقط وقتی این برنامه باز و آنلاین باشه فرستاده می‌شن. وقتی برنامه بسته‌ست هیچی فرستاده نمی‌شه.',
  'drafts.hintAccount': 'در بتا، برای فرستادن هر پیش‌نویس باید با همون حسابی وارد شی که ذخیره‌ش کرده.',
  'drafts.count': 'پیش‌نویس‌های این دستگاه: {n} از {max}',
  'drafts.stateLocal': 'فقط روی همین دستگاهه و هنوز فرستاده نشده. برای فرستادن، این برنامه باید باز و آنلاین باشه. «همین حالا بفرست» رو بزن.',
  'drafts.stateUnclear': 'روی همین دستگاهه، ولی جواب آخرین ارسال نرسید، پس ممکنه درسش ساخته شده باشه. «همین حالا بفرست» روشنش می‌کنه — حداکثر یه درس.',
  'drafts.stateDaily': 'روی همین دستگاهه. منتظر: لحظه‌های تازه‌ی امروز تموم شده — بعد از ساعت {time} به وقت {timezone} و با باز بودن این برنامه می‌شه فرستادش.',
  'drafts.stateReview': 'روی همین دستگاهه. منتظر مرور امروز — تا تمومش کنی می‌شه فرستادش.',
  'drafts.stateAiOwn': 'روی همین دستگاهه. منتظر: سهمیه‌ی هوش مصنوعی این ماهت تموم شده — بعد از ساعت ۰۰:۰۰ به وقت UTC در {date} و با باز بودن این برنامه می‌شه فرستادش.',
  'drafts.stateAiBeta': 'روی همین دستگاهه. منتظر: بودجه‌ی مشترک هوش مصنوعی بتا تموم شده — بعد از ساعت ۰۰:۰۰ به وقت UTC در {date} و با باز بودن این برنامه می‌شه فرستادش.',
  'drafts.stateFailed': 'روی همین دستگاهه و درس نمی‌شه: {why}. می‌تونی پاکش کنی.',
  'drafts.whyAccount': 'با یه حساب دیگه ذخیره شده',
  'drafts.whyLanguage': 'برای یه زبان دیگه ذخیره شده',
  'drafts.whyTooLarge': 'حجم این ضبط از محدودیت {mb} مگابایتی پیش‌نویس روی این دستگاه بیشتره.',
  'drafts.readError': 'نتونستم پیش‌نویس‌های روی این دستگاه رو بخونم. ممکنه هنوز این‌جا باشن؛ قبل از ضبط یا نوشتن چیز تازه، دوباره امتحان کن.',
  'drafts.continueWithoutRead': 'بدون بررسی پیش‌نویس‌ها ادامه بده',
  'drafts.whyRejected': 'سرور قبولش نکرد ({err})',
  'drafts.completed': '✓ درس شد',
  'drafts.reconciledOne': '✓ این درس قبلاً به سرور رسیده بود — دارم بازش می‌کنم. دوباره فرستاده نشد.',
  'drafts.reconciledMany': '✓ {n} تاشون قبلاً به سرور رسیده بودن — یکی رو باز می‌کنم. دوباره فرستاده نشد.',
  'home.reconciledOne': '✓ این درس قبلاً به سرور رسیده بود — دوباره فرستاده نشد.',
  'home.reconciledMany': '✓ {n} تاشون قبلاً به سرور رسیده بودن — دوباره فرستاده نشد.',
  'drafts.removed': 'قبلاً درس شده بود و بعد پاکش کردی — دوباره فرستاده نشد.',
  'drafts.open': 'باز کن',
  'drafts.dismiss': 'بستن',
  'drafts.tryNow': 'همین حالا بفرست',
  'drafts.sendNow': 'همین حالا بفرست',
  'drafts.checkNow': 'ببین آماده‌ست یا نه',
  'drafts.tryAll': 'همه رو حالا بفرست',
  'drafts.delete': 'پاک کن',
  'drafts.deleteAria': 'پاک کردن این پیش‌نویس',
  'drafts.confirmDelete': 'این پیش‌نویس از این دستگاه پاک بشه؟ دیگه برنمی‌گرده.',
  'drafts.keep': 'نگهش دار',
  'drafts.deleted': 'پیش‌نویس از این دستگاه پاک شد.',
  'drafts.saveAsDraft': '💾 ذخیره به‌عنوان پیش‌نویس',
  'drafts.savedDaily': '✓ به‌عنوان پیش‌نویس روی همین دستگاه موند. بعد از ساعت {time} به وقت {timezone} ممکنه آماده باشه؛ وقتی برنامه بازه، «ببین آماده‌ست یا نه» رو بزن تا امتحانش کنیم.',
  'drafts.savedAiOwn': '✓ به‌عنوان پیش‌نویس روی همین دستگاه موند. وقتی سهمیه‌ی هوش مصنوعی ساعت ۰۰:۰۰ به وقت UTC در {date} برگرده، ممکنه آماده باشه؛ وقتی برنامه بازه، «ببین آماده‌ست یا نه» رو بزن تا امتحانش کنیم.',
  'drafts.savedAiBeta': '✓ به‌عنوان پیش‌نویس روی همین دستگاه موند. وقتی بودجه‌ی بتا ساعت ۰۰:۰۰ به وقت UTC در {date} برگرده، ممکنه آماده باشه؛ وقتی برنامه بازه، «ببین آماده‌ست یا نه» رو بزن تا امتحانش کنیم.',
  'drafts.full': 'الان {max} پیش‌نویس برای حسابت روی این دستگاه هست، یعنی حداکثر. یکی رو پایین پاک کن، بعد این یکی رو ذخیره کن.',
  'drafts.tooLong': 'برای پیش‌نویس زیادی بلنده — حداکثر {max} نویسه.',
  'drafts.cantSave': 'روی این دستگاه نگه داشته نشد. این صفحه رو باز نگه دار و دوباره امتحان کن.',
  'drafts.noOwner': 'به سرور نرسیدیم و این دستگاه هنوز نمی‌دونه کی وارد شده، پس نمی‌تونه پیش‌نویس نگه داره. این صفحه رو باز نگه دار و دوباره امتحان کن.',
  'drafts.countPending': '{n} روی این دستگاه، فرستاده نشده',
  'drafts.countUnclear': '{n} تاشون ممکنه درس شده باشن — «همین حالا بفرست» قبل از فرستادن بررسی می‌کنه',
  'drafts.countBlocked': '{n} منتظر برداشته شدن محدودیت',
  'drafts.countFailed': '{n} فرستاده نمی‌شه',
  'drafts.manage': 'مدیریت پیش‌نویس‌ها',
  'drafts.needsAccount': 'برای فرستادن این پیش‌نویس‌ها با همون حسابی وارد شو که ذخیره‌شون کرده.',
  'drafts.stillWaiting': 'هنوز نه — چیزی قبول نشد و پیش‌نویس‌هات روی همین دستگاه می‌مونن.',
  'drafts.logoutOne': '۱ پیش‌نویس روی این دستگاه داری',
  'drafts.logoutMany': '{n} پیش‌نویس روی این دستگاه داری',
  'drafts.logoutBody': 'هنوز درس نشدن. اگه نگهشون داری، تا وقتی بیرونی نه دیده می‌شن نه فرستاده، و وقتی دوباره با همین حساب وارد شی برمی‌گردن.',
  'drafts.logoutDelete': 'پیش‌نویس‌هام رو پاک کن و خارج شو',
  'drafts.logoutKeep': 'روی این دستگاه نگهشون دار تا دوباره وارد شم',
  'drafts.logoutCancel': 'انصراف',

  // ---- moment ----
  'moment.delete': 'حذف',
  'moment.panelUp': 'نمایش کامل این لحظه',
  'moment.panelDown': 'نمایش دوباره‌ی شهر',
  'moment.textPill': '✍️ می‌خواستم بگم — بدون ضبط',
  'moment.practice': '✏️ تمرین',
  'moment.whatGoingOn': 'چه خبر بود',
  'moment.yourNote': 'یادداشت خودت: «{note}»',
  'moment.confidence': 'اطمینان {n}٪',
  'moment.confidenceHint': 'اطمینان یعنی تحلیل چقدر مطمئنه که حرف‌ها رو درست گرفته — هرچی کمتر باشه، بیشتر حدس زده.',
  'moment.confidenceHintTyped': 'اطمینان یعنی تحلیل چقدر مطمئنه که منظورت رو درست فهمیده — هرچی کمتر باشه، بیشتر حدس زده.',
  'moment.sample': 'داده‌ی نمونه',
  'moment.soFar': '{usd} دلار تا اینجا',
  'moment.replies': 'چی می‌تونستم بگم؟',
  'moment.repliesHint': 'اونایی رو نگه دار که می‌خوای بتونی بگی. جوابی که نگه داری می‌ره پیش کلمه‌های تازه‌ت و توی مرور برمی‌گرده؛ بقیه همین‌جا می‌مونن.',
  'moment.keepSeed': '🌱 نگه دار',
  'moment.keptSeed': '✓ نگه داشتی',
  'moment.keepSeedTitle': 'این جواب رو نگه دار تا یادش بگیری',
  'moment.unkeepSeedTitle': 'نگه داشتی. بزن تا دیگه یادش نگیری',
  'moment.seedIsWord': 'این از قبل جزو کلمه‌هاته',
  'climb.opens': 'وقتی {words} رو بلد باشی باز می‌شه',
  'climb.opensSome': 'وقتی {n} تا از {words} رو بلد باشی باز می‌شه',
  'climb.opensLines': 'وقتی {need} تا از جمله‌هات این الگو رو داشته باشن باز می‌شه (الان {n} تا)',
  'climb.pattern': 'الگو: {words}، توی {n} تا از جمله‌هات',
  'climb.comma': '، ',
  'climb.or': ' یا ',
  'climb.and': ' و ',
  'moment.learnFirst': 'اول این‌ها',
  'moment.learnFirstHint': 'این‌ها بیشتر به دردت می‌خورن — بقیه صبر می‌کنه.',
  'moment.forged': '⚒ آب‌دیده',
  'moment.whatTheySaid': 'چی گفتن',
  'moment.whatYouWanted': 'چیزی که می‌خواستی بگی',
  'moment.speakerYou': 'تو',
  'moment.imaginedScene': 'یه صحنهٔ خیالی',
  'moment.imaginedNote': 'دور چیزی که خواستی ساخته شده.',
  'moment.imaginedPill': '✍️ از روی یادداشتت ساخته شده',
  'moment.playScene': 'پخش صحنه',
  'moment.preparingScene': 'در حال آماده کردن صحنه…',
  'moment.yourTurn': 'نوبت تو',
  'moment.turnLocked': 'نوبت تو وقتی باز می‌شه که کلمه‌های خط‌هات از مه بیرون بیان: {n} تا هنوز توی مه‌ان',
  'moment.turnLockedOne': 'نوبت تو وقتی باز می‌شه که آخرین کلمه‌ی خط‌هات از مه بیرون بیاد',
  'moment.turnSaid': 'گفتمش',
  'moment.turnShow': 'نشونم بده',
  'moment.turnAll': 'همه‌ش رو گفتی.',
  'moment.turnAgain': 'یک بار دیگه',
  'tend.hearScene': 'شنیدن صحنه',
  'land.imagined': 'خیالی',
  'rude.tag': 'بی‌ادبانه',
  'rude.mild': 'بی‌ادبانه، ملایم',
  'rude.strong': 'بی‌ادبانه، شدید',
  'moment.showAll': 'نمایش همه',
  'moment.hideTranslations': 'پنهان کردن ترجمه‌ها',
  'moment.tapLine': 'روی هر خط بزن تا ترجمه‌اش بیاد، 🔊 رو بزن تا بشنوی.',
  'moment.blueprintsHint': 'کلمه‌هات علامت خورده‌ان. کارت‌ها توی همین جمله‌ها ازت می‌پرسن‌شون. علامت‌های کم‌رنگ کلمه‌هایی‌ان که هنوز توی مه منتظر نگاه اول‌ان.',
  'moment.fogMarkTitle': '{meaning} (هنوز توی مه)',
  'known.count': 'همه‌ی کلمه‌های {n} تا از این {total} خط رو بلدی',
  'known.all': 'همه‌ی کلمه‌های هر {total} خط رو بلدی',
  'known.one': 'همه‌ی کلمه‌های این خط رو بلدی',
  'known.why': 'یه خط وقتی حساب می‌شه که هر کلمه‌اش یا از مه بیرون اومده باشه، یا یه کلمه‌ی کوچیک باشه که ۱۰ بار شنیدی، یا تکه‌ای از عبارتی باشه که بلدی.',
  'known.mark': 'همه‌ی کلمه‌های این خط رو بلدی',
  'known.markNew': 'تازه: حالا همه‌ی کلمه‌های این خط رو بلدی',
  'known.listen': 'این خط‌ها رو گوش کن',
  'moment.noRecording': 'برای این یکی ضبطی نداریم — عبارت‌های پایین همون‌هایی‌ان که دفعه‌ی بعد به کار می‌آن.',
  'moment.words': 'کلمه‌ها',
  'moment.expressions': 'عبارت‌ها',
  'moment.literally': 'تحت‌اللفظی: {text}',
  'moment.mission': '🎯 دفعه‌ی بعد امتحان کن',
  'moment.tried': '✓ امتحانش کردم',
  'moment.understood': '👂 جوابش رو فهمیدم',
  'moment.stuck': '😅 گیر کردم',
  'moment.reflectionPlaceholder': 'چی شد؟ (اختیاری — مثلاً چی بهت گفتن)',
  'moment.saveReflection': 'ذخیره‌ی یادداشت',
  'moment.startNext': 'درس بعدی رو از همین شروع کن',
  'moment.reflectionPrivate': 'فقط برای خودته — این یادداشت‌ها روی نمره و مرور اثری ندارن.',
  'moment.markOne': 'حداقل یکی از «امتحان کردم / فهمیدم / گیر کردم» رو بزن.',
  'moment.savedStuck': 'ذخیره شد — همین یادداشت می‌تونه شروع درس بعدی باشه.',
  'moment.savedNice': 'ذخیره شد — آفرین که رفتی سراغش.',
  'moment.unclear': 'نامفهوم',
  'moment.deleteConfirm': 'کل این لحظه حذف بشه — درسش، پیشرفتت و ضبطش؟',
  'moment.deleteFailed': 'این لحظه پاک نشد — اتصال اینترنتت رو بررسی کن و دوباره امتحان کن.',
  'moment.deleteRefused': 'این لحظه پاک نشد. سرور قبولش نکرد.',
  'moment.listen': 'شنیدن',
  'moment.stopListen': 'توقف',
  'triage.active': 'می‌خوام یاد بگیرم',
  'triage.activeTitle': 'توی مرور بمونه',
  'triage.known': 'بلدم',
  'triage.knownTitle': 'از مرور برداشته بشه',
  'triage.later': 'بمونه برای بعد',
  'triage.laterTitle': 'فعلاً کنار گذاشته بشه',
  'triage.removed': '✕ برداشتن',
  'triage.removedTitle': 'اشتباهه یا اصلاً به زبان مقصد نیست — از یادگیری و درس‌های بعدی برداشته بشه',
  'triage.isRemoved': '✕ از یادگیری برداشته شد.',
  'triage.bringBack': 'برگردونش',
  'triage.bringBackTitle': 'این عبارت رو دوباره به یادگیری برگردون',

  // ---- share with support ----
  'share.title': 'اشتراک با پشتیبانی',
  'share.start': 'اشتراک با پشتیبانی',
  'share.stop': 'قطع اشتراک',
  'share.since': '🔓 از {date} با پشتیبانی به اشتراک گذاشته شده',
  'share.sharedHint': 'صاحب برنامه می‌تونه فقط همین یک لحظه رو، با ضبطش، ببینه — تا وقتی خودت اشتراک رو قطع کنی.',
  'share.offerHint': 'با این کار صاحب برنامه می‌تونه فقط همین یک لحظه رو، با ضبطش، باز کنه تا وقتی اشتراک رو قطع کنی. هیچ چیز دیگه‌ای از حسابت دیده نمی‌شه.',

  // ---- circles (beta, v1 ticket 16) ----
  'home.circle': '👥 دوستان',
  'activity.nav': 'خبرها',
  'activity.navCount': '{actionable} مورد برای پیگیری، {unread} خوانده‌نشده',
  'activity.title': 'فعالیت‌ها',
  'activity.intro': 'درخواست‌های دوستی، لشکرها، گزارش نبرد و غنیمت اینجا می‌مانند تا به آن‌ها رسیدگی کنی. باز کردن فقط یعنی دیده شده؛ درخواست را نمی‌پذیرد، دفاع نمی‌کند و کلمه‌ای را بازسازی نمی‌کند.',
  'activity.empty': 'هنوز به‌روزرسانی اجتماعی‌ای نیست.',
  'activity.return': 'برگشت به خبرها',
  'activity.open': 'باز کردن',
  'activity.view': 'دیدن',
  'activity.unread': 'خوانده‌نشده · هنوز باز نشده و آمادهٔ اقدام',
  'activity.seen': 'دیده‌شده · هنوز آمادهٔ اقدام',
  'activity.resolved': 'پایان‌یافته',
  'activity.withdrawn': 'پس‌گرفته‌شده',
  'activity.expired': 'منقضی‌شده',
  'activity.cancelled': 'لغوشده',
  'activity.indicatorShown': 'نشانگر خبر نمایش داده شد',
  'activity.availableAt': 'در دسترس از {date}',
  'activity.friendRequest': '{name} می‌خواهد با تو دوست شود',
  'activity.army': '{name} لشکر فرستاد — {count} سلاح',
  'activity.report': 'گزارش نبرد با {name} — {band}',
  'activity.spoilsReady': 'غنیمت از {name} — آمادهٔ بازسازی',
  'activity.spoilsWaiting': 'غنیمت از {name} — هنوز آماده نیست',
  'activity.spoilsDone': 'غنیمت از {name}',
  'activity.outcome.accepted': 'درخواست پذیرفته و ذخیره شد',
  'activity.outcome.declined': 'درخواست رد شد',
  'activity.outcome.defended': 'لشکر بعد از دفاع به پایان رسید',
  'activity.outcome.auto_played': 'لشکر خودکار بازی شد',
  'activity.outcome.report_read': 'گزارش خوانده شد',
  'activity.outcome.rebuilt': 'کلمه بازسازی و ذخیره شد',
  'activity.outcome.withdrawn': 'درخواست پس گرفته شد',
  'activity.outcome.expired': 'درخواست منقضی شد',
  'activity.outcome.cancelled': 'لشکر لغو شد',
  'activity.outcome.planted': 'کلمه در شهر کاشته شد',
  'activity.outcome.dropped': 'غنیمت دیگر در دسترس نیست',
  'circle.title': 'دوستان',
  'circle.loading': 'در حال بارگذاری…',
  'circle.introTitle': 'با دوستات یاد بگیر',
  'circle.intro': 'حلقه یعنی حداکثر {max} دوست. شهر و افتخار همدیگه رو می‌بینید و می‌تونید به شهر هم حمله کنید. کلمه‌ها خصوصی می‌مونن، به‌جز استثنای غنیمت هم‌جفت‌زبان که توی جزئیات پایین هست.',
  'circle.start': 'یه حلقه راه بنداز',
  'circle.haveCode': 'کد داری؟',
  'circle.codeLabel': 'کد',
  'circle.join': 'ادامه',
  'circle.consentStart': 'قبل از این‌که حلقه راه بندازی',
  'circle.consentJoin': 'قبل از این‌که عضو بشی',
  'circle.sharingSummary': 'دوست‌ها نام کاربریت، پیشرفت تمرینت و نتیجه‌های نبرد رو می‌بینن.',
  'circle.sharingSpoils': 'وقتی به دوستی حمله می‌کنی که همان جفت‌زبان را یاد می‌گیرد، اگر سلاحی بشکند و واژه برایش تازه باشد، ممکن است واژه، تلفظ و معنی‌اش را ببیند.',
  'circle.sharingPrivate': 'واژه‌های دیگه، عنوان درس‌ها، یادداشت‌ها و صداهای ضبط‌شده‌ت خصوصی می‌مونن.',
  'circle.sharingDetails': 'جزئیات کامل چیزهایی که می‌بینن',
  'circle.seeTitle': 'دوستات این‌ها رو می‌بینن:',
  'circle.seeTown': 'شهرت، همون‌طور که کشیده شده',
  'circle.seeGlory': 'افتخارت',
  'circle.seeDistricts': 'نوع محله‌های شهرت',
  'circle.seeName': 'نام کاربریت',
  'circle.seeRaid': 'توی حمله: اسمت، افتخارت، برای هر محله این‌که چند کلمه از هر جنس داره و چند پیچک، چراغ و خیابون سنگ‌فرش‌شده (هیچ‌وقت اسم یا کلمه‌هاش نه)، جنس هر سلاح و این‌که جلوش گرفته شد یا نه',
  'circle.seeSpoils': 'توی حمله به دوستی که همان جفت‌زبان را یاد می‌گیرد: اگر سلاحی بشکند و آن واژه برای او تازه باشد، ممکن است واژه، تلفظ و معنی‌اش را ببیند (هیچ‌وقت جمله یا لحظه‌ای نه)',
  'circle.neverTitle': 'هیچ‌وقت این‌ها رو نمی‌بینن:',
  'circle.neverWords': 'هیچ کلمهٔ دیگه‌ای از تو، یا معنیش',
  'circle.neverTitles': 'عنوان درس‌ها یا یادداشت‌هات',
  'circle.neverRecordings': 'صداهای ضبط‌شده‌ت',
  'circle.leaveAnyTime': 'هر وقت بخوای می‌تونی از حلقه بیرون بیای و همون لحظه اعمال می‌شه.',
  'circle.agreeStart': 'قبول دارم — حلقه رو راه بنداز',
  'circle.agreeJoin': 'قبول دارم — عضو شو',
  'circle.cancel': 'الان نه',
  'circle.count': '{n} از {max} عضو',
  'circle.keeper': 'گرداننده',
  'circle.you': 'تو',
  'circle.codeTitle': 'کد حلقه‌ت',
  'circle.codeHint': 'لینک رو برای دوستات بفرست، یا کد رو بخون براشون. فقط همین الان نشون داده می‌شه، پس قبل از رفتن از این صفحه بفرستش.',
  'circle.share': 'فرستادن لینک',
  'circle.copyLink': 'کپی لینک',
  'circle.linkCopied': 'لینک کپی شد ✓',
  'circle.copy': 'کپی کد',
  'circle.copied': 'کپی شد ✓',
  'circle.newCode': 'کد تازه بساز',
  'circle.newCodeHint': 'کد قبلی همون لحظه از کار می‌افته.',
  'circle.noCode': 'الان کد فعالی نیست. برای دعوت دوستات یه کد بساز.',
  'circle.askKeeper': 'برای دعوت کسی، کد رو از {name} بگیر.',
  'circle.leave': 'خروج از حلقه',
  'circle.leaveConfirm': 'از حلقه بیرون می‌ری؟ دوستات همون لحظه دیگه شهرت رو نمی‌بینن.',
  'circle.leaveConfirmKeep': 'از حلقه بیرون می‌ری؟ اعضای حلقه همون لحظه دیگه شهرت رو نمی‌بینن، به‌جز اونایی که دوست مستقیمت هم هستن.',
  'circle.leaveYes': 'خروج',
  'circle.leaveNo': 'می‌مونم',
  'circle.left': 'از حلقه بیرون اومدی.',
  // ---- friends by a one-time link (beta) ----
  'friends.add': 'یه دوست اضافه کن',
  'friends.addTitle': 'یه دوست اضافه کن',
  'friends.addHint': 'برای یه دوست یه لینک بفرست. وقتی قبول کنه، دوست می‌شید و می‌تونید به هم حمله کنید.',
  'friends.groupTitle': 'با یه گروه یاد می‌گیری؟',
  'friends.listTitle': 'دوستات',
  'friends.circleMark': 'حلقه',
  'friends.circleTitle': 'حلقه‌ت',
  'friends.consentTitle': 'قبل از این‌که دوست اضافه کنی',
  'friends.agree': 'قبول دارم',
  'friends.dontShow': 'دیگه نشون نده',
  'friends.whatSee': 'دوستا چی می‌بینن',
  'friends.agreeBe': 'قبول دارم — دوست بشیم',
  'friends.make': 'یه لینک بساز',
  'friends.makeHint': 'لینک فقط یک بار، برای یک نفر و تا {days} روز کار می‌کنه.',
  'friends.linkTitle': 'لینک دوستی‌ت',
  'friends.linkHint': 'فقط یک بار، برای یک نفر و تا {days} روز کار می‌کنه. فقط همین الان نشون داده می‌شه، پس قبل از رفتن از این صفحه بفرستش.',
  'friends.linkCodeHint': 'اگه لینک جای اشتباهی باز شد، دوستت می‌تونه این کد رو توی صفحهٔ دوستان خودش بنویسه.',
  'friends.copyByHand': 'این لینک رو خودت کپی کن:',
  'friends.ownCode': 'این کدِ خودته. برای یه دوست بفرستش.',
  'friends.linkGone': 'این لینک دیگه باز نیست. اگه دوستت استفاده‌ش کرده، الان توی فهرستته.',
  'friends.openTitle': 'لینک‌های بازت',
  'friends.openLine': 'لینک {made} ساخته شد، تا {until} بازه',
  'friends.withdraw': 'پس گرفتن',
  'friends.withdrawn': 'لینک پس گرفته شد.',
  'friends.done': 'تموم',
  'friends.wants': '{name} می‌خواد دوستت بشه',
  'friends.bothWays': 'دوطرفه‌ست: هر دوتون همین‌ها رو از هم می‌بینید.',
  'friends.nowFriends': 'تو و {name} الان دوستید.',
  'friends.already': 'تو و {name} از قبل دوستید.',
  'friends.toFriends': 'برو به دوستان',
  'friends.tryAgain': 'دوباره امتحان کن',
  'friends.leaveFresh': 'این لینک فقط همین الان نشون داده می‌شه. بدون فرستادنش بری؟',
  'friends.withdrawLabel': 'پس گرفتن لینکی که {made} ساخته شد',
  'friends.yourName': 'نام کاربری تو: {name}',
  'friends.askTitle': 'با نام کاربری درخواست بده',
  'friends.askHint': 'نام کاربریِ دقیقِ دوستت رو بنویس. هر چی بنویسی «درخواست فرستاده شد» می‌بینی، پس از این‌جا نمی‌شه فهمید کی حساب داره.',
  'friends.askLabel': 'نام کاربریِ اون',
  'friends.send': 'درخواست بفرست',
  'friends.requestSent': 'درخواست فرستاده شد',
  'friends.openTitleBoth': 'لینک‌ها و درخواست‌های بازت',
  'friends.requestLine': 'درخواست برای {name}، تا {until} بازه',
  'friends.withdrawRequestLabel': 'پس گرفتن درخواست برای {name}',
  'friends.requestsTitle': 'درخواست‌های دوستی',
  'friends.accept': 'قبول کن',
  'friends.decline': 'ردش کن',
  'friends.acceptLabel': 'قبول کردن درخواست {name}',
  'friends.declineLabel': 'رد کردن درخواست {name}',
  'friends.wantMany': '{n} نفر می‌خوان دوستت بشن',
  'friends.seeRequests': 'ببین',
  'friends.requestWithdrawn': 'درخواست پس گرفته شد.',
  'friends.circleMarkLabel': 'توی حلقه‌ته',
  'friends.endAnyTime': 'هر وقت بخوای می‌تونی دوستی رو تموم کنی و همون لحظه اعمال می‌شه.',
  'friends.moreFor': 'گزینه‌های بیشتر برای {name}',
  'friends.endConfirm': '{name} همون لحظه دیگه شهرت رو نمی‌بینه.',
  'friends.end': 'تموم کردن دوستی',
  'friends.keep': 'نگهش دار',
  'friends.ended': 'تو و {name} دیگه دوست نیستید.',
  'raid.title': 'حمله',
  'raid.newTitle': 'حمله به {name}',
  'raid.fromTitle': 'حمله‌ی {name}',
  'raid.glory': 'افتخار',
  'raid.raid': 'حمله',
  'raid.waiting': 'یه چیزی منتظرته',
  'raid.block.pending': 'لشکرت همین الان توی راهه.',
  'raid.block.full': 'دم دروازه‌هاش همین الان لشکر زیاده.',
  'raid.block.no-town': 'هنوز کلمه‌ای نداره که ازش دفاع کنه.',
  'raid.block.limit': 'لشکرهای امروزت رو فرستادی. فردا دوباره.',
  'raid.ready.title': 'قبل از حمله',
  'raid.ready.yours': 'تو',
  'raid.ready.ok': 'آماده‌ای',
  'raid.ready.theirOk': 'برای حمله بازه',
  'raid.ready.you.no-words': 'هنوز هیچ کلمه‌ای از تو از مه بیرون نیومده.',
  'raid.ready.you.resting': 'همهٔ کلمه‌هایی که می‌تونی بفرستی بعد از یه سلاح شکسته استراحت می‌کنن.',
  'raid.ready.you.limit': 'لشکرهای امروزت رو فرستادی. فردا دوباره.',
  'raid.ready.you.pending': 'لشکرت همین الان توی راهه.',
  'raid.ready.them.full': 'دم دروازه‌هاش همین الان لشکر زیاده.',
  'raid.ready.them.no-town': 'هنوز کلمه‌ای نداره که ازش دفاع کنه.',
  'raid.ready.act.capture': 'اول یه کلمه تمرین کن',
  'raid.ready.act.army': 'لشکرت رو ببین',
  'raid.ready.act.friends': 'یه دوست دیگه انتخاب کن',
  'raid.preview.badge': 'پیش‌نمایش',
  'raid.preview.note': 'یه نبرد تمرینی با کلمه‌های ساختگی. هیچ دوست، افتخار، امتیاز یا سابقهٔ یادگیری‌ای عوض نمی‌شه.',
  'raid.preview.win.addFriend': 'یه دوست اضافه کن',
  'friends.previewTitle': 'یه نبرد تمرینی امتحان کن',
  'friends.previewHint': 'ببین حمله با کلمه‌های تمرینی چطوری کار می‌کنه. هیچ‌چیزش حساب نمی‌شه و به کسی خبر نمی‌رسه.',
  'raid.atGates': 'دم دروازه‌هات',
  'raid.armyOf': 'لشکر {name}',
  'raid.scoutTitle': 'شناسایی {name}',
  'raid.scoutHint': 'یه محله رو برای هدف‌گیری انتخاب کن. فقط می‌بینی هر کدوم چه وضعی داره، هیچ‌وقت کلمه‌هاش رو نه.',
  'raid.scoutConditionHint': 'این فقط یه نشونه‌ی کلی از آمادگیه، نه پیش‌بینی آسیب. جواب درست موقع دفاع هنوز می‌تونه ضربه رو دفع کنه.',
  'raid.conditionLabel': 'آمادگی',
  'raid.condition.maintained': 'رسیدگی‌شده',
  'raid.condition.mixed': 'آمادگی ترکیبی',
  'raid.condition.neglected': 'نیاز به رسیدگی',
  'raid.condition.unproven': 'هنوز سابقه‌ی کافی نداره',
  'raid.scoutNone': 'هنوز چیزی برای شناسایی نیست: محله‌ای ندارن.',
  'raid.districtN': 'محله‌ی {n}',
  'raid.aimedAt': 'هدف: محله‌ی {n}',
  'raid.aimedAtOwn': 'هدف: محله‌ی خودت، {title}',
  'raid.rescout': 'یه محله‌ی دیگه انتخاب کن',
  'raid.ivy': '{n} تا با پیچک',
  'raid.ivyOne': 'یکی با پیچک',
  'raid.lamps': '{n} چراغ روشن',
  'raid.lampOne': 'یک چراغ روشن',
  'raid.streetsPaved': '{paved} خیابون از {streets} سنگ‌فرش شده',
  'raid.fort.aria': 'قلعه. دیوار سنگی: {paved} از {walls}. برج: {towers}. پیچک: {ivy}.',
  'raid.fort.legend': 'دیوار سنگی: خط‌هایی که همه‌ی کلماتشون رو بلدی. برج: کلمه‌هایی که این هفته تمرین کردی. پیچک: کلمه‌هایی که وقت مرورشون گذشته.',
  'raid.fort.legendTheirs': 'دیوار سنگی: خط‌هایی که همه‌ی کلماتشون رو بلدن. برج: کلمه‌هایی که این هفته تمرین کردن. پیچک: کلمه‌هایی که وقت مرورشون گذشته.',
  'raid.canTake': 'جا برای {n} سلاح داره',
  'raid.canTakeOne': 'جا برای یک سلاح داره',
  'raid.cantTake': 'هنوز کلمه‌ای برای زدن اینجا نیست',
  'raid.limit': '{n} سلاح از حداکثر {max}',
  'raid.limitOne': 'اینجا حداکثر یک سلاح',
  'raid.weapons': '{n} سلاح',
  'raid.weaponsOne': 'یک سلاح',
  'raid.defend': 'دفاع کن',
  'raid.onTheirWay': 'توی راه',
  'raid.draft': 'لشکرت برای {name} هنوز فرستاده نشده.',
  'raid.continue': 'ادامه بده',
  'raid.armyTo': 'به سمت {name}',
  'raid.reports': 'گزارش‌ها',
  'raid.unread': 'تازه',
  'raid.read': 'بخون',
  'raid.home.army': 'لشکر {name} دم دروازه‌هاته: {n} سلاح.',
  'raid.home.armyOne': 'لشکر {name} دم دروازه‌هاته: یک سلاح.',
  'raid.home.reportOn': 'حمله‌ت به {name} تموم شد: گزارشش رسیده.',
  'raid.home.reportFrom': '{name} به شهرت حمله کرد: گزارشش رسیده.',
  'raid.reportOn': 'حمله‌ی تو به {name}',
  'raid.reportFrom': 'حمله‌ی {name} به تو',
  'raid.weapon.choice': 'تیر',
  'raid.weapon.listen': 'اژدها',
  'raid.weapon.dictation': 'طوفان',
  'raid.weapon.gap': 'منجنیق',
  'raid.weapon.recall': 'طلسم',
  'raid.strike.choice': 'تیرهای {material}',
  'raid.strike.listen': 'اژدهای {material}',
  'raid.strike.dictation': 'طوفان {material}',
  'raid.strike.gap': 'منجنیق {material}',
  'raid.strike.recall': 'طلسم {material}',
  'raid.mat.scaffolding': 'داربستی',
  'raid.mat.wood': 'چوبی',
  'raid.mat.stone': 'سنگی',
  'raid.mat.gold': 'طلایی',
  'raid.band.held': 'پابرجا',
  'raid.band.draw': 'مساوی',
  'raid.band.breach': 'رخنه',
  'raid.band.sacked': 'غارت',
  'raid.verdict.blocked': 'دفع شد',
  'raid.verdict.scratched': 'خراش انداخت',
  'raid.verdict.hit': 'خورد',
  'raid.verdict.missed': 'خطا رفت',
  'raid.out.attacker.held': 'شهرشون پابرجا موند.',
  'raid.out.attacker.draw': 'مساوی شد.',
  'raid.out.attacker.breach': 'توی دیوارها رخنه کردی.',
  'raid.out.attacker.sacked': 'شهر رو غارت کردی.',
  'raid.out.defender.held': 'شهرت پابرجا موند.',
  'raid.out.defender.draw': 'مساوی شد.',
  'raid.out.defender.breach': 'توی دیوارهات رخنه شد.',
  'raid.out.defender.sacked': 'شهرت غارت شد.',
  'raid.damageLine': '{damage} از {total} قدرت رد شد.',
  'raid.auto': 'تا یه روز کسی جواب نداد، برای همین سلاح‌های مونده خودشون بازی شدن.',
  'raid.raidBack': 'تو هم حمله کن',
  'raid.raidAgain': 'دوباره حمله کن',
  'raid.defenceBreakdown': 'جواب\u200cهات، جنس خانه\u200cها و چراغ\u200cهای روشن روی هم {prevented} قدرت رو متوقف کردن. هدفی که از دست رفته آسیبی نمی\u200cبینه.',
  'raid.damageCapped': 'آسیبِ کل حداکثر نصف مجموع جون کلمه‌های هدف‌گرفته‌شده است.',
  'gate.resetAt': 'این الزام ساعت {time} به وقت {timezone} از نو شروع می‌شه.',
  'raid.toFriends': 'دوستان',
  'raid.rulesChanged': 'قوانین حمله تغییر کرد، پس این لشکرِ نفرستاده لغو شد. جواب‌های قبلی‌ش به‌عنوان یادگیری ثبت نشدن. یه لشکر جدید آماده کن.',
  'raid.expired': 'مهلت آماده‌سازی این لشکر تموم شد. یادگیری و زمان استراحت کلمه‌هات ذخیره موند؛ یه لشکر تازه آماده کن یا تمرین کن.',
  'raid.gone': 'این حمله لغو شد.',
  'raid.left.head': 'روی میدان جا موند',
  'raid.left.row': 'روی میدان جا موند',
  'raid.spoils.line': '{name} کلمهٔ {word} رو توی {district} روی میدان جا گذاشت. فردا دوباره بسازش تا مال خودت بشه.',
  'raid.spoils.lineBare': '{name} کلمهٔ {word} رو روی میدان جا گذاشت. فردا دوباره بسازش تا مال خودت بشه.',
  'raid.spoils.salvaged': '{word} رو از {name} گرفتی.',
  'raid.spoils.planted': '{word} توی مه شهرت جا موند.',
  'raid.home.spoilsOne': 'یک کلمه برای ساختن',
  'raid.home.spoils': '{n} کلمه برای ساختن',
  'raid.spoils.go': 'بساز',
  'raid.spoils.title': 'کلمه‌های جاماندهٔ روی میدان',
  'raid.spoils.intro': 'سلاح شکستهٔ یه دوست این‌ها رو جا گذاشته. کلمه رو از روی معنیش دوباره بساز تا مال خودت بشه؛ روزی یک بار.',
  'raid.spoils.from': 'جاگذاشتهٔ {name}',
  'raid.spoils.in': 'توی {district}',
  'raid.spoils.undo': 'پاک کن',
  'raid.spoils.check': 'بررسی',
  'raid.spoils.right': 'ساخته شد. حالا مال خودته و فردا نوبت مرورشه.',
  'raid.spoils.wrong': 'این ترتیب نبود. {word} بود. فردا دوباره امتحان کن.',
  'raid.spoils.empty': 'الان چیزی برای ساختن نیست.',
  'raid.spoils.later': '{n} تای دیگه هم بعداً می‌رسه.',
  'raid.spoils.laterOne': 'یکی دیگه هم بعداً می‌رسه.',
  'raid.spoils.done': 'تموم شد',
  'raid.spoils.built': 'کلمه تا اینجا',
  'town.trophy': 'گرفته‌شده از {name}',
  'finale.sacked': 'غارت شد',
  'finale.breach': 'رخنه شد',
  'finale.draw': 'مساوی',
  'finale.held': 'دوام آوردیم',
  'finale.sub.sacked': 'پرچم {name} بر فراز {town} برافراشته شد.',
  'finale.sub.breach': 'پرچم {name} کنار دروازه‌ات برافراشته شد.',
  'finale.sub.draw': 'هیچ‌کدام عقب ننشستید.',
  'finale.sub.held': 'دیوارهات جلوی {name} دوام آوردن.',
  'banner.title': 'پرچم {name}',
  'banner.label': 'پرچم {name}. لمس کن تا ببینی چطور پایین میاد.',
  'banner.sheet': 'پرچم {name} این‌جا بالاست. کلمه‌های ترک‌خورده رو ترمیم کن تا پایین بیاد.',
  'banner.words': 'کلمه‌های ترک‌خورده',
  'banner.repair': 'ترمیم کلمه‌های ترک‌خورده',
  'banner.strike': 'تو هم حمله کن',
  'cracks.bar': 'نوار ترمیم: چقدر از محله سرپاست',
  'cracks.hit': 'آسیب‌دیده',
  'cracks.building': 'توی حمله‌ی {name} آسیب دید. جواب درستِ این کلمه ترمیمش می‌کنه.',
  'cracks.district': 'توی حمله‌ی {names} آسیب دید. جواب درست به کلمه‌هاش ترمیمش می‌کنه، {pct}٪ سرپاست.',
  'end.head.repaired': '{n} ترک ترمیم شد',
  'end.head.repairedOne': '۱ ترک ترمیم شد',
  'end.chip.repaired': 'ترمیم‌شد',
  'error.spoil-not-ready': 'این کلمه هنوز آمادهٔ ساختن نیست.',
  'error.spoil-tried-today': 'امروز این کلمه رو امتحان کردی. فردا دوباره امتحان کن.',
  'raid.settling': 'همه‌ی کارت‌ها جواب داده شدن. گزارش داره نوشته می‌شه.',
  'raid.refresh': 'گزارش رو نشون بده',
  'raid.gates': 'لشکر {name} دم دروازه‌هاته',
  'raid.starter': 'استفاده از ارتش پیشنهادی',
  'raid.starterHint': 'با حداکثر سه واژهٔ آماده و تمرین‌های سبک‌تر شروع کن. قبل از آماده کردن ارتش، هر انتخابی را پایین تغییر بده.',
  'raid.clearSelection': 'پاک کردن انتخاب‌ها',
  'raid.formLabel.choice': 'انتخاب معنی',
  'raid.formLabel.listen': 'گوش دادن و انتخاب',
  'raid.formLabel.gap': 'پر کردن جای خالی',
  'raid.formLabel.recall': 'نوشتن واژه',
  'raid.formLabel.dictation': 'گوش دادن و نوشتن',
  'raid.defHint': 'با کلمه‌ی خودت جواب بده تا جلوش رو بگیری.',
  'raid.landsAs.choice': 'به شکل تیر می‌رسه: این کلمه هنوز تازه‌ست.',
  'raid.landsAs.listen': 'به شکل اژدها می‌رسه: این کلمه هنوز تازه‌ست.',
  'raid.landsAs.recall': 'به شکل طلسم می‌رسه: این کلمه هنوز تازه‌ست.',
  'raid.landsAs.gap': 'به شکل منجنیق می‌رسه: این کلمه هنوز تازه‌ست.',
  'raid.def.blocked': 'دفعش کردی. آسیبی نرسید.',
  'raid.def.scratched': 'خراش انداخت: {damage} آسیب رد شد.',
  'raid.def.hit': 'خورد: {damage} آسیب.',
  'raid.def.noDamage': 'رد شد، ولی آسیبی نزد.',
  'raid.learningSaved': 'جوابت به‌عنوان تمرین ذخیره شد.',
  'raid.recoverySaved': 'جوابت ذخیره شد. تمرین جبرانی آسون‌تر منتظرته.',
  'raid.toReport': 'گزارش رو ببین',
  'raid.battle.walls': 'دیوار {n}',
  'raid.battle.gaps': 'شکاف {n}',
  'raid.battle.towers': 'برج دیده‌بانی {n}',
  'raid.battle.raising': 'دیوارهات دارن بالا می‌رن…',
  'raid.chip.blocked': 'دفع شد',
  'raid.chip.scratched': 'خراش −{damage}',
  'raid.chip.hit': 'خورد −{damage}',
  'raid.wasIt': 'این بود:',
  'raid.absorbed': 'دیوارها بیشترش را گرفتند',
  'raid.wallsTook': 'دیوارهای شما بقیه را گرفتند.',
  'raid.nextWeapon': 'سلاح بعدی',
  'raid.missedOne': 'یک سلاح خطا رفت: چیزی برای جواب دادن نداره.',
  'raid.missedNote': '{n} سلاح خطا رفتن: چیزی برای جواب دادن ندارن.',
  'raid.kicker': 'سلاح {n} از {total}',
  'raid.loadHint': 'جواب بده تا این سلاح پر بشه.',
  'raid.loaded': 'پر شد.',
  'raid.loadedScratch': 'پر شد، با یه غلط املایی کوچیک.',
  'raid.loadedScratchPractice': 'قدرت کامل حساب شد. کارت املای این کلمه توی تمرین منتظرته.',
  'raid.broke': 'شکست و توی خونه می‌مونه.',
  'raid.recoveryPractice': 'تمرین جبرانی آسون‌تر',
  'raid.practise': 'تمرین کن',
  'raid.expiry': 'این آماده‌سازی حداکثر تا {hours} ساعت بعد از اولین جواب موفق، توی {time} تموم می‌شه.',
  'raid.newPreparation': 'آماده‌کردن لشکر تازه',
  'raid.spellingPractice': 'رفتن به تمرین املا',
  'raid.toSummary': 'لشکر رو ببین',
  'raid.summaryTitle': 'لشکرت برای {name}',
  'raid.noneLoaded': 'هیچ سلاحی پر نشد، پس لشکری برای فرستادن نیست.',
  'raid.summaryOne': 'تنها سلاحت پر شده.',
  'raid.summaryCount': '{n} از {total} سلاح پر شدن.',
  'raid.row.loaded': 'پر',
  'raid.row.broken': 'شکسته',
  'raid.send': 'لشکر رو بفرست',
  'raid.rebuild': 'یه لشکر دیگه بساز',
  'raid.edit': 'لشکر رو عوض کن',
  'raid.editNote': 'سلاح‌هایی که قبلاً پر کردی، پر می‌مونن.',
  'watch.marches': 'لشکرت به سمت شهر {name} می‌تازه',
  'watch.lands': 'وقتی دفاع کنه می‌رسه، یا حدود {n} ساعت دیگه خودش بازی می‌شه.',
  'watch.landsOne': 'وقتی دفاع کنه می‌رسه، یا حدود یک ساعت دیگه خودش بازی می‌شه.',
  'watch.landsSoon': 'وقتی دفاع کنه می‌رسه، یا همین الان خودش بازی می‌شه.',
  'watch.watch': 'تماشای نبرد',
  'watch.skip': 'برای رد شدن بزن',
  'watch.auto': 'نیومدن: خودش بازی شد.',
  'watch.sacked': 'شهر {name} رو غارت کردی!',
  'watch.breach': 'رخنه کردی!',
  'watch.draw': 'مساوی',
  'watch.held': 'دیوارهاشون پابرجا موند',
  'watch.glory': '{n} افتخار گرفتی',
  'watch.noGlory': 'این بار افتخاری نبود.',
  'watch.standin': 'شهرشون مال خودشونه: این قلعه یه نمونه‌ی ساختگیه که فقط از شمارش‌های گزارش کشیده شده.',
  'raid.onWay': 'لشکرت توی راهه به سمت {name}.',
  'raid.sentLineOne': 'یک سلاح، با قدرت {power}.',
  'raid.sentLine': '{weapons} سلاح، با قدرت {power}.',
  'raid.waitNote': 'یه روز وقت داره که دفاع کنه. گزارش همین‌جا برمی‌گرده.',
  'raid.allGoldTitle': 'کلمه‌های مسلط‌شده‌ات از شهرت محافظت می‌کنن',
  'raid.allGoldHint': 'کلمه‌های طلایی از حمله فارغ‌التحصیل شدن. با نگاه‌های اول یا یه لحظه‌ی جدید، سلاح‌های تازه یاد بگیر یا یه نبرد تمرینی رو امتحان کن. هنوز می‌تونی با کلمه‌های خودت جلوی لشکرهای ورودی دفاع کنی.',
  'raid.allGoldLearn': 'نگاه‌های اول و لحظه‌های جدید',
  'raid.allGoldDefend': 'دوست‌ها و لشکرهای ورودی',
  'raid.potentialSpentTitle': 'توان اولیه‌ی حمله مصرف شده',
  'raid.potentialSpentHint': 'این توانِ مصرف‌شده‌ی حمله‌ست، نه تسلط طلایی. این کلمه‌ها رو یاد بگیر یا کلمه‌های تازه‌ای برای سلاح‌های جدید پیدا کن.',
  'raid.potentialSpentOne': 'مرحله‌ی توان اولیه‌ی مصرف‌شده: {n} از {total}',
  'raid.potentialSpentMany': 'مرحله‌های توان اولیه‌ی مصرف‌شده: {n} از {total}',
  'raid.emptyTitle': 'هنوز سلاحی برای حمله آماده نیست',
  'raid.emptyHint': 'با یه نگاه اول یا اضافه کردن یه لحظه‌ی تازه، سلاحی از یادگرفته‌هات آماده کن. تا اون موقع می‌تونی یه نبرد تمرینی داشته باشی یا جلوی لشکر ورودی از شهرت دفاع کنی.',
  'raid.emptyPractice': 'نبرد تمرینی',
  'raid.loadingRestUntil': 'با موفقیت بارگذاری شد. از {time} دوباره آماده‌ست؛ تا اون موقع این کلمه رو تمرین کن.',
  'raid.challengeHint': 'با چالش پیشنهادی شروع کن یا یه چالش سخت‌تر انتخاب کن. فقط چالش‌هایی رو می‌بینی که با مرحله‌ی یادگیریت جورن.',
  'raid.recommended': 'پیشنهادی',
  'raid.optionalHarder': 'چالش اختیاری',
  'raid.context.familiar': 'جمله‌ی آشنا',
  'raid.context.meaning': 'از روی معنی',
  'raid.context.new': 'موقعیت تازه',
  'raid.context.audio': 'با شنیدن',
  'raid.preparationPreview': 'می‌تونی این لشکر رو بررسی کنی. وقتی قوانین جدید نبرد آماده بشن، فرستادنش هم در دسترس می‌شه.',
  'error.raid-preview-only': 'فرستادن این لشکر هنوز در دسترس نیست. هنوز می‌تونی جلوی لشکرهای ورودی دفاع کنی.',
  'error.raid-recovery-pending': 'قبل از بارگذاری دوباره، تمرین جبرانی این کلمه رو انجام بده.',
  'error.raid-proof-pending': 'جوابت ذخیره شد، ولی این سلاح هنوز بارگذاری نشده. همین کارت رو دوباره جواب بده تا بارگذاریش تموم بشه.',
  'error.raid-defence-pending': 'جوابت ذخیره شد، ولی دفاع هنوز ثبت نشده. همین کارت رو دوباره جواب بده تا ثبت بشه.',
  'error.raid-rebuild-pending': 'جوابت ذخیره شد، ولی این کلمه هنوز بازسازی نشده. دوباره امتحان کن تا تموم بشه.',
  'error.raid-rebuild-failed': 'این کلمه الان بازسازی نشد و چیزی ازش ذخیره نشد. یه کم بعد دوباره امتحان کن.',
  'error.raid-preparation-expired': 'مهلت آماده‌سازی این لشکر تموم شد. یادگیری و زمان استراحت کلمه‌هات ذخیره موند؛ یه لشکر تازه آماده کن یا تمرین کن.',
  'raid.noWords': 'هنوز هیچ کلمه‌ای از مه بیرون نیومده. اول توی شهرت یه نگاه اول بنداز، بعد برگرد.',
  'raid.weight': 'وزن {n} از {budget}',
  'raid.power': 'قدرت {n}',
  'raid.muster.power': 'قدرت',
  'raid.muster.full': 'قدرت کامل',
  'raid.muster.potential': 'از {n} در قدرت کامل',
  'raid.muster.aria': 'قدرت لشکر {n} از {total}',
  'raid.meter.label': 'قدرت لشکر',
  'raid.meter.of': 'از {n}',
  'raid.fill.left': '{n} واحد وزن دیگه جا داره',
  'raid.fill.full': 'لشکر پره',
  'raid.fill.none': 'کلمه‌ی دیگه‌ای برای اضافه کردن نیست',
  'raid.fill.aria': 'وزن {n} از {budget} پر شده، {left} مونده',
  'raid.armouryHint': 'برای هر کلمه یه سلاح بردار: 🏹 تیر (معنی رو انتخاب کن)، 🐉 اژدها (بشنو و معنی رو انتخاب کن)، 🪨 منجنیق (جای خالی رو پر کن)، 🔮 طلسم (خودت تایپش کن)، 🌪 طوفان (بشنو و تایپش کن). روی هر کدوم اول وزنش نوشته شده، بعد قدرتش.',
  'raid.pick': 'سلاح‌هات رو انتخاب کن',
  'raid.loadOne': 'یک سلاح رو پر کن',
  'raid.load': '{n} سلاح رو پر کن',
  'raid.shaky': 'لق',
  'raid.shakyHint': 'وقت مرورشه: ممکنه یادت نیاد.',
  'raid.restingUntil': 'از {time} دوباره آماده است. تا آن زمان، واژهٔ دیگری انتخاب کن.',
  'raid.resting': 'بعد از جواب اشتباه در حال استراحت است. واژهٔ آمادهٔ دیگری انتخاب کن.',
  'raid.chipAria': '{weapon}: وزن {weight}، قدرت {power}',
  'raid.tour.title': 'حمله چطوری کار می‌کنه',
  'raid.tour.skip': 'رد شو',
  'raid.tour.step': '{n} از {of}',
  'raid.tour.practice': 'کلمه‌ی تمرینی',
  'raid.tour.next': 'بعدی',
  'raid.tour.duel.title': 'حمله یه دوئل سؤال‌وجوابه.',
  'raid.tour.duel.body': 'تو و یه دوست برای هم سؤال‌هایی درباره‌ی کلمه‌ها می‌فرستین. برای هر دوتون تمرینه.',
  'raid.tour.weapons.title': 'سلاحت همون کلمه‌هاییه که بلدی.',
  'raid.tour.weapons.body': 'یه کلمه انتخاب کن، بعد با جواب دادن به سؤالش ثابت کن که بلدیش. درست: سلاحت پر می‌شه. غلط: می‌شکنه و یه روز استراحت می‌کنه، پس یه کلمهٔ دیگه بردار.',
  'raid.tour.weapons.tap': 'روی 🏹 بزن تا پرش کنی.',
  'raid.tour.weapons.try': 'با یه کلمه‌ی تمرینی امتحانش کن.',
  'raid.tour.weapons.loaded': 'پر شد! هرچی یه کلمه رو بهتر بلد باشی (چوب، سنگ، طلا)، محکم‌تر می‌زنه.',
  'raid.tour.weapons.broke': 'سلاحت شکست. حمله این‌جوری مطمئن می‌شه که فقط کلمه‌هایی رو می‌فرستی که واقعاً بلدی. دوباره امتحان کن.',
  'raid.tour.weapons.again': 'دوباره امتحان کن',
  'raid.tour.weapons.more': 'سلاح‌های دیگه ازت می‌خوان گوش کنی، جای خالی یه جمله رو پر کنی یا کلمه رو تایپ کنی. هرچی سخت‌تر، قوی‌تر.',
  'raid.tour.defend.title': 'وقتی یه لشکر بیاد، دفاع می‌کنی.',
  'raid.tour.defend.body': 'هر سلاح یکی از کلمه‌های خودت رو نشونه می‌گیره. جواب بده تا جلوش رو بگیری.',
  'raid.tour.defend.blocked': 'جلوش رو گرفتی! برج‌های روشن و خیابون‌های سنگ‌فرش، زدن کلمه‌هات رو سخت‌تر می‌کنن.',
  'raid.tour.defend.hit': 'رد شد و یه خونه ترک برداشت. بعداً که به اون کلمه جواب بدی، تعمیر می‌شه.',
  'raid.tour.defend.either': 'در هر حال، همین الان کلمه‌ی خودت رو تمرین کردی.',
  'raid.help.defend.open': 'راهنمای دفاع',
  'raid.help.defend.title': 'چطور از شهرت دفاع کنی',
  'raid.help.defend.body': 'هر سلاح یکی از کلمه‌های خودت رو نشونه می‌گیره. کارت رو جواب بده تا جلوش رو بگیری؛ این‌طوری همون کلمه رو هم تمرین می‌کنی.',
  'raid.help.defend.detail': 'این راهنما همین لشکری رو که باز کردی نگه می‌داره. وقتی برگردی، اگه هنوز منتظر باشه دفاعش رو می‌بینی، اگه تموم شده باشه گزارشش رو و اگه پس گرفته شده باشه وضعیتش رو.',
  'raid.help.defend.return': 'برگشت به همین دفاع',
  'raid.tour.win.title': 'کی می‌بره؟ هر کی بیشتر یاد گرفته باشه.',
  'raid.tour.win.stronger': 'کلمه‌های بیشتری بلد باشی ← لشکرهای قوی‌تری می‌فرستی.',
  'raid.tour.win.walls': 'کلمه‌هات رو تمرین کن ← برج‌های روشن و خیابون‌های سنگ‌فرش دیوارهات رو محکم می‌کنن.',
  'raid.tour.win.calm': 'حمله هیچ‌وقت کلمه‌ای رو ازت نمی‌گیره، و لشکری که جوابش رو ندی بعد از یه روز خودش بازی می‌شه، پس عجله‌ای نیست.',
  'raid.tour.win.continue': 'ادامه بده',
  'raid.tour.win.pick': 'یه دوست رو برای حمله انتخاب کن',
  'raid.tour.win.learnFirst': 'اول یه کلمه یاد بگیر. هر کلمه بعد از اولین نگاهت بهش، سلاح می‌شه.',
  'raid.tour.win.practise': 'برو تمرین کن',
  'raid.tour.win.replay': 'راهنما رو دوباره ببین',
  'raid.answerIs': 'جواب:',
  'raid.ask.choice': 'یعنی چی؟',
  'raid.ask.listen': 'گوش کن، بعد معنیش رو انتخاب کن.',
  'raid.ask.dictation': 'گوش کن، بعد همون رو که شنیدی تایپ کن.',
  'raid.ask.gap': 'کلمه‌ی جاافتاده رو تایپ کن.',
  'raid.ask.recall': 'از روی معنیش تایپش کن.',
  'raid.typeAria': 'جواب تو',
  'raid.typeHere': 'این‌جا تایپ کن',
  'raid.check': 'بررسی',

  // ---- original recording retention (ticket 13) ----
  'audio.expiresOn': 'ضبط اصلی {date} خودبه‌خود پاک می‌شه.',
  'audio.delete': 'پاک کردن ضبط',
  'audio.deleteConfirm': 'فقط ضبط اصلی پاک بشه؟ متن، تمرین‌ها و پیشرفتت سر جاشون می‌مونن.',
  'audio.deleted': 'ضبط اصلی {date} پاک شد.',
  'audio.expired': 'ضبط اصلی {date} منقضی شد — ضبط‌ها فقط ۳۰ روز نگه داشته می‌شن.',
  'drafts.hintAge': 'پیش‌نویس‌هایی که از ۳۰ روز قدیمی‌ترن، دفعه‌ی بعد که برنامه رو باز کنی پاک می‌شن.',
  'drafts.expiredOne': '۱ پیش‌نویسِ قدیمی‌تر از ۳۰ روز از این دستگاه پاک شد.',
  'drafts.expiredMany': '{n} پیش‌نویسِ قدیمی‌تر از ۳۰ روز از این دستگاه پاک شدن.',

  // ---- practice / review / exam ----
  'lesson.play': '🔊 پخش',
  'lesson.stopPlay': '⏹ توقف',
  'lesson.loadingCard': 'در حال آوردن کارت…',
  'lesson.sayCheck': '🎙 بگو و بررسی کن',
  'lesson.stopCheck': '⏹ توقف و بررسی',
  'lesson.scoring': 'در حال بررسی…',
  'lesson.pronPass': '✓ گرفتمش — خوب بود.',
  'lesson.pronFail': 'کامل نشد: {tip}',
  'lesson.pronErrorTail': '{err} — یا روی کارت «گفتمش» رو بزن.',
  'lesson.scoringFailed': 'بررسی انجام نشد',
  'lesson.saveFailed': 'جوابت ذخیره نشد — اتصال اینترنتت رو بررسی کن و دوباره امتحان کن.',
  'lesson.saveFailedMany': '{n} تا از جواب‌هات ذخیره نشد — اتصال اینترنتت رو بررسی کن و دوباره امتحان کن.',
  'lesson.saving': 'در حال ذخیره…',
  'lesson.saveRefused': 'این جواب ذخیره نشد. سرور قبولش نکرد.',
  'lesson.saveRefusedMany': '{n} تا از جواب‌هات ذخیره نشد. سرور قبولشون نکرد.',
  'lesson.leaveUnsaved': 'بعضی از جواب‌هات هنوز ذخیره نشدن و از دست می‌رن. با این حال می‌خوای بری؟',
  'lesson.skip': 'رد کن',
  'lesson.next': 'بعدی',
  'lesson.finish': 'تمام',
  'lesson.whySkip': 'چرا رد می‌کنی؟',
  'lesson.wrongCard': '✕ کارت اشتباهه — برای همیشه بردارش',
  'lesson.removeAction': 'برداشتن از یادگیری…',
  'lesson.undoRemoval': 'برگردوندن کارت',
  'lesson.undoFailed': 'برگردوندن کارت انجام نشد ({err}).',
  'lesson.undoConflict': 'وضعیت این مورد بعد از برداشتنش عوض شده. فرصت برگرداندن تمام شده و انتخاب فعلی حفظ شده.',
  'lesson.statusConflict': 'وضعیت این مورد جای دیگری عوض شده، برای همین این بار از یادگیری برداشته نشد. تمرین، مرور یا رسیدگی به شهر را دوباره باز کن تا وضعیت فعلی‌اش را ببینی.',
  'lesson.triageConflict': 'وضعیت این مورد جای دیگری عوض شده و این انتخاب اعمال نشد. قبل از انتخاب دوباره، لحظه را از نو باز کن.',
  'lesson.removeConfirm': '«{label}» از یادگیری برداشته بشه؟ از مرور و درس‌های بعدی می‌ره. تا وقتی این فعالیت بازه می‌تونی برش گردونی. در تمرین، زدن «یک بار دیگه» این فرصت رو می‌بنده؛ در مرور یا رسیدگی به شهر، جواب دادن یا رد کردن کارت بعدی فرصت برگردوندن رو می‌بنده.',
  'lesson.removeFailed': 'این کارت برداشته نشد ({err}) — فعلاً برای یادگیری می‌مونه.',
  'lesson.cardError': 'کارت {n} نمایش داده نشد.',
  'lesson.worthLook': 'ارزش یک نگاه دوباره رو دارن',
  'lesson.worthLookHint': '— همین که الان می‌بینی‌شون بخشی از یادگیریه',
  'lesson.flagged': '⚑ «{given}» رو علامت زدی — برای بهتر شدن نگه داشتیم، امتیازی کم نمی‌شه',
  'skip.defer': 'الان نمی‌تونم حرف بزنم',
  'skip.unclear': 'سؤال نامفهومه',

  'practice.stopEarly': "پایان این نوبت",
  'practice.endedEarly': "تمرین زودتر متوقف شد",
  'practice.endedEarlySub': "به {visited} کارت از {total} کارت رسیدی.",
  'practice.sessionSaved': "فعالیت ذخیره‌شده: {saved} کارت از {total} کارت؛ {revealed} نمایش جواب؛ {hinted} با راهنمایی؛ {skipped} رد کردن؛ {selfReported} خودارزیابی.",
  'practice.sessionStudy': "مطالعه: {visited} کارت از {total} کارت دیده شد؛ دیدن کارت‌ها جواب ذخیره نمی‌کند.",
  'practice.doneMixedSub': "در حالت‌های مطالعه و آزمون به پایان {n} کارت رسیدی.",
  'practice.progressUnavailable': "پیشرفت ذخیره‌شده الان تازه نشد. شمارش اینجا فقط جواب‌هایی را شامل می‌شود که سرور تأیید کرده است.",
  'practice.title': 'تمرین',
  'practice.newPill': 'تازه',
  'practice.newPillTitle': 'همین حالا برای این لحظه ساخته شد',
  'practice.loadError': 'تمرین باز نشد.',
  'practice.noTasks': 'این لحظه هنوز تمرینی نداره.',
  'practice.study': 'مطالعه‌ی آزاد',
  'practice.test': 'تست',
  'practice.frame': 'داری کلمه‌های همین لحظه رو تمرین می‌کنی. اگه زود بری، جواب‌های ذخیره‌شده می‌مونن و تمرین تموم‌شده حساب نمی‌شه.',
  'practice.studyHint': 'توی مطالعه‌ی آزاد جواب‌ها رو راحت می‌بینی — چیزی ثبت نمی‌شه.',
  'practice.testHint': 'هر کارت رو جواب بده تا بری سراغ بعدی — همه‌ی جواب‌هات توی پیشرفتت حساب می‌شن.',
  'practice.testIntro': 'توی «تست» هر جوابت توی پیشرفتت ثبت می‌شه و مرورهای بعدیت از روی همین برنامه‌ریزی می‌شن؛ «مطالعه‌ی آزاد» برای اینه که کارت‌ها و جواب‌ها رو بدون ثبت شدن ببینی.',
  'practice.listenHint': 'گوش کن (صدای تمیز مدل) — بدون متن، از روی شنیدن جواب بده. ضبط اصلی توی صفحه‌ی لحظه هست.',
  'practice.forgedNow': '⚒ آب‌دیده شد: {list}',
  'practice.doneTitle': "این نوبت تمرین تمام شد",
  'practice.doneSub': '({n} تمرین، حالت {mode}).',
  'practice.progress': "فعالیت ذخیره‌شده تا حالا: {saved} کارت ({tested} پاسخ امتیازدار)؛ {forged} واژه از این لحظه آب‌دیده شده است.",
  'practice.loadingProgress': 'در حال آوردن پیشرفت…',
  'practice.forgedRun': 'توی این دور آب‌دیده شد: {list}',
  'practice.reflect': 'توی صفحه‌ی لحظه یادداشت بگذار',
  'practice.moment': 'لحظه',
  'practice.returnMoments': 'لحظه‌ها',
  'practice.again': 'یک بار دیگه',

  'review.title': 'مرور',
  'review.titleDue': 'مرور ({n} تا رسیده)',
  'review.loadError': 'صف مرور باز نشد.',
  'review.nothingDue': 'چیزی برای مرور نیست.',
  'review.nothingDueHint': 'کلمه‌های تازه‌ی لحظه‌هات روزی چندتا وارد مرورت می‌شن؛ فردا دوباره سر بزن.',
  'review.cardOf': 'کارت {i} از {n} — {seen}، {right}',
  'review.cardOfNew': 'کارت {i} از {n} — بار اول',
  'review.seenOnce': 'یک بار دیدی',
  'review.seenTwice': 'دو بار دیدی',
  'review.seenMany': '{n} بار دیدی',
  'review.rightNone': 'هنوز درست نزدی',
  'review.rightOnce': 'یک بار درست زدی',
  'review.rightTwice': 'دو بار درست زدی',
  'review.rightMany': '{n} بار درست زدی',
  'review.listenHint': 'گوش کن (صدای تمیز مدل) — بدون متن، از روی شنیدن جواب بده.',
  'review.complete': 'مرور تمام شد',
  // Ticket 06: each review card named by what it asks (its ladder step), never by box.
  'review.card.firstLook': 'نگاه اول',
  'review.card.rebuild': 'دوباره بسازش',
  'review.card.byEar': 'با گوش پیداش کن',
  'review.card.soundAlike': 'فرقش رو بشنو',
  'review.card.recall': 'به یادش بیار',
  'review.card.sayIt': 'از روی معنی بگوش',
  'review.card.somewhereNew': 'یه جای تازه به کارش ببر',
  'review.card.dictation': 'چیزی رو که می‌شنوی بنویس',
  'review.card.spelling': 'درست بنویسش',
  // Ticket 04: the easier card right after a miss (one step down, the same word), never by box.
  'retreat.card': 'یه قدم آسون‌تر',
  'retreat.hint': 'این کلمه رو اشتباه زدی، برای همین این‌جا با یه کارت آسون‌تر اومده. درست بزنی سر جاش می‌مونه؛ اشتباه بزنی یه کم عقب می‌ره. در هر حال فردا دوباره میاد.',

  'exam.title': 'آزمون موقعیتی',
  'exam.checkingReadiness': 'دارم آماده بودن آزمون رو بررسی می‌کنم…',
  'exam.readinessUnavailable': 'شرایط فعلی آزمون بررسی نشد. قبل از شروع دوباره امتحان کن.',
  'exam.readinessChanged': 'آزمون شروع نشد. وضعیت فعلی با بررسی قبلی فرق داره.',
  'exam.buildError': 'آزمون ساخته نشد.',
  'exam.intro': 'یک داستان کوتاه با کلمه‌هایی که باهاشون آشنا شدی، اول لرزون‌ترهاشون — بعد سؤال‌هایی درباره‌شون: معنی، املا، و مأموریت‌هایی توی خود داستان (یک جمله بشنو، جواب درست رو انتخاب کن، یک عبارت رو به یاد بیار). جواب‌هات مثل هر تست دیگه‌ای حساب می‌شن.',
  'exam.writing': 'دارم داستانت رو می‌نویسم…',
  'wait.voice': 'دارم صدا رو آماده می‌کنم…',
  'wait.practice.words': 'دارم از کلمه‌هات سؤال می‌نویسم…',
  'wait.practice.lines': 'دارم می‌ذارمشون توی جمله‌هایی که گفتن…',
  'wait.newPack': 'دارم یه دسته سؤال تازه می‌نویسم…',
  'wait.exam.words': 'دارم کلمه‌هایی رو که بیشتر لازم داری انتخاب می‌کنم…',
  'wait.exam.story': 'دارم یه داستان کوتاه دورشون می‌نویسم…',
  'wait.exam.questions': 'دارم سؤال‌ها رو لای داستان می‌بافم…',
  'wait.exam.long': 'نوشتن یه داستان ممکنه نیم دقیقه طول بکشه.',
  'wait.pron': 'دارم گوش می‌دم ببینم چطور گفتیش…',
  'exam.start': 'شروع آزمون',
  'exam.sceneTitle': 'یک صحنه برای خواندن',
  'exam.startQuestions': 'شروع سؤال‌ها ({n})',
  'exam.question': 'سؤال {i} از {n}',
  'exam.loading': 'در حال آوردن…',
  'exam.seeScore': 'دیدن نتیجه',
  'exam.easierIntro': 'قبل از نتیجه: برای هر کلمه‌ای که اشتباه زدی یه کارت آسون‌تر. این‌ها توی نتیجه حساب نمی‌شن.',
  'exam.easierCount': 'کارت آسون‌تر {i} از {n}',
  'exam.score': '{correct} از {n} درست ({pct}٪)',
  'exam.passed': 'قبول شدی!',
  'exam.passMark': 'حد قبولی ۸۰٪ه.',
  'exam.missedNote': 'کلمه‌هایی که اشتباه زدی به‌زودی توی مرور برمی‌گردن.',
  'exam.newExam': 'آزمون تازه',
  'exam.questionError': 'سؤال {n} نمایش داده نشد.',
  'exam.pastTitle': 'آزمون‌های قبلی',
  'exam.pastNone': 'هنوز هیچ آزمونی رو تموم نکردی.',
  'exam.pastLine': '{date}: {score} از {total} ({pct}٪)',
  'exam.pastWork': 'برای کار بیشتر:',
  'exam.pastError': 'آزمون‌های قبلیت نیومدن.',

  // ---- feedback ----
  'feedback.pickFirst': 'اول سختی یا مفید بودنش رو مشخص کن.',
  'feedback.saved': 'ذخیره شد — درس بعدی با همین تنظیم می‌شه.',
  'feedback.title': 'این درس چطور بود؟',
  'feedback.sub': '— درس بعدی رو تنظیم می‌کنه',
  'diff.too_easy': 'خیلی آسون',
  'diff.just_right': 'درست اندازه',
  'diff.too_hard': 'خیلی سخت',
  'useful.5': '👍 مفید بود',
  'useful.3': '😐 بد نبود',
  'useful.1': '👎 به درد نخورد',
  'feedback.notePlaceholder': 'چیزی هست که بهتر بشه؟ (اختیاری)',
  'feedback.savedBtn': '✓ ذخیره شد',
  'feedback.save': 'ثبت نظر',

  // ---- exercise cards ----
  'ex.flashcard': 'فلش‌کارت',
  'ex.tapReveal': 'بزن تا رو کنی',
  'ex.gradeSelf': 'بلد بودی؟ خودت به خودت نمره بده',
  'ex.tapFront': 'بزن تا دوباره روی کارت رو ببینی',
  'ex.missed': '✗ بلد نبودم',
  'ex.gotIt': '✓ بلد بودم',
  'ex.gapfill': 'جای خالی رو پر کن · اول خودت امتحان کن، راهنمایی مجانیه',
  'ex.startsWith': 'با «{s}» شروع می‌شه',
  'ex.typeWord': 'کلمه‌ی جاافتاده رو بنویس',
  'ex.check': 'بررسی',
  'ex.hint': '💡 راهنمایی',
  'ex.hintN': '💡 راهنمایی ({n})',
  'ex.showMe': 'نشونم بده',
  'ex.dontKnow': 'نمی‌دونم — نشونم بده',
  'ex.answerIs': 'جواب درست:',
  'ex.expectedAnswer': 'جواب درست',
  'ex.leaveOut': 'این رو حذف کن',
  'ex.wordOrderChanged': 'کلمات یکیه، ترتیبشون فرق داره.',
  'ex.correctIs': 'درسته — {answer}',
  'ex.notQuiteHint': 'نشد — دوباره امتحان کن یا از راهنمایی بالا کمک بگیر',
  'ex.scratch': 'قبول شد، با یه خراش — حواست به جای علامت‌خورده باشه:',
  'ex.spelling': 'املا · حواست به {spikes} باشه',
  'ex.spellingMissed': 'نزدیک بود — حواست به جای علامت‌خورده باشه:',
  'spike.accent': 'علامت تکیه',
  'spike.diaeresis': 'دو نقطه‌ی روی حرف',
  'spike.cedilla': 'سدی (ç)',
  'spike.apostrophe': 'آپاستروف',
  'spike.hyphen': 'خط تیره',
  'spike.middot': 'نقطه‌ی وسط (l·l)',
  'spike.ligature': 'حروف چسبیده (œ، æ)',
  'ex.shouldCount': '⚑ جواب من هم درست بود',
  'ex.flagged': '⚑ علامت خورد — برای بهتر شدن نگهش داشتیم، روی امتیازت اثری نداره.',
  'ex.choose': 'انتخاب کن',
  'ex.correct': 'درسته',
  'ex.answerWas': 'جواب درست: {a}',
  'ex.spotit': 'پیداش کن · این کلمه رو توی جمله پیدا کن',
  'ex.spotitFound': 'پیداش کردی.',
  'ex.spotitMissed': 'این نبود — این‌جاست، توی جمله علامت خورده.',
  'ex.dictation': 'دیکته · پخشش کن، بعد چیزی رو که می‌شنوی بنویس',
  'ex.typeHeard': 'چیزی رو که می‌شنوی بنویس',
  'ex.reorder': 'کلمه‌ها رو مرتب کن',
  'ex.notQuiteReorder': 'نشد — روی کلمه‌ها بزن تا برگردن',
  'ex.sayit': 'بلند بگو',
  'ex.isaidit': 'گفتمش',
  'ex.listening': 'گوش می‌دم…',
  'ex.soundalike': 'پخشش کن، بعد کلمه‌ای رو که شنیدی انتخاب کن',
  'ex.sayfrommeaning': 'کلمه‌ی این معنی رو بلند بگو',
  'review.sayItOff': 'الان بررسی تلفظ ممکن نیست — همین کارت رو یه جور دیگه ازت می‌پرسیم.',

  // ---- server errors ----
  'error.generic': 'مشکلی پیش اومد. لطفاً دوباره امتحان کن.',
  'error.server': 'سرور به مشکل خورد. لطفاً کمی بعد دوباره امتحان کن.',
  'error.offline': 'به سرور نرسیدیم — اتصال اینترنتت رو بررسی کن و دوباره امتحان کن.',
  'error.invitation-not-found': 'این لینک دعوت معتبر نیست. یه لینک تازه بخواه.',
  'error.invitation-used': 'از این دعوت‌نامه قبلاً استفاده شده. وارد شو، یا یه دعوت تازه بخواه.',
  'error.invitation-expired': 'این دعوت‌نامه منقضی شده. یه دعوت تازه بخواه.',
  'error.invalid-username': 'نام کاربری باید ۳ تا ۲۴ حرف انگلیسی، عدد، _ یا - باشه.',
  'error.password-too-short': 'رمز باید حداقل ۱۰ کاراکتر باشه.',
  'error.invalid-target': 'این زبان در دسترس نیست.',
  'error.username-taken': 'این نام کاربری قبلاً گرفته شده — یکی دیگه امتحان کن.',
  'error.account-unreadable': 'حسابت ساخته شد ولی باز نشد. لطفاً از صفحه‌ی ورود وارد شو.',
  'error.wrong-credentials': 'نام کاربری یا رمز درست نیست.',
  'error.too-many-attempts': 'تعداد تلاش‌ها زیاد شد — لطفاً چند دقیقه صبر کن و دوباره امتحان کن.',
  'error.recovery-invalid': 'این لینک بازیابی دیگه معتبر نیست.',
  'error.sign-in-required': 'لطفاً دوباره وارد شو.',
  'error.not-found': 'پیدا نشد — شاید پاک شده باشه.',
  'error.history-unavailable': 'الان سابقه‌ی یادگیریت باز نمی‌شه. لطفاً به ما خبر بده.',
  'error.delete-failed': 'این لحظه پاک نشد — چیزی حذف نشد. لطفاً دوباره امتحان کن.',
  'error.no-recording': 'این لحظه دیگه ضبط صدا نداره.',
  'error.no-scene': 'این لحظه صحنهٔ خیالی‌ای برای پخش نداره.',
  'error.scene-audio-unavailable': 'صداهای صحنه اینجا در دسترس نیستن؛ جمله‌ها یکی‌یکی خونده می‌شن.',
  'error.scene-audio-failed': 'الان نشد صداهای صحنه ساخته بشن؛ جمله‌ها یکی‌یکی خونده می‌شن.',
  'error.moment-deleted': 'این لحظه پاک شده.',
  'error.draft-account': 'این پیش‌نویس با یه حساب دیگه ذخیره شده.',
  'error.draft-language': 'این پیش‌نویس برای یه زبان دیگه ذخیره شده.',
  'error.draft-expired': 'این ضبط مال بیشتر از ۳۰ روز پیشه و دیگه نگه داشته نمی‌شه.',
  'error.daily-allowance': 'سهم لحظه‌های تازه‌ی امروزت تموم شد. ساعت {time} به وقت {timezone} دوباره باز می‌شه. تمرین و مرور همچنان بازه.',
  'error.daily-review': 'اول مرور امروز رو تموم کن ({done} از {required} تا انجام شده) تا لحظه‌های تازه باز شن. تمرین و مرور همچنان بازه.',
  'error.ai-allowance': 'سهمیه‌ی هوش مصنوعیت برای این ماه تموم شد — ساعت ۰۰:۰۰ به وقت UTC در {date} دوباره شارژ می‌شه. تمرین و مرور چیزهایی که داری همچنان بازه.',
  'error.beta-budget': 'بودجه‌ی مشترک هوش مصنوعی بتا تموم شده — ساعت ۰۰:۰۰ به وقت UTC در {date} دوباره شارژ می‌شه. تمرین و مرور چیزهایی که داری همچنان بازه.',
  'error.audio-required': 'صدایی به دستمون نرسید. لطفاً دوباره ضبط کن.',
  'error.audio-too-large': 'این ضبط زیادی بزرگه (حداکثر ۱۵ مگابایت).',
  'error.shared-too-long': 'این پیام صوتی بیشتر از ۳۰ ثانیه‌ست. فال‌گوش پیام‌های صوتی تا ۳۰ ثانیه رو قبول می‌کنه.',
  'error.shared-unsupported': 'فال‌گوش هنوز نمی‌تونه این نوع صدا رو بخونه. پیام‌های صوتی واتس‌اپ و تلگرام جواب می‌دن.',
  'shared.title': 'یک پیام صوتی',
  'shared.menu': 'نصب و اشتراک‌گذاری',
  'shared.capability.android': 'برای دریافت پیام صوتی، فال‌گوش رو از منوی کروم نصب کن، بعد توی منوی Share برنامهٔ فرستنده، فال‌گوش رو انتخاب کن. پشتیبانی اندروید به مرورگر و گوشی بستگی داره؛ اگه فایلی نرسید، همین‌جا یه لحظه ضبط کن یا بنویس. پیامی که رسیده به نصب دوباره نیاز نداره.',
  'shared.capability.ios': 'روی آیفون یا آیپد، توی سافاری Share و بعد Add to Home Screen رو بزن تا فال‌گوش برای یادآورهای پشتیبانی‌شده نصب بشه. فرستادن پیام صوتی به فال‌گوش روی iOS پشتیبانی نمی‌شه؛ همین‌جا یه لحظه ضبط کن یا بنویس. پیامی که رسیده هنوز پایین همین صفحه هست.',
  'shared.capability.other': 'دریافت پیام صوتی به مرورگر پشتیبانی‌شدهٔ اندروید و نسخهٔ نصب‌شدهٔ فال‌گوش نیاز داره. روی این دستگاه، یه لحظه ضبط کن یا بنویس. پیامی که رسیده هنوز پایین همین صفحه هست.',
  'shared.offline': 'آفلاینی. پیامی که رسیده می‌تونه به‌صورت پیش‌نویس روی همین دستگاه بمونه؛ بعد از اتصال دوباره توی همین حساب، تبدیل به درس می‌شه.',
  'shared.sub': 'با فال‌گوش به اشتراک گذاشته شد. مثل یه ضبط، تبدیل می‌شه به یه لحظه.',
  'shared.none': 'چیزی به فال‌گوش نرسید یا مهلتش تموم شده. پیام صوتی تا ۳۰ ثانیه رو از واتس‌اپ یا تلگرام و از نسخهٔ نصب‌شدهٔ اندروید بفرست. دوباره به اشتراک بگذار یا همین‌جا یه لحظه ضبط کن یا بنویس.',
  'shared.unsupported': 'فال‌گوش هنوز نمی‌تونه این نوع صدا رو به درس تبدیل کنه. پیام صوتی واتس‌اپ یا تلگرام رو بفرست، یا یه لحظهٔ تازه ضبط کن یا بنویس.',
  'shared.recordInstead': 'یه لحظهٔ تازه ضبط کن',
  'shared.typeInstead': 'به‌جاش یه لحظه بنویس',
  'shared.tooLong': 'این پیام صوتی {length} طول داره. فال‌گوش پیام‌های صوتی تا ۳۰ ثانیه رو قبول می‌کنه.',
  'shared.pill': 'پیام',
  'error.note-audio-too-large': 'این یادداشت صوتی زیادی طولانیه. زیر یه دقیقه نگهش دار، یا تایپش کن.',
  'error.transcribe-limit': 'برای امروز یادداشت صوتی زیاد شد. این یکی رو تایپ کن، یا فردا دوباره بگو.',
  'error.transcribe-failed': 'الان نشد نوشته بشه. دوباره امتحان کن، یا تایپش کن.',
  'error.note-too-long': 'این یادداشت زیادی طولانیه (حداکثر ۲٬۰۰۰ کاراکتر).',
  'error.note-too-short': 'اول بنویس چی می‌خواستی بگی.',
  'error.analysis-failed': 'تحلیل این لحظه انجام نشد. لطفاً کمی بعد دوباره امتحان کن.',
  'error.content-blocked': 'از این نتوانستیم درسی بسازیم.',
  'error.newcomers-gone': 'این تازه‌واردها رفتن. یه روز دیگه کسای دیگه‌ای به شهرت میان.',
  'error.newcomers-failed': 'تازه‌واردها امروز نتونستن بیان. یه روز دیگه یکی دیگه میاد.',
  'error.moment-save-failed': 'این لحظه ذخیره نشد. لطفاً دوباره امتحان کن.',
  'error.seed-not-found': 'این جواب دیگه توی این لحظه نیست.',
  'error.seed-not-kept': 'این جواب رو نگه نداشتی، برای همین تمرین نمی‌شه. اول توی لحظه‌ش نگهش دار.',
  'error.town-fog-word': 'این کلمه هنوز توی مهه. اول توی محله‌ی خودش نگاه اولش رو بگیر.',
  'error.town-name-invalid': 'اسم شهرت باید ۱ تا ۲۴ حرف باشه، به هر خطی، و نشانه‌ی کنترلی پنهان نداشته باشه.',
  'error.heard-invalid': 'نشد این جمله رو شنیده‌شده حساب کنیم.',
  'error.comeback-invalid': 'نشد این لحظه رو دیده‌شده علامت بزنیم.',
  'error.seed-save-failed': 'ذخیره نشد. لطفاً دوباره امتحان کن.',
  'error.scoring-unavailable': 'بررسی تلفظ اینجا در دسترس نیست',
  'error.snippet-too-large': 'این ضبط برای بررسی زیادی طولانیه',
  'error.scoring-failed': 'بررسی نشد. دوباره امتحان کن',
  'error.attempt-save-failed': 'جوابت ذخیره نشد.',
  'error.attempt-item-removed': 'این مورد جای دیگری حذف شده. برای پاسخ‌دادن، اول حذفش را برگردان.',
  'error.exam-few-words': 'اول با چند کلمه‌ی دیگه آشنا شو.',
  'error.placement-unavailable': 'آزمون اولیه برای این زبان هنوز آماده نیست.',
  'error.circle-consent-required': 'اول باید با چیزهایی که دوستات می‌بینن موافقت کنی.',
  'error.already-in-circle': 'الان عضو یه حلقه‌ای. برای رفتن به یه حلقه‌ی دیگه، اول از این یکی بیرون بیا.',
  'error.consent-seen-invalid': 'این انتخاب ذخیره نشد. دوباره امتحان کن.',
  'error.circle-code-invalid': 'این کد کار نمی‌کنه. دوباره نگاهش کن، یا کد فعلی رو از دوستت بخواه: کدی که عوض شده دیگه کار نمی‌کنه.',
  'error.circle-full': 'این حلقه پره: همین الان ۱۰ عضو داره.',
  'error.not-in-circle': 'عضو هیچ حلقه‌ای نیستی.',
  'error.circle-not-keeper': 'فقط گرداننده‌ی حلقه می‌تونه کد تازه بسازه.',
  'error.friend-offer-invalid': 'این لینک دیگه کار نمی‌کنه. از دوستت یه لینک تازه بخواه.',
  'error.friend-own-link': 'این لینکِ خودته. برای یه دوست بفرستش.',
  'error.friends-full': 'الان نمی‌شه این دوستی رو ساخت.',
  'error.friends-own-full': 'همین الان ۱۰ دوست داری. برای اضافه‌کردن یکی دیگه، یه دوستی رو تموم کن.',
  'error.friend-offers-full': 'همین الان ۵ لینک و درخواست باز داری. یکی رو پس بگیر، یا صبر کن تا یکی استفاده بشه.',
  'error.friend-limit': 'امروز درخواست‌های زیادی فرستادی. فردا دوباره امتحان کن.',
  'error.raid-invalid': 'این لشکر این‌طوری فرستاده نمی‌شه. دوباره بسازش.',
  'error.raid-not-friends': 'فقط به یکی از دوستات می‌تونی حمله کنی.',
  'error.raid-pending': 'لشکرت همین الان توی راهه.',
  'error.raid-full': 'دم دروازه‌هاش همین الان لشکر زیاده. بعداً دوباره امتحان کن.',
  'error.raid-no-town': 'هنوز کلمه‌ای نداره که ازش دفاع کنه.',
  'error.raid-limit': 'لشکرهای امروزت رو فرستادی. فردا دوباره.',
  'error.raid-empty': 'هیچ سلاحی پر نشده، پس لشکری برای فرستادن نیست.',
  'error.raid-not-found': 'این حمله دیگه این‌جا نیست.',
  'error.raid-over': 'این حمله تموم شده.',
  // ---- the town (both looks) and the town phone check (/town-check) ----
  'town.nightLabel': 'شهرت در شب',
  'town.nightConditionLabel': 'شهر تو در شب: {ivy} نیاز به مرور؛ {cracks} ساختمون آسیب‌دیده از حمله.',
  'town.dioramaLabel': 'شهرت به شکل یک ماکت نورانی',
  'town.help': 'این چیه که می‌بینم؟',
  'town.credit': 'صدا و خانواده‌ی کلمه‌ها از',
  'town.zoomIn': 'بزرگ‌نمایی',
  'town.zoomOut': 'کوچک‌نمایی',
  'town.fitLabel': 'نمایش کل شهر',
  'town.fit': 'کل شهر',
  'town.noWebgl': 'این نما به WebGL2 نیاز داره که این مرورگر نداره.',
  'town.homeTitle': 'شهر تو',
  'town.actions': 'جاها و تمرین‌های شهر',
  'town.openDistrict': 'باز کردن محله‌ی {name}',
  'town.openBuilding': 'باز کردن کلمه‌ی {name}، معنی {meaning}',
  'town.openBridge': 'باز کردن پل بین {a} و {b}',
  'town.bridge': 'پل',
  'town.bannerHint': 'پرچم‌ها: یکی برای هر یورشی که ترک‌هاش هنوز بازه. یه پرچم رو باز کن تا ببینی چطور پایین میاد.',
  'town.openBanner': 'باز کردن پرچم {name}. کلمه‌های ترک‌خورده رو ترمیم کن تا پایین بیاد.',
  'town.baysHint': 'تیکه‌های ساختمون، یکی برای هر هجا. یه تیکه رو انتخاب کن تا هجاش رو بخونی.',
  'town.pickBay': 'خواندن هجای تیکه‌ی {i} از {n}{syllable}',
  'town.detailsLoading': 'در حال بارگذاری جزئیات شهر',
  'town.detailsReady': 'جزئیات شهر آماده است',
  'town.homeHint': 'روی یه ساختمون، محله یا پل بزن تا بهش برسی.',
  'town.homeMaterials': '{fog} توی مه، {scaffolding} داربست، {wood} چوب، {stone} سنگ، {gold} طلا',
  'town.maintenanceExplain': 'چراغ هر کلمه تا مرور بعدی آماده می‌مونه. بعد از موعد، تو سه فاصله‌ی مرور کم‌نور و علف‌گرفته می‌شه. بدون کمک، توی مرحله‌ی فعلی یادش بیار تا آماده بشه. عقب‌افتادگی جنس ساختمون رو عوض نمی‌کنه؛ ارزیابی بازیابی ممکنه اونو عوض کنه. ترک‌های حمله جدان.',
  'town.conditionSummary': 'آمادگی شهر',
  'town.conditionDistrictHint': 'آمادگی، وضعیت ساختمون‌های این محله رو خلاصه می‌کنه. برای مرور به این محله رسیدگی کن؛ ترک‌های حمله تعمیر جدا دارن.',
  'town.maintenanceLights': 'چراغ روشن: {lamps}، نیاز به مرور: {ivy}',
  'town.maintenanceFading': '{n} چراغ با رسیدن موعد مرور کم‌نور می‌شن',
  'town.maintenanceFadingOne': 'یه چراغ با رسیدن موعد مرور کم‌نور می‌شه',
  'tend.condition.maintained': 'تا مرور بعدی آماده‌ست. بدون کمک یادش بیار تا آماده بمونه.',
  'tend.condition.neglected': 'مرور عقب افتاده و محافظت فرسوده می‌شه. بدون کمک یادش بیار تا آماده بشه؛ ترک‌های حمله تعمیر جدا دارن.',
  'tend.condition.unproven': 'این کلمه رو توی مرحله‌ی فعلی بدون کمک یادش بیار تا محافظتش آماده بشه.',
  'town.homeLights': '{lamps} چراغ روشن این هفته، {ivy} علف‌گرفته',
  'town.knownOnly': 'کلمه‌هایی که از قبل بلدی همین‌جا وایسادن؛ نیازی به نگهداری ندارن.',
  'town.restingOnly': 'کلمه‌های اینجا یا بلدی یا برای بعد کنار گذاشته شدن؛ برای همین نیازی به نگهداری ندارن.',
  'town.homeLanes': 'هر خطی که توی یه لحظه گفته شده، یه کوچه‌ی گچی بین خونه‌های کلمه‌هاشه. همه‌ی کلمه‌های یه خط رو که بلد باشی، کوچه‌اش سنگ‌فرش می‌شه و دو سرش چراغ روشن می‌شه.',
  'town.homeExplain': 'هر کلمه‌ای که می‌شنوی یه ساختمونه. اولین باری که درست جوابش رو بدی از مه بیرون میاد و داربست می‌شه، بعد هر بار که بعد از یه فاصله‌ی طولانی‌تر یادت بیاد چوب، سنگ و طلا می‌شه. چراغ روشن یعنی توی ۷ روز گذشته درست جوابش رو دادی. علف‌گرفته یعنی وقت مرورشه. روی یه ساختمون، محله یا پل بزن تا با همون کارت‌های مرور بهش برسی.',
  // ---- the town's props ----
  'props.yourTown': 'شهر تو',
  'props.checkLand': 'زمین خالی',
  'props.checkLandIntro': 'شهری که هنوز هیچ لحظه‌ای نداره، مثل شب اول: زمین با تابلو و فانوسش، توی هر نما باز می‌شه و {seconds} ثانیه جابه‌جا می‌شه.',
  // ---- the town's "What am I seeing?" panel ----
  'townLegend.head': 'هر جزیره یه لحظه‌ست که ضبط کردی؛ هر قطعه‌ی زمین روش یه کلمه یا عبارته که اونجا شنیدی.',
  'townLegend.stats': 'امشب: {lit} روشن، {weathered} علف‌گرفته، {fog} توی مه، {forged} آب‌دیده.',
  'townLegend.fog': 'مه روی کلمه‌هاییه که شنیدی و هنوز نوبت اولین نگاه بهشون نرسیده؛ فقط طرحشون پیداست.',
  'townLegend.ivy': 'علف از کلمه‌ای بالا می‌ره که وقت مرورش گذشته، از تیرک‌های داربست یا از دیوارهای بقیه؛ هرچی دیرتر شده باشه، بالاتر می‌ره.',
  'townLegend.bridge': 'کلمه‌ای که توی بیشتر از یه لحظه شنیده شده، اون لحظه‌ها رو با یه پل طنابی به هم وصل می‌کنه؛ اگه این هفته یه کلمه‌ی مشترک رو تمرین کرده باشی، چراغ‌هاش روشنه.',
  'townLegend.guess': 'حدس‌های تقریبی: کاربری ساختمون (فعل‌ها کارگاه‌ان، اسم‌ها خونه، بقیه باغ) و تابلوی کوچیک کنار در (یه موضوع مثل غذا یا پول) از حدس زدن معنی انگلیسی میان.',
  'townLegend.syllables': 'هر هجا یه تیکه از ساختمونه، و بلندترین تیکه جاییه که تکیه روشه.',
  'townLegend.windows': 'شکل پنجره‌ها از شکل دهن موقع گفتن مصوت میاد: a طاق، e مربع، i شکاف باریک، o گرد، u دایره‌ی کوچیک.',
  'townLegend.accent': 'علامت تکیه‌ی نوشته‌شده یه سنگ تاج بالای پنجره می‌ذاره؛ حرفی که نوشته می‌شه ولی خونده نمی‌شه یه پنجره‌ی کور اضافه می‌کنه.',
  'townLegend.lights': 'نورهای گرم کلمه‌هایی‌ان که این هفته تمرین کردی؛ هرچی روشن‌تر، تازه‌تر. بقیه زیر نور ماه خوابیدن.',
  'townLegend.moon': 'ماه با سهم کلمه‌هایی که از مه بیرون اومدن و این هفته تمرینشون کردی بزرگ می‌شه؛ وقتی همه‌ی چراغ‌ها روشنن کامل می‌شه.',
  'townLegend.day': 'وقتی همه‌ی کلمه‌هایی که از مه بیرون اومدن همین هفته تمرین شده باشن و هیچ‌کدوم عقب نیفتاده باشه، دست‌کم {min} تا باشن، و دست‌کم نصف کلمه‌هایی که شنیدی از مه بیرون اومده باشن، شهر روز می‌شه. اولین چراغی که خاموش بشه، شب برمی‌گرده.',
  'townLegend.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های روشن دور پی، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌کوبی‌شده)، سنگ (دیوار)، بعد طلا (سقفی با تیغه، لبه یا کلاهک طلاکاری‌شده).',
  'townLegend.walls': 'بافت دیوارها از صامت‌ها میاد: سنگ برای p/t/k، آجر برای s/f، طاق برای m/n، نرده‌ی آهنی برای l/r.',
  'townLegend.phrase': 'یه عبارت یه ردیف ساختمونه: کلمه‌های کوچیک ایوون‌ها، پلاک‌ها، درگاه‌ها و طاق‌هایی می‌شن که بهمشون وصل می‌کنن.',
  'townLegend.together': 'کلمه‌هایی که مال هم‌ان با هم ساخته می‌شن: دیوارهای مشترک، یه خط بام، راه‌های سرپوشیده و ریسه‌های فانوس؛ وقتی دو کلمه‌ی به‌هم‌مربوط هر دو خوب ساخته بشن، یه طاق روی کوچه به هم وصلشون می‌کنه.',
  'townLegend.gold': 'حاشیه‌ی طلایی و بادنما نشون کلمه‌هاییه که کلی تلاش برد؛ خزه و مس سبزشده نشون کلمه‌های قدیمی‌ایه که نگهشون داشتی.',
  'townLegend.lanes': 'کوچه‌ها: هر کدوم یه خط از اون لحظه‌ست، با گچ بین خونه‌های کلمه‌هاش کشیده شده، و وقتی همه‌ی کلمه‌هاش رو بلد باشی سنگ‌فرش می‌شه.',
  'townLegend.fa.lights': 'فانوس‌های کهربایی و ارسی‌های روشن (پنجره‌های کشویی با شیشه‌ی رنگی) کلمه‌هایی‌ان که این هفته تمرین کردی؛ هرچی روشن‌تر، تازه‌تر. بقیه زیر نور ماه خوابیدن.',
  'townLegend.fa.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های سپیدار)، چوب (دیوارهای سپیدار با لبه‌ی بام، ایوان ستون‌دار و پنجره‌های مشبک)، سنگ (خشت خام با ورودی‌های طاق‌دار)، بعد طلا (کاهگل، بام کامل و سردر تزئین‌شده، با لبه، تیغه یا کلاهک طلاکاری‌شده).',
  'townLegend.fa.syllables': 'هر هجا یه تیکه از ساختمونه، و بلندترین تیکه جاییه که تکیه روشه؛ روی خونه به شکل ایوون باز می‌شه، ایوونی طاق‌دار پشت یه پیشطاق با مقرنس توی سرش.',
  'townLegend.fa.openings': 'شکل پنجره‌ها و درگاه‌ها از شکل دهن موقع گفتن مصوت میاد: a طاق جناغی، e مشبک چوبی، i شکاف باریک طاق‌دار، o ارسی، u روزن ستاره‌ای.',
  'townLegend.fa.walls': 'کار دیوار از صامت‌ها میاد: آجر پخته برای p/t/k، هزارباف برای s/f، طاقچه‌های کور طاق‌دار برای m/n، ستون‌های باریک تالار برای l/r.',
  'townLegend.fa.accent': 'علامت تکیه‌ی نوشته‌شده یه ترنج کاشی بالای پنجره می‌ذاره؛ حرفی که نوشته می‌شه ولی خونده نمی‌شه یه طاقچه‌ی کور اضافه می‌کنه.',
  'townLegend.fa.phrase': 'یه عبارت یه ردیف ساختمونه: کلمه‌های کوچیک بینشون یه پله‌ی آجری با سکو، کتیبه‌های کاشی، یه درگاه جناغی، یه طاق روی کوچه یا یه در گل‌میخ‌دار می‌شن.',
  'townLegend.fa.together': 'کلمه‌هایی که مال هم‌ان با هم ساخته می‌شن: دیوارهای مشترک، یه کتیبه‌ی کاشی سرتاسری، راه‌های آجری و ریسه‌های فانوس رنگی؛ وقتی دو کلمه‌ی به‌هم‌مربوط هر دو خوب ساخته بشن، یه طاق روی کوچه به هم وصلشون می‌کنه.',
  'townLegend.fa.use': 'بر اساس کاربری: طاق‌های بازار با گنبدهای نورگیردار (بازار)، چهارباغ با جوی‌ها، حوض و سروها (باغ)، پیشطاق کاشی‌کاری با گنبد یا مناره‌ها (بنای شاخص). وقتی این هفته تمرین شده باشن، بعضی کارگاه‌های فعل یه آسباد (آسیاب بادی عمودی) دارن که می‌چرخه و بعضی یه دودکش که دود می‌کنه.',
  'townLegend.fa.gold': 'حاشیه‌ی طلایی و کلاهک زراندود نشون کلمه‌هاییه که کلی تلاش برد؛ زنگار سبزآبی روی کاشی‌ها و گرد نشسته نشون کلمه‌های قدیمی‌ایه که نگهشون داشتی.',
  'townLegend.fa.lanes': 'کوچه‌ها: هر کدوم یه خط از اون لحظه‌ست، با گچ بین خونه‌های کلمه‌هاش کشیده شده، و وقتی همه‌ی کلمه‌هاش رو بلد باشی آجرفرش می‌شه.',
  // fr kit
  'townLegend.fr.lights': 'فانوس‌های گرم، پنجره‌های فرانسوی روشن و نور یه کافه کلمه‌هایی‌ان که این هفته تمرین کردی؛ هرچی روشن‌تر، تازه‌تر. بقیه زیر نور ماه خوابیدن.',
  'townLegend.fr.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های روشن دور یه پی از قطعه‌سنگ‌های تراشیده، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌کوبی‌شده)، سنگ (سنگ آهک خاکستری و زبر؛ کافه از همین‌جا سایه‌بون راه‌راهش رو داره)، بعد طلا (سنگ کرم‌رنگ تراش‌خورده با طبقه‌ی همکف شیاردار، سرپنجره‌ها و بالکن آهنی، زیر یه بام مانسارد از روی و سنگ لوح با پنجره‌های سقفی، طلاکاری‌شده روی نرده‌ی بالکن، تیغه‌ها یا کلاهک).',
  'townLegend.fr.syllables': 'هر هجا یه تیکه از ساختمونه که از تلفظ فرهنگ لغت خونده می‌شه، و بلندترین تیکه جاییه که تکیه روشه: توی فرانسوی، آخرین هجایی که گفته می‌شه. کلمه‌ای که فرهنگ لغت تلفظش رو نداره یه خونه‌ی ثابت می‌گیره که از خود کلمه ساخته می‌شه.',
  'townLegend.fr.windows': 'شکل پنجره‌ها از شکل دهن موقع گفتن مصوت میاد: a یه پنجره‌ی فرانسوی با سر گرد، e سرصاف، i شکاف باریک، o طاق کم‌خیز، u یه «چشم گاوی» (پنجره‌ی گرد کوچیک).',
  'townLegend.fr.walls': 'کار دیوار از صامت‌ها میاد: سنگ‌های گوشه برای p/t/k، آجر بین لبه‌های سنگی برای s/f، طاقچه‌های کور برای m/n، ستون‌نماهای چدنی برای l/r.',
  'townLegend.fr.accent': 'علامت نوشته‌شده روی حرف (é، è، ê، à…) یه سنگ تاج کنده‌کاری‌شده بالای پنجره می‌ذاره که توی طلا لبه‌ی طلایی می‌گیره؛ حرف‌هایی که گفته نمی‌شن چیزی رو عوض نمی‌کنن.',
  'townLegend.fr.phrase': 'یه عبارت یه ردیف ساختمونه: کلمه‌های کوچیک می‌شن یه پله‌ی سنگی یا سنگ‌فرش، پلاک‌های لعابی آبی، یه درگاه کالسکه‌رو، یه طاق توی طبقه‌ی اول یا یه در آهنی فرفورژه بینشون، و سؤال یه زنگ زیر یه برجک با سقف سنگ لوح آویزون می‌کنه.',
  'townLegend.fr.together': 'کلمه‌هایی که با هم‌ان با هم ساخته می‌شن: دیوارهای مشترک، یه خط بام مانسارد، راهروها و ریسه‌های فانوس؛ وقتی دو کلمه‌ی به‌هم‌مربوط هر دو خوب ساخته بشن، یه طاق روی کوچه به هم وصلشون می‌کنه.',
  'townLegend.fr.use': 'بر اساس کاربرد: کافه‌ها زیر سایه‌بون راه‌راه (بازار)، یه میدون با درخت‌های چنار، یه آب‌نمای والاس و یه ستون موریس (باغ)، یه گنبد یا مناره‌ی سنگ لوحی (بنای شاخص). بعضی کارگاه‌های فعل یه آسیاب بادی دارن، بقیه دودکش‌هایی که وقتی این هفته تمرین شده باشن دود می‌کنن.',
  'townLegend.fr.gold': 'یه نوار یه طبقه بالاتر (برنزی توی سنگ، طلایی توی طلا) و یه کلاهک زراندود یا یه پرچم سه‌گوش نشون کلمه‌هاییه که کلی تلاش برد؛ رگه‌های تیره‌ی بارون روی دیوارها نشون کلمه‌های قدیمیه که نگهشون داشتی.',
  'townLegend.fr.lanes': 'کوچه‌ها: هر کدوم یه خط از اون لحظه‌ست، با گچ بین خونه‌های کلمه‌هاش کشیده شده، و وقتی همه‌ی کلمه‌هاش رو بلد باشی سنگ‌فرش می‌شه.',
  // es kit
  'townLegend.es.lights': 'فانوس‌های گرم و نورگیرهای مشبک روشن بالای درها کلمه‌هایی‌ان که این هفته تمرین کردی؛ هرچی روشن‌تر، تازه‌تر. بقیه زیر نور ماه خوابیدن.',
  'townLegend.es.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های کاج دور یه ازاره‌ی ماسه‌سنگی، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌ی کاج)، سنگ (دیوارهای لاشه‌سنگ و ماسه‌سنگ طلایی)، بعد طلا (سفیدکاری گرم آهکی روی یه ازاره‌ی اُخرایی، با بام سفال یا بام صاف پشت یه جان‌پناه کوتاه، با تیغه، لبه یا کلاهک طلاکاری‌شده).',
  'townLegend.es.syllables': 'هر هجا یه تیکه از ساختمونه، و بلندترین تیکه جاییه که تکیه روشه؛ در سنگین گل‌میخ‌دار خونه همون‌جاست، زیر یه نورگیر مشبک.',
  'townLegend.es.openings': 'شکل پنجره‌ها و درگاه‌ها از شکل دهن موقع گفتن مصوت میاد: a طاق نعل‌اسبی، e پنجره‌ی گود پشت نرده‌ی آهنی با گلدون‌های شمعدونی، i در بلند بالکن پشت نرده‌ی آهنی، o پنجره‌ی طاق‌گرد، u یه روزن گرد.',
  'townLegend.es.walls': 'کار دیوار از صامت‌ها میاد: ستون‌نماهای اُخرایی برای p/t/k، آجرکاری مدجن برای s/f، طاقچه‌های کور نعل‌اسبی برای m/n، ستون‌های آهن‌کاری برای l/r.',
  'townLegend.es.accent': 'علامت تکیه‌ی نوشته‌شده یه ترنج کاشی نقاشی‌شده بالای پنجره می‌ذاره؛ حرفی که نوشته می‌شه ولی خونده نمی‌شه یه پنجره‌ی کور اضافه می‌کنه.',
  'townLegend.es.phrase': 'یه عبارت یه ردیف ساختمونه: کلمه‌های کوچیک بینشون یه پله‌ی سنگی با گلدون‌های شمعدونی، کتیبه‌های کاشی، یه گذر نعل‌اسبی، یه طاق روی کوچه یا یه درِ آهنی می‌شن، و یه سؤال یه زنگ توی یه دیوارک زنگ آویزون می‌کنه.',
  'townLegend.es.together': 'کلمه‌هایی که مال هم‌ان با هم ساخته می‌شن: دیوارهای مشترک، یه نوار کاشی سرتاسری، راه‌های سنگی و ریسه‌های فانوس جشن؛ وقتی دو کلمه‌ی به‌هم‌مربوط هر دو خوب ساخته بشن، یه طاق روی کوچه به هم وصلشون می‌کنه.',
  'townLegend.es.bridge': 'کلمه‌ای که توی بیشتر از یه لحظه شنیده شده، اون لحظه‌ها رو با یه پل سنگی روی طاق‌های گرد به هم وصل می‌کنه؛ اگه این هفته یه کلمه‌ی مشترک رو تمرین کرده باشی، چراغ‌پایه‌های آهنیش روشنه.',
  'townLegend.es.use': 'بر اساس کاربری: یه رواق مغازه‌ها زیر سایه‌بون راه‌راه (بازار)، یه حیاط با درخت‌های پرتقال و فواره‌ی کاشی (باغ)، یه گنبد کاشی یا یه برج آجری مدجن (بنای شاخص). وقتی این هفته تمرین شده باشن، بعضی کارگاه‌های فعل پره‌های آسیاب بادی دارن که می‌چرخه و بعضی یه دودکش سفید که دود می‌کنه.',
  'townLegend.es.gold': 'حاشیه‌ی طلایی دور پنجره‌ها و درها و یه بادنمای زراندود یا یه پرچم سه‌گوش نشون کلمه‌هاییه که کلی تلاش برد؛ رسوب آهک و خزه نشون کلمه‌های قدیمی‌ایه که نگهشون داشتی.',
  'townLegend.es.lanes': 'کوچه‌ها: هر کدوم یه خط از اون لحظه‌ست، با گچ بین خونه‌های کلمه‌هاش کشیده شده، و وقتی همه‌ی کلمه‌هاش رو بلد باشی سنگ‌فرش می‌شه.',
  // en kit
  'townLegend.en.lights': 'نور کهربایی چراغ‌گازی توی پنجره‌ها و کنار درها کلمه‌هایی‌ان که این هفته تمرین کردی؛ هرچی روشن‌تر، تازه‌تر. بقیه زیر نور ماه خوابیدن.',
  'townLegend.en.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های روشن دور یه ازاره‌ی آجری تیره، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌کوبی‌شده)، سنگ (آجر زرد لندنی خام با درگاه‌های ساده)، بعد طلا (ردیف خونه‌های تموم‌شده: آجرچینی فلاندری روی طبقه‌ی همکف گچ‌کاری سفید، پنجره‌های کشویی سفید، در قاب‌دار زیر یه نورگیر نیم‌دایره، و بام سنگ‌لوحی پشت جان‌پناه با تیغه، لبه یا کلاهک طلاکاری‌شده).',
  'townLegend.en.syllables': 'هر هجا یه تیکه از ساختمونه، و بلندترین تیکه جاییه که تکیه روشه؛ روی خونه یه پنجره‌ی جلوآمده (بی‌ویندو) بیرون میده.',
  'townLegend.en.openings': 'شکل پنجره‌ها از شکل دهن موقع گفتن مصوت میاد: a پنجره‌ی کشویی با سر گرد، e پنجره‌ی کشویی شش‌درشش، i پنجره‌ی کشویی باریک، o پنجره‌ی گرد، u چشم‌گاوی کوچیک.',
  'townLegend.en.walls': 'کار دیوار از صامت‌ها میاد: سنگ‌کنج برای p/t/k، ستون‌نمای آجر قرمز برای s/f، طاق کور برای m/n، ناودون چدنی برای l/r.',
  'townLegend.en.accent': 'علامت تکیه‌ی نوشته‌شده یه سنگ تاج سفید بالای پنجره می‌ذاره؛ حرفی که نوشته می‌شه ولی خونده نمی‌شه یه پنجره‌ی کور نقاشی‌شده اضافه می‌کنه.',
  'townLegend.en.phrase': 'یه عبارت یه ردیف خونه‌ست: کلمه‌های کوچیک بین خونه‌هاش پله‌های سنگی و نرده، پلاک‌های خیابون، طاق‌های کالسکه‌رو، اتاق‌هایی روی کوچه و درهای آهنی می‌شن.',
  'townLegend.en.together': 'کلمه‌هایی که مال هم‌ان با هم ساخته می‌شن: دیوارهای مشترک، یه خط جان‌پناه، پیاده‌روهای سنگی و ریسه‌های چراغ؛ وقتی دو کلمه‌ی به‌هم‌مربوط هر دو خوب ساخته بشن، یه طاق روی کوچه به هم وصلشون می‌کنه.',
  'townLegend.en.use': 'بر اساس کاربری: میخونه‌ی سر نبش با کاشی سبز بطری (بازار)، یه چمن‌زار روستایی با بلوط‌ها و یه صندوق پست قرمز (باغ)، مناره‌ی نوک‌تیز کلیسا یا گنبدک سربی (بنای شاخص)، خونه‌ها در ردیف. وقتی این هفته تمرین شده باشن، بعضی کارگاه‌های فعل پره‌هایی دارن که می‌چرخن و بعضی یه دودکش با کلاهک‌های سفالی که دود می‌کنه.',
  'townLegend.en.gold': 'یه نوار طلایی و یه بادنما یا یه پرچم نشون کلمه‌هاییه که کلی تلاش برد؛ دوده و رد بارون روی آجر نشون کلمه‌های قدیمی‌ایه که نگهشون داشتی.',
  'townLegend.en.lanes': 'کوچه‌ها: هر کدوم یه خط از اون لحظه‌ست، با گچ بین خونه‌های کلمه‌هاش کشیده شده، و وقتی همه‌ی کلمه‌هاش رو بلد باشی سنگ‌فرش می‌شه.',
  'townLegend.night.moor': 'اولین لحظه وسط نشسته؛ هر لحظه‌ی تازه کنار لحظه‌ای لنگر می‌ندازه که بیشترین کلمه‌ی مشترک رو باهاش داره. روی یه ساختمون بزن تا کلمه‌اش رو ببینی، روی یه تابلو تا لحظه‌اش رو.',
  'townLegend.night.mist': 'مه و یه طرح کم‌رنگ: شنیده شده، هنوز تمرین نشده. اینجا مرز توئه؛ این طرح همون ساختمونیه که کلمه قراره بشه.',
  'townLegend.night.bridge': 'یه پل: یه کلمه توی هر دو لحظه اومده. پل پهن‌تر یعنی کلمه‌های مشترک بیشتر. کلمه یه پرچم کوچیک به رنگ اون یکی لحظه داره. لحظه‌هایی که برای پل خیلی از هم دورن یه ریسه‌ی چراغ می‌گیرن.',
  'townLegend.night.ivy': 'علف: وقت مرورش رسیده. از تیرک‌های داربست یا از دیوارهای بقیه بالا می‌ره، و هرچی کلمه دیرتر شده باشه، بالاتر می‌ره.',
  'townLegend.night.guess': 'حدس‌های تقریبی: کاربری ساختمون و تابلوی کوچیک کنار در (یه موضوع مثل غذا یا پول) از حدس زدن معنی انگلیسی میان.',
  'townLegend.night.stars': 'ستاره‌ها: هرچی بیشتر جواب بدی، آسمون پرستاره‌تره. ماه وقتی کامل‌تره که بیشتر کلمه‌های تمرین‌شده‌ات همین هفته تمرین شده باشن.',
  'townLegend.night.stats': 'امشب: {lit} روشن، {weathered} علف‌گرفته، {fog} توی مه، {forged} آب‌دیده.',
  'townLegend.night.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های روشن دور پی، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌کوبی‌شده)، سنگ (دیوار)، بعد طلا (گچ‌کاری و سقفی با تیغه، لبه یا کلاهک طلاکاری‌شده). از سنگ به بالا کلمه آب‌دیده‌ست، آماده‌ی نبرد.',
  'townLegend.night.walls': 'بافت دیوارها از صامت‌ها میاد: سنگ برای p/t/k، آجر برای s/f، طاق برای m/n، نرده‌ی آهنی برای l/r؛ صامت تشدیددار نوارش رو دوتا می‌کنه.',
  'townLegend.night.phrase': 'یه عبارت یه ردیف ساختمونه: کلمه‌های کوچیک ایوون‌ها، پلاک‌ها، درگاه‌ها، طاق‌ها و درهایی می‌شن که بهمشون وصل می‌کنن، و یه سؤال یه زنگ آویزون می‌کنه.',
  'townLegend.night.use': 'بر اساس کاربری: فعل‌ها کارگاه‌ان (با دودکش یا آسیاب بادی)، اسم‌ها خونه، بقیه باغ؛ اصطلاح‌ها بناهای شاخص با گنبد یا مناره‌ان، جمله‌های شنیده‌شده خونه‌های ردیفی با شیروونی رو به کوچه، جواب‌ها غرفه‌های بازار.',
  'townLegend.night.lights': 'پنجره‌ها و فانوس‌های گرم: توی ۷ روز گذشته تمرین شده؛ هرچی تازه‌تر، پنجره‌های روشن بیشتر. تمرین رو ول کنی، شهر خاموش می‌شه.',
  'townLegend.night.gold': 'حاشیه‌ی طلایی و یه بادنما یا یه پرچم نشون کلمه‌هاییه که کلی تلاش برد.',
  'townLegend.night.fa.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های سپیدار)، چوب (دیوارهای سپیدار با لبه‌ی بام، ایوان ستون‌دار و پنجره‌های مشبک)، سنگ (خشت خام با ورودی‌های طاق‌دار)، بعد طلا (کاهگل، بام کامل و سردر تزئین‌شده، با لبه، تیغه یا کلاهک طلاکاری‌شده). از سنگ به بالا کلمه آب‌دیده‌ست، آماده‌ی نبرد.',
  'townLegend.night.fa.syllables': 'هر هجا یه تیکه از ساختمونه، و بلندترین تیکه جاییه که تکیه روشه؛ روی خونه به شکل ایوون باز می‌شه، ایوونی طاق‌دار پشت یه پیشطاق.',
  'townLegend.night.fa.openings': 'شکل پنجره‌ها و درگاه‌ها از شکل دهن موقع گفتن مصوت میاد: a طاق جناغی، e مشبک چوبی، i شکاف باریک طاق‌دار، o ارسی (پنجره‌ی کشویی با شیشه‌ی رنگی)، u روزن ستاره‌ای.',
  'townLegend.night.fa.walls': 'کار دیوار از صامت‌ها میاد: آجر پخته برای p/t/k، هزارباف برای s/f، طاقچه‌های کور طاق‌دار برای m/n، ستون‌های باریک تالار برای l/r؛ صامت تشدیددار دوتاش می‌کنه.',
  'townLegend.night.fa.phrase': 'یه عبارت یه ردیف ساختمونه: کلمه‌های کوچیک بینشون یه پله‌ی آجری با سکو، کتیبه‌های کاشی، یه درگاه جناغی، یه طاق روی کوچه یا یه در گل‌میخ‌دار می‌شن، و یه سؤال یه زنگ آویزون می‌کنه.',
  'townLegend.night.fa.use': 'بر اساس کاربری: طاق‌های بازار با گنبد (بازار)، چهارباغ با جوی‌ها، حوض و سروها (باغ)، پیشطاق کاشی‌کاری با گنبد یا مناره‌ها (بنای شاخص)؛ کارگاه‌های فعل یه آسباد (آسیاب بادی) یا یه دودکش می‌گیرن.',
  'townLegend.night.fa.lights': 'فانوس‌های کهربایی و ارسی‌های روشن: توی ۷ روز گذشته تمرین شده؛ هرچی تازه‌تر، پنجره‌های روشن بیشتر. تمرین رو ول کنی، شهر خاموش می‌شه.',
  'townLegend.night.fa.gold': 'حاشیه‌ی طلایی و یه کلاهک زراندود یا یه پرچم سه‌گوش نشون کلمه‌هاییه که کلی تلاش برد.',
  // fr kit
  'townLegend.night.fr.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های روشن دور پی، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌کوبی‌شده)، سنگ (دیوارهای سنگ آهک خاکستری)، بعد طلا (سنگ کرم‌رنگ تراش‌خورده و یه بام سنگ لوحی با تیغه، لبه یا کلاهک طلاکاری‌شده). از سنگ به بالا کلمه آب‌دیده‌ست، آماده‌ی نبرد.',
  // es kit
  'townLegend.night.es.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های کاج دور یه ازاره‌ی ماسه‌سنگی، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌ی کاج)، سنگ (دیوارهای لاشه‌سنگ و ماسه‌سنگ طلایی)، بعد طلا (سفیدکاری گرم روی یه نوار اُخرایی، با بام سفال یا بام صاف، با تیغه، لبه یا کلاهک طلاکاری‌شده). از سنگ به بالا کلمه آب‌دیده‌ست، آماده‌ی نبرد.',
  'townLegend.night.es.syllables': 'هر هجا یه تیکه از ساختمونه، و بلندترین تیکه جاییه که تکیه روشه؛ در سنگین خونه همون‌جاست.',
  'townLegend.night.es.openings': 'شکل پنجره‌ها و درگاه‌ها از شکل دهن موقع گفتن مصوت میاد: a طاق نعل‌اسبی، e پنجره پشت نرده‌ی آهنی، i در بلند بالکن، o پنجره‌ی طاق‌گرد، u یه روزن گرد.',
  'townLegend.night.es.walls': 'کار دیوار از صامت‌ها میاد: ستون‌نماهای اُخرایی برای p/t/k، آجرکاری مدجن برای s/f، طاقچه‌های کور نعل‌اسبی برای m/n، ستون‌های آهن‌کاری برای l/r؛ صامت تشدیددار دوتاش می‌کنه.',
  'townLegend.night.es.phrase': 'یه عبارت یه ردیف ساختمونه: کلمه‌های کوچیک بینشون یه پله‌ی سنگی با گلدون‌های شمعدونی، کتیبه‌های کاشی، یه گذر نعل‌اسبی، یه طاق روی کوچه یا یه درِ آهنی می‌شن، و یه سؤال یه زنگ آویزون می‌کنه.',
  'townLegend.night.es.use': 'بر اساس کاربری: رواق مغازه‌ها (بازار)، حیاط با درخت‌های پرتقال و فواره (باغ)، گنبد کاشی یا برج (بنای شاخص)؛ کارگاه‌های فعل پره‌های آسیاب بادی یا یه دودکش می‌گیرن.',
  'townLegend.night.es.lights': 'فانوس‌های گرم و پنجره‌های روشن: توی ۷ روز گذشته تمرین شده؛ هرچی تازه‌تر، پنجره‌های روشن بیشتر. تمرین رو ول کنی، شهر خاموش می‌شه.',
  'townLegend.night.es.gold': 'حاشیه‌ی طلایی و یه بادنمای زراندود یا یه پرچم سه‌گوش نشون کلمه‌هاییه که کلی تلاش برد.',
  // en kit
  'townLegend.night.en.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های روشن دور یه ازاره‌ی آجری تیره، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌کوبی‌شده)، سنگ (آجر زرد لندنی خام)، بعد طلا (آجر پخته، حاشیه‌ی سنگی و بام سنگ‌لوحی با تیغه، لبه یا کلاهک طلاکاری‌شده). از سنگ به بالا کلمه آب‌دیده‌ست، آماده‌ی نبرد.',
  'townLegend.night.en.walls': 'کار دیوار از صامت‌ها میاد: سنگ‌کنج برای p/t/k، ستون‌نمای آجر قرمز برای s/f، طاق کور برای m/n، ناودون چدنی برای l/r؛ صامت تشدیددار دوتاش می‌کنه.',
  'townLegend.night.en.phrase': 'یه عبارت یه ردیف خونه‌ست: کلمه‌های کوچیک بین خونه‌هاش پله، پلاک، طاق کالسکه‌رو و در آهنی می‌شن، و یه سؤال یه زنگ آویزون می‌کنه.',
  'townLegend.night.en.use': 'بر اساس کاربری: میخونه‌ی سر نبش (بازار)، یه چمن‌زار روستایی (باغ)، مناره‌ی نوک‌تیز کلیسا یا گنبدک سربی (بنای شاخص)، خونه‌ها در ردیف؛ کارگاه‌های فعل پره یا دودکش می‌گیرن.',
  'townLegend.night.en.lights': 'نور کهربایی چراغ‌گازی توی پنجره‌ها و کنار درها: توی ۷ روز گذشته تمرین شده؛ هرچی تازه‌تر، پنجره‌های روشن بیشتر. تمرین رو ول کنی، شهر خاموش می‌شه.',
  'townLegend.night.en.gold': 'حاشیه‌ی طلایی و یه بادنما یا یه پرچم نشون کلمه‌هاییه که کلی تلاش برد.',
  'townLegend.night.neutral.grow': 'ساختمون‌ها با تمرین بزرگ می‌شن: داربست (تیرک‌ها و تخته‌های روشن دور پی، به بلندی دیوارهایی که قراره بیان)، چوب (دیوارهای تخته‌کوبی‌شده)، سنگ (دیوار)، بعد طلا (دوغاب سفید و بام سنگ‌لوحی با تیغه، لبه یا کلاهک طلاکاری‌شده). از سنگ به بالا کلمه آب‌دیده‌ست، آماده‌ی نبرد.',
  'townLegend.night.neutral.seeded': 'هر کلمه ساختمون خودش رو داره. تا وقتی صداهای این زبان خونده نشده، تیکه‌ها، پنجره‌ها و بافت دیوارهاش از یه الگوی ثابت میان که خود کلمه تعیینش می‌کنه، پس یه کلمه همیشه همون خونه رو داره؛ بلندترین تیکه باز هم جای تکیه‌ست.',
  'townLegend.night.neutral.phrase': 'یه عبارت یه ردیف ساختمونه با دیوارهای مشترک.',
  'townLegend.night.neutral.use': 'بر اساس کاربری: فعل‌ها کارگاه‌ان، اسم‌ها خونه، بقیه باغ؛ اصطلاح‌ها بناهای شاخص با گنبد یا مناره‌ان، جمله‌های شنیده‌شده خونه‌های ردیفی، جواب‌ها غرفه‌های بازار.',
  'tend.sheetLabel': 'یه جا توی شهرت',
  'tend.close': 'بستن',
  'tend.untitled': 'یه لحظه',
  'tend.buildingLabel': 'ساختمون، بزرگ‌تر',
  'tend.tapBay': 'روی یه تیکه از ساختمون بزن تا هجاش رو ببینی.',
  'tend.syllable': 'هجا:',
  'tend.syllableStressed': 'هجای تکیه‌دار:',
  'tend.noSyllables': 'هجاهای این ساختمون معلوم نیست.',
  'tend.syllableUnknown': 'هجای این تیکه رو نمی‌شه نشون داد.',
  'tend.material.fog': 'توی مه: شنیدیش، ولی هنوز باهاش آشنا نشدی.',
  'tend.material.scaffolding': 'داربست: می‌تونی توی جمله‌اش پیداش کنی.',
  'tend.material.wood': 'چوب: می‌تونی توی جمله‌ی خودش دوباره بسازیش.',
  'tend.material.stone': 'سنگ: از روی معنیش یادت میاد.',
  'tend.material.gold': 'طلا: می‌تونی یه جای تازه به کارش ببری.',
  'tend.next.fog': 'بعدی: اولین نگاه بهش.',
  'tend.next.scaffolding': 'بعدی، وقتی وقتش رسید: توی جمله‌ی خودش دوباره بسازیش.',
  'tend.next.wood': 'بعدی، وقتی وقتش رسید: از روی معنیش یادش بیار.',
  'tend.next.stone': 'بعدی، وقتی وقتش رسید: یه جای تازه به کارش ببر.',
  'tend.next.gold': 'بعدی: نگهش دار، هر دو هفته یه بار یه سر بهش بزن.',
  'tend.state.fog': 'هنوز چراغی نداره: هنوز توی مهه.',
  'tend.state.lamp': 'چراغش روشنه: توی ۷ روز گذشته درست جوابش رو دادی.',
  'tend.state.dark': 'چراغش خاموشه: توی ۷ روز گذشته جواب درستی بهش ندادی.',
  'tend.state.known': 'این یکی رو بلدی؛ نیازی به مرور نداره.',
  'tend.state.later': 'کنار گذاشتیش برای بعد: تا وقتی توی لحظه‌اش «می‌خوام یاد بگیرم» رو نزنی، مرور نمی‌شه.',
  'tend.state.ivy': 'علف‌گرفته: وقت مرورشه.',
  'tend.tendIt': 'بهش برس',
  'tend.firstLook': 'اولین نگاه رو بنداز',
  'tend.firstLookHint': 'اگه اولین نگاهش رو درست جواب بدی از مه بیرون میاد، داربست می‌شه و از فردا توی مرورت میاد.',
  'tend.firstLookWait': 'الان نگاه اولی برای این کلمه در دسترس نیست.',
  'tend.openMoment': 'باز کردن لحظه',
  'tend.alsoHeard': 'اینجاها هم شنیده شده:',
  'tend.roads': 'جاده‌ها:',
  'tend.roadsHint': 'با یه کلمه‌ی مشترک رد شو: توی جمله‌ی اون یکی لحظه ازت پرسیده می‌شه.',
  'tend.roadTo': 'به {title}',
  'tend.roadFog': '(توی مه)',
  'tend.roadTitle': 'یه جاده',
  'tend.roadHint': 'کلمه‌هایی که توی هر دو لحظه شنیده شدن. با یکیش رد شو: توی جمله‌ی اون یکی لحظه ازت پرسیده می‌شه.',
  'tend.roadEmpty': 'هنوز هیچ کلمه‌ای توی هر دو لحظه شنیده نشده.',
  'tend.count.ivy': 'علف‌گرفته، وقت مرور: {n}',
  'tend.count.dark': 'خاموش، این هفته جواب درستی نگرفته: {n}',
  'tend.count.fog': 'توی مه، هنوز آشنا نشده: {n}',
  'tend.districtHint': 'رسیدگی تا ۱۲ کارت می‌پرسه: اول هر کارت آسون‌تری که منتظره، بعد ساختمون‌های علف‌گرفته، خاموش‌ها و تا {n} نگاه اول.',
  'tend.districtHintOne': 'رسیدگی تا ۱۲ کارت می‌پرسه: اول هر کارت آسون‌تری که منتظره، بعد ساختمون‌های علف‌گرفته، خاموش‌ها و ۱ نگاه اول.',
  'tend.districtHintNone': 'رسیدگی تا ۱۲ کارت می‌پرسه: اول هر کارت آسون‌تری که منتظره، بعد ساختمون‌های علف‌گرفته و خاموش‌ها.',
  'tend.tendDistrict': 'به این محله برس',
  'tend.titleWord': 'رسیدگی به یه ساختمون',
  'tend.titleRoad': 'از جاده رد شو',
  'tend.titleDistrict': 'رسیدگی به یه محله',
  'tend.titleDistrictOf': 'رسیدگی: {title}',
  'tend.nothing': 'الان اینجا چیزی برای رسیدگی نیست.',
  'tend.nothingDistrictHint': 'همه‌ی چراغ‌های اینجا روشنه و هیچ‌چی علف نگرفته. وقتی یه چراغ خاموش شد یا وقت مرور یه ساختمون رسید برگرد.',
  'tend.nothingHint': 'این ساختمون فعلاً کارتی نداره. فردا برگرد.',
  'tend.backToTown': 'برگشت به شهرت',
  'tend.done': 'تموم',
  'tend.nearby': '{n} خونهٔ دیگه همین نزدیکی منتظرتن',
  'tend.nearbyOne': 'یه خونهٔ دیگه همین نزدیکی منتظرته',
  'tend.nextHouse': 'خونهٔ بعدی',
  'tend.glintDue': 'یه خونهٔ علف‌گرفته: بعدی به این برس',
  'tend.glintDark': 'یه خونه با چراغ خاموش: بعدی به این برس',
  'tend.allTended': 'فعلاً خونهٔ دیگه‌ای منتظرت نیست.',
  'tend.retryNext': 'دوباره امتحان کن',
  'townCheck.title': 'بررسی شهر روی گوشی',
  'townCheck.intro': 'این صفحه دو شهر ساختگی رو، نه درس‌های کسی رو، با هر دو نمای شهر می‌کشه و اندازه می‌گیره این گوشی با چه سرعتی بازشون می‌کنه و جابه‌جاشون می‌کنه. حساب کاربری لازم نداره. حدود {minutes} دقیقه طول می‌کشه: صفحه رو روشن نگه دار و تا تموم نشده بهش دست نزن.',
  'townCheck.sizes': 'شهر کوچک: {smallD} لحظه، {smallB} ساختمان. شهر بزرگ: {largeD} لحظه، {largeB} ساختمان.',
  'townCheck.notes': 'بارگذاری سرد: اولین باری که یه شهر با یه نما باز می‌شه، از ساختن شهر تا اولین فریمش (اولین بار کدِ اون نما هم بارگذاری می‌شه). بارگذاری گرم: باز کردن دوباره‌ی همون شهر. میانه و ۱٪ کندترین: فریم در ثانیه در طول {seconds} ثانیه جابه‌جایی خودکار؛ ۱٪ کندترین میانگینِ کندترین یک درصد فریم‌هاست. فریم‌ها: تعداد فریم‌هایی که شهر در طول جابه‌جایی کشید. دیرکرده: سهم فریم‌هایی که دیر رسیدن، یعنی بیشتر از ۱٫۵ برابرِ فاصله‌ی معمولِ خودِ صفحه‌نمایش (برآورد شده از پایدارترین فریم‌های اجرا)، و صفحه همون فریم قبلی رو دوباره نشون داد. طولانی‌ترین فریم: بیشترین فاصله‌ی بین دو فریم. جابه‌جایی بلند شهر بزرگ رو بزرگ‌نمایی می‌کنه و از این سرش تا اون سرش و برگشت جابه‌جاش می‌کنه.',
  'townCheck.screen': 'صفحه‌نمایش',
  'townCheck.screenValue': '{w} × {h} پیکسل',
  'townCheck.dpr': 'نسبت پیکسل دستگاه',
  'townCheck.dRatio': 'نسبت پیکسلِ ماکت نورانی (D)',
  'townCheck.browser': 'مرورگر',
  'townCheck.panLength': 'مدت جابه‌جایی: {seconds} ثانیه',
  'townCheck.present': 'هست',
  'townCheck.missing': 'نیست',
  'townCheck.noWebgl2': 'این مرورگر WebGL2 نداره، پس ماکت نورانی (D) کشیده نمی‌شه. بررسی فقط شهر شب (B) رو می‌کشه.',
  'townCheck.dFailed': 'ماکت نورانی (D) روی این دستگاه راه نیفتاد، پس بررسی فقط شهر شب (B) رو می‌کشه.',
  'townCheck.start': 'شروع بررسی',
  'townCheck.again': 'دوباره اجرا کن',
  'townCheck.running': 'در حال اندازه‌گیری {look}، {size}: {step}…',
  'townCheck.stepCold': 'بارگذاری سرد',
  'townCheck.stepWarm': 'بارگذاری گرم',
  'townCheck.stepPan': 'جابه‌جایی به مدت {seconds} ثانیه',
  'townCheck.stepSweep': 'جابه‌جایی بلند به مدت {seconds} ثانیه',
  'townCheck.done': 'تموم شد. نتیجه‌ها رو کپی کن و برای صاحب برنامه بفرست.',
  'townCheck.stageLabel': 'شهری که داره اندازه‌گیری می‌شه',
  'townCheck.look': 'نما',
  'townCheck.size': 'شهر',
  'townCheck.cold': 'بارگذاری سرد',
  'townCheck.warm': 'بارگذاری گرم',
  'townCheck.median': 'میانه‌ی fps',
  'townCheck.low': '۱٪ کندترین (fps)',
  'townCheck.frames': 'فریم‌ها',
  'townCheck.missed': 'دیرکرده',
  'townCheck.longest': 'طولانی‌ترین فریم',
  'townCheck.lookD': 'ماکت نورانی (D)',
  'townCheck.lookB': 'شهر شب (B)',
  'townCheck.small': 'شهر کوچک',
  'townCheck.large': 'شهر بزرگ',
  'townCheck.longPan': '{size}، جابه‌جایی بلند',
  'townCheck.seconds': '{n} ثانیه',
  'townCheck.ms': '{n} میلی‌ثانیه',
  'townCheck.percent': '{n}٪',
  'townCheck.noFrame': 'فریمی کشیده نشد',
  'townCheck.skipped': 'کشیده نشد',
  'townCheck.skippedLine': '{look}، {size}: کشیده نشد',
  'townCheck.line': '{look}، {size}: سرد {cold}، گرم {warm}، میانه {median} fps، ۱٪ کندترین {low} fps، {frames} فریم، {missed} دیرکرده، طولانی‌ترین فریم {longest}',
  'townCheck.copy': 'کپی نتیجه‌ها',
  'townCheck.copied': 'کپی شد',
  'townCheck.copyFailed': 'کپی نشد. متن زیر رو انتخاب و کپی کن.',
  'townCheck.effectsOff': '{size}، بدون جلوه‌ها',
  'townCheck.stepPrice': 'قیمت هر جلوه',
  'townCheck.effects': 'هر جلوه چقدر خرج داره',
  'townCheck.effectsIntro': 'شهر بزرگ در ماکت نورانی (D)، هر فریم تا وقتی این گوشی کشیدش زمان‌گیری می‌شه: یه جابه‌جایی دو سه پیکسلی، و کشیدن دوباره‌ی کل شهر مثل وقت بزرگ‌نمایی، هم در نمای کامل و هم با بزرگ‌نمایی {zoom}×. «همه‌ی جلوه‌ها روشن» و «همه‌ی جلوه‌ها خاموش» کل فریم رو نشون می‌دن؛ هر جلوه نشون می‌ده چقدر به یه فریم با همه‌ی جلوه‌ها اضافه می‌کنه، پس منفی یعنی صرفه‌جویی. سایه‌ی نرم و درخشش پهن در برابر سایه‌ی تیز و درخشش باریکی قیمت می‌خورن که گوشی کند بهشون برمی‌گرده.',
  'townCheck.effect': 'جلوه',
  'townCheck.fitPan': 'جابه‌جایی',
  'townCheck.fitWhole': 'کشیدن دوباره',
  'townCheck.zoomPan': 'جابه‌جایی با {zoom}×',
  'townCheck.zoomWhole': 'کشیدن دوباره با {zoom}×',
  'townCheck.fx.all': 'همه‌ی جلوه‌ها روشن',
  'townCheck.fx.softShadows': 'سایه‌ی نرم',
  'townCheck.fx.shadows': 'سایه‌ی ماه و سایه‌روشن',
  'townCheck.fx.wideGlow': 'درخشش پهن',
  'townCheck.fx.glow': 'درخشش',
  'townCheck.fx.smooth': 'لبه‌های صاف',
  'townCheck.fx.walls': 'دیوارهای تمیز',
  'townCheck.fx.skip': 'نکشیدن چیزی که دیده نمی‌شه',
  'townCheck.fx.off': 'همه‌ی جلوه‌ها خاموش',
  'townCheck.msFine': '{n} میلی‌ثانیه',
  'townCheck.msSigned': '{n} میلی‌ثانیه',
  'townCheck.shadowMap': 'نقشه‌ی سایه‌ی ماه، یه بار موقع باز شدن شهر کشیده می‌شه: {ms}',
  'townCheck.fallback': 'روی این گوشی ماکت نورانی این‌ها رو کم می‌کنه: {effects}.',
  'townCheck.fallbackNone': 'روی این گوشی ماکت نورانی همه‌ی جلوه‌ها رو نگه می‌داره.',
  'townCheck.effectLine': '{effect}: جابه‌جایی {fitPan}، کشیدن دوباره {fitWhole}، جابه‌جایی با بزرگ‌نمایی {zoomPan}، کشیدن دوباره با بزرگ‌نمایی {zoomWhole}',
  'townCheck.landing': 'فرود یه لحظه',
  'townCheck.landingIntro': 'شهر بزرگ بدون تازه‌ترین لحظه‌اش، بعد اون لحظه همون‌جوری فرود میاد که توی برنامه: دوربین به زمین خالی پرواز می‌کنه، کلمه‌های تازه‌اش مثل نور روی قطعه‌های زمینشون می‌افتن، محله‌اش از توی مه بالا میاد و دوربین به طرفش پرواز می‌کنه. هر فریم، از اولین پرواز تا آخرِ آخرین پرواز، زمان‌گیری می‌شه، مدت جابه‌جایی هر چی باشه.',
  'townCheck.landingBar': 'حد، همون حدی که ماکت نورانی از جابه‌جایی‌های گوشی می‌خواد: میانه‌ی دست‌کم {median} fps و ۱٪ کندترینِ بالای {low} fps.',
  'townCheck.landingSize': '{size}، فرود یه لحظه',
  'townCheck.stepLanding': 'فرود یه لحظه',
  'townCheck.bar': 'حد',
  'townCheck.pass': 'قبول',
  'townCheck.fail': 'رد',
  'townCheck.landingLine': '{look}، {size}: میانه {median} fps، ۱٪ کندترین {low} fps، {frames} فریم، {missed} دیرکرده، طولانی‌ترین فریم {longest}؛ حد: {verdict}',
  'townCheck.landingStill': 'این گوشی «کاهش حرکت» رو خواسته، پس فرودها به‌جای پرواز یهو عوض شدن و به‌جای بالا اومدن کم‌کم پیدا شدن، همون‌جوری که توی برنامه.',

  // ---- the fog: new words wait for their first look (v1 ticket 07) ----
  'fog.waiting': '🌫 {n} کلمه‌ی تازه منتظرن',
  'fog.waitingOne': '🌫 ۱ کلمه‌ی تازه منتظره',
  'fog.waitingTitle': 'توی لحظه‌هات شنیده شدن ولی هنوز باهاشون آشنا نشدی. هر روز چندتاشون توی مرور میان، و وقتی یه درس رو دوباره باز کنی چندتای دیگه.',
  'fog.fewer': 'امروز کلمه‌ی تازه‌ی کمتری میاد: {n} کلمه منتظر مرورن.',
  'fog.fewerOne': 'امروز کلمه‌ی تازه‌ی کمتری میاد: ۱ کلمه منتظر مروره.',
  'fog.fewerNone': 'امروز کلمه‌ی تازه‌ی کمتری میاد: اول نوبت مرور بود.',

  // ---- دور روزانه (v1 ticket 15) ----
  'round.title': 'دور امروز',
  'round.cardsToday': '{n} کارت امروز',
  'round.cardsTodayOne': '۱ کارت امروز',
  'round.start': 'شروع دور امروز ({n} کارت)',
  'round.startOne': 'شروع دور امروز (۱ کارت)',
  'round.continue': 'ادامه‌ی دور امروز ({n} کارت مونده)',
  'round.continueOne': 'ادامه‌ی دور امروز (۱ کارت مونده)',
  'round.doneToday': 'دور امروز تموم شد. فردا دوباره سر بزن.',
  'round.fading': '{n} چراغ داره خاموش می‌شه',
  'round.fadingOne': '۱ چراغ داره خاموش می‌شه',
  'round.fadingTitle': 'کلمه‌هایی که آخرین بار ۶ یا ۷ روز پیش درست زدی: چراغشون به‌زودی خاموش می‌شه. یه جواب درست دوباره روشنش می‌کنه.',
  'round.stop': 'همین‌جا تمومش کن',
  'round.bank': 'آجرهای این دور',
  'round.brick': '{word} ← {material}',
  'round.material.fog': 'مه',
  'round.material.scaffolding': 'داربست',
  'round.material.wood': 'چوب',
  'round.material.stone': 'سنگ',
  'round.material.gold': 'طلا',
  'sign.inFog': 'توی مه',
  'sign.outOfFog': 'بیرون از مه',
  'sign.find': '{word} رو توی حرفاشون پیدا کن',
  'sign.notThat': 'این نبود؛ اینجا نشانش دادم، توی جمله.',
  'sign.means': '{word} یعنی…',
  'sign.of': '{i} / {n}',
  'sign.play': 'جمله رو بشنو',
  'sign.stop': 'جمله رو قطع کن',
  'sign.gap': 'کلمه‌ی جاافتاده رو بنویس',
  'sign.heard': 'چیزی رو که می‌شنوی بنویس',
  'sign.listen': 'پخشش کن، بعد انتخاب کن',
  'round.build': 'هرچی به دست آوردم رو بساز',
  'round.building': 'در حال ساختن…',
  'round.andMore': 'و {n} تای دیگه',
  'round.stopped': 'همین‌جا وایسادی. بقیه‌ی دور امروز منتظرته.',
  'round.complete': 'دور امروز تموم شد',
  'round.nothing': 'دور امروز چیزی نداره.',

  // ---- every moment lands in the town (v1 ticket 41) ----
  'land.lines': 'چیزی که گفته شد',
  'land.streets': 'جمله‌هایی که گفتند، با گچ کشیده شدند و شدند خیابان.',
  'land.skip': 'رد شو',
  'land.skipTitle': 'همه‌ی لحظه‌های منتظر یک‌جا فرود بیان و برو خونه.',
  'land.fogWords': '{n} کلمه توی مه منتظرن.',
  'land.fogWordsOne': '۱ کلمه توی مه منتظره.',
  'land.fogNone': 'اینجا هیچ کلمه‌ی تازه‌ای توی مه منتظر نیست.',
  'land.firstLook': 'اولین نگاه رو بنداز',
  'land.startPractice': 'تمرین رو شروع کن',
  'land.choiceNote': 'لحظه‌ات ذخیره شد. با «تمرین رو شروع کن» کلمه‌هاش رو امتحان می‌کنی، و با «باز کردن لحظه» جزئیاتش رو می‌بینی.',
  'land.lessonLoading': 'دارم کارت‌ها رو آماده می‌کنم…',
  'land.opening': 'دارم شهرت رو باز می‌کنم…',
  'land.lessonAiOut': 'سهم این ماه تموم شده، برای همین الان کارت‌ها ساخته نمی‌شن. این کلمه‌ها توی دور روزانه و شهرت میان.',
  'land.lessonFailed': 'الان کارت‌ها ساخته نشدن. این کلمه‌ها توی دور روزانه و شهرت میان.',
  'land.lessonDone': 'امروز اینجا کارت دیگه‌ای نیست.',
  'land.lessonTitle': 'نگاه اول',
  'land.lessonTitleOf': 'نگاه اول: {title}',
  'land.cardOf': 'کارت {i} از {n}',
  'land.stopped': 'همین‌جا وایسادی. جواب‌هات ذخیره شدن و محله بقیه‌ش رو امروز برات نگه می‌داره.',
  'land.lessonComplete': 'نگاه اول تموم شد',
  'land.continue': 'نگاه اول رو ادامه بده',
  'land.left': '{n} کارت برای امروز مونده',
  'land.leftOne': '۱ کارت برای امروز مونده',

  // ---- تازه‌واردها (v1 ticket 42) ----
  'newcomers.offer': 'یه تازه‌وارد دمِ دروازه‌ست: {title}، نزدیکِ {place}.',
  'newcomers.meet': 'ببینشون',
  'newcomers.else': 'یکی دیگه',
  'newcomers.notNow': 'الان نه',
  'newcomers.failed': 'تازه‌واردها این بار نتونستن بیان.',
  'newcomers.readiness.need-word': 'اول باید با یک کلمه آشنا بشی تا تازه‌واردی بیاد.',
  'newcomers.readiness.fog-open': 'برای جا باز کردن برای تازه‌وارد، یک کلمه از مه رو ببین.',
  'newcomers.readiness.fog-waits': 'نگاه‌های اولت برای روز بعد می‌مونن.',
  'newcomers.readiness.landing': 'اول لحظه‌ای رو که منتظر فرود اومدنه کامل کن.',
  'newcomers.readiness.allowance': 'تازه‌وارد منتظر سهمیه‌ی بعدی تحلیلته.',
  'newcomers.readiness.met-today': 'امروز با یکی آشنا شدی. فردا یکی دیگه می‌تونه بیاد.',
  'newcomers.readiness.not-now': 'گفتی امروز نه. فردا دروازه دوباره باز می‌شه.',
  'newcomers.readiness.action.capture': 'یک لحظه بساز',
  'newcomers.readiness.action.round': 'رفتن به دور امروز',
  'newcomers.readiness.action.landing': 'لحظه رو فرود بیار',
  'learning.roundNext': 'رفتن به دور امروز',
  'learning.roundWait': 'الان نگاه اول یا مرور مفیدی در دسترس نیست. بعداً دوباره بررسی کن.',
  'learning.roundUnavailable': 'مسیرهای یادگیری امروز بررسی نشد. صفحه رو تازه کن و دوباره امتحان کن.',
  'learning.roundChecking': 'دارم مسیرهای یادگیری امروز رو بررسی می‌کنم…',
  'newcomers.gated': 'تا مرور امروز تموم شه، پشت دروازه منتظر می‌مونن.',
  'newcomers.onTheWay': 'تازه‌واردها دارن میان به {town}…',
  'newcomers.onTheWayHome': 'تازه‌واردها دارن میان به شهرت…',
  'newcomers.settle': 'تازه‌واردها توی {town} ساکن می‌شن.',
  'newcomers.settleHome': 'تازه‌واردها توی شهرت ساکن می‌شن.',
  'newcomers.signTitle': 'تازه‌واردها: {title}',
  'newcomers.pill': 'تازه‌واردها',
  'newcomers.answerPill': 'جوابت',
  'newcomers.answeredLink': 'جوابِ {name} رو دادی: جوابت رو باز کن',
  'newcomers.answeredLinkPlain': 'جواب دادی: جوابت رو باز کن',
  'newcomers.sceneNote': 'تازه‌واردها به شهرت اومدن: یه صحنهٔ ساختگی با کلمه‌هایی که بلدی.',
  'newcomers.answer': 'جوابشون رو بده',
  'newcomers.wild': 'اینو بیرون، توی دنیای واقعی شنیدی، توی {title}.',
  'newcomers.offerNamed': '{name} دمِ دروازه‌ست: {title}، نزدیکِ {place}.',
  'newcomers.arriving': '{name} داره از دروازه میاد تو…',
  'newcomers.waiting': '{name} هنوز منتظرِ جوابته.',
  'newcomers.answerNamed': 'جوابِ {name} رو بده',
  'newcomers.settleNamed': '{name} توی {town} ساکن می‌شه.',
  'newcomers.settleNamedHome': '{name} توی شهرت ساکن می‌شه.',
  'newcomers.signNamed': '{name}، {role}',
  'land.newcomerWith': '{name}، {role}',
  'land.fogWordsAsk': '{n} کلمه توی مه منتظرن، سؤالِ {name} هم باهاشون.',
  'land.fogWordsAskOne': '۱ کلمه توی مه منتظره، سؤالِ {name} هم باهاش.',
  'record.answerTitleNamed': '{name} پرسید',
  'record.answerSend': 'جوابِ {name} رو بده',
  'tend.personWaiting': '{name} منتظرِ جوابته.',
  'tend.personHome': '{name} اینجا زندگی می‌کنه.',
  'record.answerTitle': 'جوابشون رو بده',
  'record.answerAsked': '{name} پرسید:',
  'record.answerPlaceholder': 'مثلاً: آره، یکی کنار ایستگاه هست',
  'record.answerHint': 'جوابت رو به فارسی بنویس یا بگو، یا با کلمه‌هایی که بلدی. من ادامه‌ی صحنه رو تصور می‌کنم و کلمه‌هاش رو برمی‌دارم.',
  'land.loadError': 'این لحظه الان به شهرت نرسید.',
  'land.hearScene': 'صحنه رو بشنو',
  'land.justRead': 'فقط بخونمش',
  'land.hearAgain': 'یه بار دیگه بشنو و کلمه‌های تازه رو بگیر',
  'land.sceneContinue': 'ادامه',
  'land.sceneStop': 'توقف',
  'land.sceneWith': 'با {name}',
  'land.sceneLines': '{n} خط',
  'land.sceneLinesOne': '۱ خط',
  'land.sceneYouSay': '{n} تاش رو تو می‌گی',
  'land.sceneYouSayOne': 'یکیش رو تو می‌گی',

  // ---- the first week (v1 ticket 37) ----
  'week.promise': 'تا همین موقع فردا توی شهرت: {list}.',
  'week.next': 'بعدی توی شهرت: {day}، {list}.',
  'week.promiseIn': 'تا همین موقع فردا توی {town}: {list}.',
  'week.nextIn': 'بعدی توی {town}: {day}، {list}.',
  'week.houses': '{n} تا خونه می‌تونن به {material} برسن',
  'week.house': '۱ خونه می‌تونه به {material} برسه',
  'week.more': '{n} تا به {material}',
  'week.sep': '، ',
  'week.and': ' و ',
  'week.calendar': 'فردا {word} رو درست جواب بده تا به چوب برسه، حدود ۳ روز بعدش به سنگ، و یه هفته بعد از اون به طلا.',
  'week.first.wood': 'اولین خونه‌ی چوبیت: {word}.',
  'week.first.stone': 'اولین کلمه‌ی آب‌دیده‌ات: {word}.',
  'week.first.gold': 'اولین خونه‌ی طلاییت: {word}.',
  'week.first.line': 'اولین خطی که همه‌ی کلمه‌هاش رو بلدی: {word}.',
  'week.comeback': 'از {title} که {ago} بود، حالا همه‌ی کلمه‌های {n} خط رو بلدی.',
  'week.comebackOne': 'از {title} که {ago} بود، حالا همه‌ی کلمه‌های یه خط رو بلدی.',
  'week.comebackOpen': 'بازش کن',
  'end.head.fogFirst': 'اولین کلمه‌ای که از مه بیرون اومد: {word}.',
  'end.head.fog': '{n} کلمه از مه بیرون اومدن',
  'end.head.fogOne': '۱ کلمه از مه بیرون اومد',
  'end.head.climbed': '{n} خونه بالا رفتن',
  'end.head.climbedOne': '۱ خونه بالا رفت',
  'end.head.lamps': '{n} چراغ دوباره روشن شدن',
  'end.head.lampsOne': '۱ چراغ دوباره روشن شد',
  'end.head.ivy': 'علفِ {n} خونه کنده شد',
  'end.head.ivyOne': 'علفِ ۱ خونه کنده شد',
  'end.again': 'ارزش یه نگاه دوباره رو دارن:',
  'end.againMore': '+{n}',
  'end.head.none': 'این بار چیزی جابه‌جا نشد. کلمه فقط با جواب درست بالا می‌ره، اون هم وقتی وقت مرورش رسیده باشه.',
  'end.score': '{right} از {n} درست',
  'end.chip.new': 'تازه',
  'end.chip.again': 'دوباره',
  'end.chip.lamp': 'چراغ روشن',
  'end.chip.climbed': '↑ {material}',
  'end.chipsLabel': 'چی توی شهر عوض شد',
  'feel.switch': 'صدا و لرزش',
  'feel.on': 'روشن',
  'feel.off': 'خاموش',
  'music.switch': 'موسیقی شهر',
  'music.credit': 'موسیقی شهر با نت‌هایی نواخته می‌شود از',
  // ---- the first night: the opening and the map (ticket 40) ----
  'opening.tagline': 'یه زبان رو از لحظه‌های دور و برت یاد بگیر.',
  'opening.begin': 'برای شروع بزن',
  'opening.beginHint': 'با صدا بهتره. حدود نیم دقیقه.',
  'opening.skipIntro': 'رد شدن از مقدمه',
  'opening.skip': 'رد شو',
  'opening.soundOn': 'صدا روشن',
  'opening.soundOff': 'صدا خاموش',
  'opening.tapLot': 'روی این قطعه بزن',
  'opening.cap.out': 'دیروقت بیرونی. بالای سرت چند نفر دارن حرف می‌زنن.',
  'opening.cap.quick': 'اون‌قدر تند که نمی‌شه دنبالش کرد.',
  'opening.cap.stay': 'ولی چند تا کلمه تو ذهنت می‌مونه.',
  'opening.cap.why': 'مثل همون ماهی که نشونش دادن. کلمه‌هایی که همون‌جا که اتفاق می‌افتن می‌شنوی، بیشتر یادت می‌مونن.',
  'opening.cap.lands': 'هر کلمه‌ای که می‌گیری توی دره فرود میاد، توی مه.',
  'opening.cap.house': 'یه کلمه رو یاد بگیر، خونه‌اش ساخته می‌شه.',
  'opening.cap.glimpse': 'فقط یه نگاه کوتاه. توی شهر خودت هیچ خونه‌ای ساخته نمی‌شه تا کلمه‌اش رو یاد بگیری.',
  'opening.cap.map': 'هر کی فال‌گوش وایسه، یه نقشه داره.',
  'map.keeps': 'هر کی فال‌گوش وایسه، یه نقشه داره.',
  'map.yours': 'این یکی مال توئه.',
  'map.nameTitle': 'اسم شهرت رو بذار',
  'map.askNote': 'شهرت هنوز اسم نداره. یه اسم بهش بده و اولین سنگش رو بذار.',
  'map.placeholder': 'یا هر اسمی',
  'map.hint': 'یه اسم رو بزن، یا اسم خودت رو بنویس.',
  'map.lay': 'اولین سنگ رو بذار',
  'map.laying': 'داره سنگ گذاشته می‌شه…',
  'map.later': 'بعداً اسم می‌ذارم',
  'map.unnamedHelp': 'هر وقت خواستی از منوی شهر اسمش رو بذار.',
  'map.notNow': 'الان نه',
  'map.nameInvalid': 'اسم باید بین ۱ تا ۲۴ حرف باشه، بدون نشونه‌ی پنهان.',
  'map.founded': 'بنا شده در شب {date}',
  'map.firstStone': 'اولین سنگ {name}.',
  'map.firstStoneUnnamed': 'اولین سنگ شهرت.',
  'map.stoneNote': 'یه سنگ، نه یه خونه: خونه‌ها فقط برای کلمه‌هایی ساخته می‌شن که یاد می‌گیری.',
  'map.unnamed': 'شهر تو',
  'map.toLand': 'ادامه',
  'map.example': 'دیدن یه نمونه‌ی کوتاه و موقت',
  'map.skipExample': 'رد کردن نمونه و انتخاب یه لحظه‌ی واقعی',
  'map.backToTown': 'برگرد به شهرت',
  'land.of': 'سرزمین {name}.',
  'land.empty': 'هنوز چیزی این‌جا ذخیره نشده. برای ساختن اولین لحظه، حرف زدن آدم‌ها رو ضبط کن یا چیزی رو که می‌خواستی بگی بنویس.',
  'land.namingDeferred': 'اسم شهر رو گذاشتی برای بعد. هر وقت خواستی از منوی شهر اسمش رو بذار.',
  'land.legendEmpty': 'شهرت با یه سنگ شروع می‌شه. حرف زدن آدم‌ها رو ضبط کن یا چیزی رو که می‌خواستی بگی بنویس؛ کلمه‌های لحظه‌های واقعی‌ات با یاد گرفتن خونه می‌شن.',
  'home.watchOpening': 'شروع رو دوباره ببین',
  'home.renameTown': 'اسم شهرت رو عوض کن',
  'map.hereBeWords': 'اینجا کلمه‌ها هستن',
  'map.slot': 'اولین سنگ اینجا گذاشته می‌شه',
  'map.north': 'ش',
  'map.est': 'بنا در {year}',
  'example.listening': 'داره گوش می‌ده…',
  'example.recNote': 'فقط یه نمونه‌ست: هیچی ضبط نمی‌شه.',
  'example.status': 'داره دوباره به چیزی که گرفتی گوش می‌ده…',
  'example.find': 'داره کلمه‌هایی رو که ارزش نگه داشتن دارن جدا می‌کنه…',
  'example.who.Customer': 'مشتری',
  'example.who.Waiter': 'گارسون',
  'example.try': 'یه بار با یه نمونه امتحانش کن.',
  'example.badge': 'نمونه‌ی موقت · چیزی ذخیره نمی‌شه',
  'example.temporary': 'نمونه اختیاریه: این ضبط و یادداشت ساختگی‌ان و چیزی ذخیره نمی‌شه.',
  'example.skip': 'رد کردن نمونه',
  'example.skipToReal': 'رد کردن نمونه‌ی موقت و انتخاب یه لحظه‌ی واقعی.',
  'example.stopAfter': 'تموم کن و بفرست (بعد از {n} ثانیه)',
  'example.stop': 'تموم کن و بفرست',
  'example.heard': '{n} کلمه شنیده شد.',
  'example.heardTyped': '{n} کلمه توی چیزی که می‌خواستی بگی.',
  'example.outline': 'هر کلمه یه خونه‌ست که توی مه منتظره. هنوز هیچی ساخته نشده.',
  'example.district': '{n} کلمه توی مه منتظرن. هر خونه فقط وقتی ساخته می‌شه که اولین نگاهش رو درست جواب بدی.',
  'example.readThey': 'بخون چی گفتن',
  'example.readYou': 'بخون چی می‌خواستی بگی',
  'example.card.head': 'اولین نگاه',
  'example.card.q': 'یعنی چی؟',
  'example.card.right': 'درسته.',
  'example.card.means': 'یعنی «{meaning}».',
  'example.card.pass': 'مه کنار می‌ره، داربست بالا می‌ره و چراغش روشن می‌شه.',
  'example.card.miss': 'مه می‌مونه. یه بار دیگه برای اولین نگاه برمی‌گرده.',
  'example.card.listen': 'گوش بده',
  'example.next': 'اولین نگاه بعدی',
  'example.seeTown': 'شهر امشب رو ببین',
  'example.chron.of': 'وقایع‌نامه‌ی {town}',
  'example.chron.title': 'شب اول',
  'example.chron.up': '{n} خونه داربست خوردن و {n} چراغ روشنه.',
  'example.chron.upOne': 'یه خونه داربست خورد و یه چراغ روشنه.',
  'example.chron.fog': '{n} تا هنوز توی مه منتظرن.',
  'example.chron.fogOne': 'یکی هنوز توی مه منتظره.',
  'example.chron.only': 'این فقط یه نمونه بود: کافه حالا محو می‌شه و چیزی ازش نگه داشته نمی‌شه. لحظه‌های خودت می‌مونن.',
  'example.chron.calendar': 'توی شهرت، کلمه‌ای که روز بعد دوباره درست جواب بدی به چوب می‌رسه، حدود {n} روز بعدش به سنگ، و یه هفته بعد از اون به طلا.',
  'example.nothingUp': 'امشب هیچی ساخته نشد. مه خونه‌هاش رو نگه می‌داره تا اولین نگاهشون رو درست جواب بدی.',
  'example.yours': 'حالا مال خودته',
  'example.chooseReal': 'حالا یه لحظه‌ی واقعی رو شروع کن:',
  'example.realRecord': 'ضبط یه لحظه‌ی واقعی',
  'example.realType': 'نوشتن یه لحظه‌ی واقعی',
  'example.type.title': 'چی می‌خواستی بگی؟',
  'example.type.hint': 'با زبون خودت. فارسی هم اشکالی نداره.',
  'example.type.make': 'ازش یه لحظه بساز',
  'example.type.note': 'فقط یه نمونه‌ست: هر یادداشتی همون لحظه‌ی ساختگی می‌شه.',
  'example.who.You': 'تو',
  'example.bannerHint': 'بزن تا ببینی چی گفتن',
  'example.bannerHintTyped': 'بزن تا ببینی چی می‌خواستی بگی',
  'example.theySaid': 'چیزی که گفتن',
  'example.youWanted': 'چیزی که می‌خواستی بگی',
  'example.yourNote': 'یادداشتت',
  'example.madeUp': 'یه لحظه‌ی ساختگی. کلمه‌هایی که زیرشون خط‌چینه توی مه {town} منتظرن.',
  // ---- inviting a friend (the owner, beta) ----
  'invite.menu': 'دعوت از یک دوست',
  'invite.title': 'دعوت از یک دوست',
  'invite.once': 'هر لینک فقط یک بار و تا {days} روز کار می‌کنه. خصوصی بفرستش.',
  'invite.make': 'یه لینک دعوت بساز',
  'invite.share': 'اشتراک‌گذاری',
  'invite.copy': 'کپی',
  'invite.copied': 'کپی شد',
  'invite.shownOnce': 'این لینک فقط همین الان نشون داده می‌شه. اگه گمش کردی، لغوش کن و یه لینک تازه بساز.',
  'app.name': 'فال‌گوش',
  'invite.shareTitle': 'فال‌گوش',
  'invite.shareText': 'بیا توی فال‌گوش پیشم: این لینک فقط یک بار کار می‌کنه.',
  'invite.openTitle': 'دعوت‌های باز',
  'invite.none': 'هیچ دعوتی باز نیست.',
  'invite.until': 'تا {date} کار می‌کنه',
  'invite.revoke': 'لغو',
  'invite.another': 'یه لینک دیگه بساز',
  'invite.close': 'بستن',
  'error.owner-only': 'فقط صاحب برنامه می‌تونه دعوت کنه.',
  'error.owner-invite-limit': 'برای امروز دعوت بسه. فردا دوباره امتحان کن.',
  'error.forbidden': 'به این بخش دسترسی نداری.',
  'error.json-required': 'این درخواست پذیرفته نشد. برنامه رو دوباره بارگذاری کن و دوباره امتحان کن.',
  'error.cross-site': 'این درخواست از یه سایت دیگه اومده بود، پس پذیرفته نشد. خود برنامه رو باز کن و دوباره امتحان کن.',
  'error.research-too-large': 'درخواست پژوهشی خیلی بزرگ بود. دوباره امتحان کن.',
  'error.research-inactive': 'مشارکت پژوهشی فعال نیست.',
  'error.research-invalid': 'درخواست پژوهشی معتبر نیست. دوباره امتحان کن.',
  'error.research-subject-mismatch': 'این رویداد به این شرکت‌کننده‌ی پژوهش تعلق ندارد.',
  'error.research-consent-changed': 'اجازه‌ی پژوهش در برگه‌ی دیگری تغییر کرده است. تنظیم را بررسی کن و دوباره تلاش کن.',
  'error.research-unavailable': 'ذخیره‌ی شواهد پژوهشی ممکن نشد. یادگیری‌ات همچنان کار می‌کند.',
  'error.research-range': 'بازه‌ی زمانی پژوهش معتبر نیست.',
  'error.recall-filter': 'درخواست گزارش یادآوری معتبر نیست.',
  'error.recall-range': 'بازه‌ی زمانی یادآوری معتبر نیست.',
  'error.recall-unknown': 'آن تکلیف انتقالی شناخته‌شده نیست.',
  // ---- field signals: the report sheet, the switch and the privacy line (ticket 43) ----
  'report.menu': 'اینجا یه چیزی درست نیست',
  'report.title': 'اینجا یه چیزی درست نیست',
  'report.lede': 'یه مشکل، ایده یا سؤال برای سازنده‌های فال‌گوش. همه‌ی گزارش‌ها رو می‌خونن ولی معمولاً توی خود برنامه جواب نمی‌دن — سؤالت تا فرستاده نشه روی همین دستگاه می‌مونه.',
  'report.kind': 'چیه؟',
  'report.problem': 'یه مشکل',
  'report.idea': 'یه ایده',
  'report.question': 'یه سؤال',
  'report.note': 'چی شد، یا چی کمک می‌کنه؟',
  'report.noteHint': 'این یادداشت رو سازنده‌های فال‌گوش می‌خونن. خطاهای اخیر و صفحه‌هایی که باز کردی هم باهاش می‌ره، هیچ‌وقت چیزی که ضبط کردی یا نوشتی.',
  'report.count': '{n} از {max}',
  'report.include': 'این لحظه رو هم بفرست',
  'report.includeHint': 'این لحظه با پشتیبانی به اشتراک گذاشته می‌شه تا فقط همین رو بخونیم، نه چیز دیگه‌ای. از خود لحظه می‌تونی اشتراک رو قطع کنی.',
  'report.send': 'بفرست',
  'report.sent': 'فرستاده شد، ممنون.',
  'report.saved': 'نگهش داشتیم؛ وقتی دوباره آنلاین شدی فرستاده می‌شه.',
  'report.kept': 'متن بالا همون‌طور که نوشتی نگه داشته شده — وقتی تونستی دوباره بفرستش.',
  'report.close': 'بستن',
  'diag.switch': 'فرستادن گزارش خطا',
  'privacy.line': 'فال‌گوش گزارش خطا و آمار استفاده رو می‌فرسته، هیچ‌وقت چیزی که ضبط کردی یا نوشتی. می‌تونی از ⋯ خاموشش کنی.',
  'error.report-limit': 'برای امروز گزارش بسه. ممنون؛ فردا دوباره امتحان کن.',
  'error.report-invalid': 'این گزارش فرستاده نشد. یه یادداشت کوتاه بنویس و دوباره امتحان کن.',
  'error.activity-not-found': 'این خبر دیگه این‌جا نیست. فهرست به‌روزه.',
  'error.invitation-check-too-large': 'بررسی دعوت‌نامه خیلی بزرگ بود. دوباره امتحان کن.',
  // ---- one reminder a day at most (ticket 38): the sheet, the offer, and the text the service worker puts together ----
  'reminder.menu': 'یادآورها',
  'reminder.title': 'یادآورها',
  'reminder.close': 'بستن',
  'reminder.privacy': 'یادآورها از سرویس اعلان مرورگرت (گوگل، اپل یا موزیلا) رد می‌شن؛ اون فقط می‌بینه که یکی رسیده، نه اینکه چی نوشته.',
  'reminder.when': 'کِی پنج دقیقه وقت داری؟',
  'reminder.preset.morning': 'با قهوهٔ صبح',
  'reminder.preset.lunch': 'سر ناهار',
  'reminder.preset.home': 'تو راه برگشت به خونه',
  'reminder.preset.dinner': 'بعد از شام',
  'reminder.preset.other': 'یه وقت دیگه',
  'reminder.timeAria': 'ساعت یادآور',
  'reminder.card': 'یکی از کلمه‌هام رو ازم بپرس',
  'reminder.cardHint': 'ممکنه یادآور معنی یکی از کلمه‌هات رو روی صفحهٔ قفل نشون بده.',
  'reminder.state.unsupported': 'این مرورگر نمی‌تونه یادآور بفرسته. از یه دستگاه پشتیبانی‌شدهٔ دیگه روشنشون کن.',
  'reminder.state.ios': 'روی آیفون یا آیپد، فال‌گوش رو توی سافاری باز کن، Share و بعد Add to Home Screen رو بزن؛ بعد از آیکنش بازش کن و یادآورها رو روشن کن.',
  'reminder.state.on': 'برای این گوشی روشنه.',
  'reminder.state.onSync': 'برای این گوشی روشنه، با بررسی روزانهٔ مرورگر.',
  'reminder.state.stopped': 'یادآورها روی این گوشی متوقف شدن. دوباره روشنشون کنی؟',
  'reminder.state.paused': 'فعلاً متوقفه: چند تای آخر باز نشدن.',
  'reminder.state.denied': 'اعلان‌های فال‌گوش بسته‌ان. توی تنظیمات این سایت در مرورگر اجازهٔ اعلان رو بده، بعد برگرد و دوباره بررسی کن.',
  'reminder.state.checkingCapabilities': 'دارم بررسی می‌کنم این مرورگر می‌تونه یادآور بفرسته یا نه…',
  'reminder.setupAfterCapability': 'بعد از روشن کردن یادآورها روی یه دستگاه پشتیبانی‌شده، ساعت و پرسش رو انتخاب کن. تنظیم‌های ذخیره‌شده‌ات تغییر نکرده.',
  'reminder.state.checking': 'دارم نگاه می‌کنم یادآور به این گوشی می‌رسه یا نه…',
  'reminder.state.fellBack': 'اعلان به این گوشی نرسید. به‌جاش یادآورها از بررسی روزانهٔ خود مرورگر می‌رسن، با ساعتی که چندان دقیق نیست.',
  'reminder.turnOn': 'روشن کن',
  'reminder.turnOff': 'خاموش کن',
  'reminder.turnBack': 'دوباره روشن کن',
  'reminder.tryAgain': 'دوباره امتحان کن',
  'reminder.offline': 'آفلاینی. برای روشن کردن یادآورها یا تغییر تنظیم‌هاشون، دوباره وصل شو. تنظیم‌های ذخیره‌شده‌ات تغییر نکرده.',
  'reminder.checkAgain': 'دوباره بررسی کن',
  'reminder.offer.line': 'روزی یه یادآور، وقتی خونه‌ها آمادهٔ ساختنن؟',
  'reminder.offer.button': 'یادآورها',
  'reminder.offer.dismiss': 'حالا نه',
  'reminder.push.test.title': 'یادآورها روشن شدن.',
  'reminder.push.test.body': 'یادآورها این‌جوری بهت می‌رسن.',
  'reminder.push.raid.title': 'یه ارتش دم دروازه‌هات منتظره.',
  'reminder.push.raid.body': 'از طرف {name}. تا خودش بازی نکرده دفاع کن؛ حدود {hours} ساعت وقت داری.',
  'reminder.push.raid.bodyOne': 'از طرف {name}. تا خودش بازی نکرده دفاع کن؛ حدود یک ساعت وقت داری.',
  'reminder.push.newcomers.title': 'یه تازه‌وارد دمِ دروازهٔ شهرت اومده.',
  'reminder.push.houses.cardTitle': '«{meaning}» رو چطور می‌گی؟',
  'reminder.push.houses.cardBody': '{count} خونه آمادهٔ ساختنه.',
  'reminder.push.houses.cardBodyOne': 'یه خونه آمادهٔ ساختنه.',
  'reminder.push.houses.title': '{count} خونه تو {town} آمادهٔ ساختنه.',
  'reminder.push.houses.titleOne': 'یه خونه تو {town} آمادهٔ ساختنه.',
  'reminder.push.houses.titleNoTown': '{count} خونه آمادهٔ ساختنه.',
  'reminder.push.houses.titleNoTownOne': 'یه خونه آمادهٔ ساختنه.',
  'reminder.push.houses.any': 'چند تا خونه آمادهٔ ساختنه.',
  'reminder.push.report.title': 'ارتشت از شهرِ {name} برگشته.',
  'reminder.push.report.titleNoName': 'ارتشت برگشته.',
  'reminder.push.raidIn.title': 'ارتشِ {name} دم دروازه‌هاته.',
  'reminder.push.raidIn.titleNoName': 'یه ارتش دم دروازه‌هاته.',
  'reminder.push.raidIn.body': 'تا خودش بازی نکرده دفاع کن؛ حدود {hours} ساعت وقت داری.',
  'reminder.push.raidIn.bodyOne': 'تا خودش بازی نکرده دفاع کن؛ حدود یک ساعت وقت داری.',
  'reminder.push.raidDone.title': 'حملهٔ تو به شهرِ {name}: {band}',
  'reminder.push.raidDone.titleNoName': 'حمله‌ات تموم شد: {band}',
  'reminder.push.raidDone.titleOnly': 'حملهٔ تو به شهرِ {name} تموم شد.',
  'reminder.push.raidDone.titleOnlyNoName': 'حمله‌ات تموم شد.',
  'reminder.push.raidDone.held': 'مقاومت کرد.',
  'reminder.push.raidDone.draw': 'مساوی.',
  'reminder.push.raidDone.breach': 'دیوار شکست!',
  'reminder.push.raidDone.sacked': 'غارت شد!',
  'reminder.push.raidDone.glory': '{glory}+ افتخار',
  'reminder.raidAlerts': 'اعلان حمله‌ها',
  'reminder.raidAlertsHint': 'وقتی ارتشی به دروازه‌هات می‌رسه و وقتی حملهٔ خودت تموم می‌شه خبرت می‌کنیم. هیچ‌وقت بین ۱۰ شب تا ۸ صبح نه.',
  'error.reminder-endpoint': 'سرویس اعلان این مرورگر پشتیبانی نمی‌شه، برای همین یادآورها به این گوشی نمی‌رسن.',
  'error.reminder-invalid': 'این تنظیم ذخیره نشد. دوباره امتحان کن.',
  'error.reminder-limit': 'برای امروز آزمایش بسه. فردا دوباره امتحان کن.',
};

const DICT: Record<Lang, Record<string, string>> = { en, fa };

function normalise(code: string | undefined): Lang {
  const base = String(code ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return base in DICT ? (base as Lang) : 'en';
}

let current: Lang = 'en';
/** The learner's target language, for direction of target-language inputs. */
let target = '';
/** The interface language this device had stored before this page load (the app's own), or null on a first visit. */
let stored: string | null = null;
/** A page's `?lang=` override (pageLanguage) holds against syncLanguage. */
let pinned = false;

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

/**
 * A number as the interface writes it: Persian digits (and the Persian
 * decimal separator) in the Persian interface, as its fixed strings and its
 * dates already are; unchanged in English.
 */
/** A page that is not the app (the public site) can ask for Persian digits without taking over the document's language. */
let persianDigits = false;
export const setPersianDigits = (on: boolean) => { persianDigits = on; };
export function num(n: number | string): string {
  const s = String(n);
  if (current !== 'fa' && !persianDigits) return s;
  return s.replace(/(\d)\.(\d)/g, '$1٫$2').replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)]);
}

/**
 * Translate `key`, filling `{name}` placeholders. Missing keys fall back to
 * English, then to the key. A number fills in as `num` writes it; a string
 * (a word, a date, a list) as it is.
 */
export function t(key: string, vars?: Vars): string {
  const raw = DICT[current][key] ?? en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name: string) => {
    if (!(name in vars)) return m;
    const v = vars[name];
    return typeof v === 'number' ? num(v) : String(v);
  });
}

/** Translate `key`, or return `fallback` when the dictionaries have no entry for it. */
export function tOr(key: string, fallback: string, vars?: Vars): string {
  if (!(key in DICT[current]) && !(key in en)) return fallback;
  return t(key, vars);
}

/**
 * A failed request as a line for the screen. The server sends a stable `code`
 * next to its English `error`: a known code reads `error.<code>`, filled from
 * the body's numbers. Anything else shows the server's own words in the
 * English interface only; other languages get a plain line, never English.
 */
export function serverErrorText(status: number, body?: Record<string, unknown>): string {
  const code = typeof body?.code === 'string' ? body.code : '';
  if (code && `error.${code}` in en) {
    const vars: Vars = {};
    for (const k of ['done', 'required', 'remaining']) if (typeof body?.[k] === 'number') vars[k] = body[k] as number;
    if (typeof body?.resetsAt === 'string') {
      vars.date = new Date(body.resetsAt).toLocaleDateString(current, { month: 'long', day: 'numeric', timeZone: 'UTC' });
      if (code === 'daily-allowance') {
        const timezone = typeof body?.timezone === 'string' ? body.timezone : undefined;
        vars.time = new Date(body.resetsAt).toLocaleTimeString(current, { hour: '2-digit', minute: '2-digit', ...(timezone ? { timeZone: timezone } : {}) });
        vars.timezone = timezone ?? t('gate.localTime');
      }
    }
    return t(`error.${code}`, vars);
  }
  const raw = typeof body?.error === 'string' ? body.error : '';
  if (raw && current === 'en') return raw;
  return t(status >= 500 ? 'error.server' : 'error.generic');
}

/** Any caught error, for the screen: server refusals as above, a lost connection as "offline". */
export function errorText(e: unknown): string {
  if (e instanceof HttpError) return e.message;
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline || (e instanceof TypeError && /fetch|network|load failed/i.test(e.message))) return t('error.offline');
  const msg = e instanceof Error ? e.message : '';
  return msg && current === 'en' ? msg : t('error.generic');
}

/** A browser error's own words (microphone, device storage): only the English interface shows them. */
export function browserDetail(e: unknown): string {
  return current === 'en' && e instanceof Error ? e.message : '';
}

/**
 * An error kept as text from an earlier try (a draft's last try): a lost
 * connection reads as offline, and another interface language never shows
 * text that was stored in English.
 */
export function storedErrorText(s: string): string {
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(s)) return t('error.offline');
  return current === 'en' || /[؀-ۿ]/.test(s) ? s : t('error.generic');
}

/** The learner's target language code (empty until the session is known). */
export function currentTarget(): string {
  return target;
}

export function currentLang(): Lang {
  return current;
}

export function dirFor(code: string | undefined): 'rtl' | 'ltr' {
  return RTL.has(normaliseAny(code)) ? 'rtl' : 'ltr';
}

function normaliseAny(code: string | undefined): string {
  return String(code ?? '').trim().toLowerCase().split(/[-_]/)[0];
}

/** Direction for an answer input in the target language ('auto' while the target is unknown). */
export function targetDir(): 'rtl' | 'ltr' | 'auto' {
  if (!target) return 'auto';
  return RTL.has(target) ? 'rtl' : 'ltr';
}

/** BCP-47 code for `lang=` on target-language inputs, or undefined while unknown. */
export function targetLangAttr(): string | undefined {
  return target || undefined;
}

export function setTargetLanguage(code: string | undefined) {
  const next = normaliseAny(code);
  const changed = next !== target;
  target = next;
  // A typed-answer box already on screen (a cold load straight into a typed
  // card) takes its `lang`/`dir` from the target: re-render it once known.
  if (changed && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('li-language', { detail: current }));
}

/**
 * Apply an interface language: document `lang`/`dir`, local seed for the next
 * cold start (unless `remember` is false), and a `li-language` event so open
 * views re-render.
 */
export function setLanguage(code: string | undefined, remember = true) {
  const next = normalise(code);
  const changed = next !== current;
  current = next;
  const el = document.documentElement;
  el.lang = next;
  el.dir = dirFor(next);
  nameApp(next);
  if (remember) try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode — direction still applies */ }
  if (changed) window.dispatchEvent(new CustomEvent('li-language', { detail: next }));
}

/**
 * The app's name follows the interface: the tab title, the name Safari gives a
 * home-screen app, and the manifest an Android install reads (فال‌گوش in Persian).
 */
function nameApp(lang: string) {
  const name = t('app.name');
  document.title = name;
  let apple = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (!apple) {
    apple = document.createElement('meta');
    apple.name = 'apple-mobile-web-app-title';
    document.head.append(apple);
  }
  apple.content = name;
  const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifest) manifest.href = lang === 'fa' ? '/manifest.fa.webmanifest' : '/manifest.webmanifest';
}

/** Seed from the last known language before any request, so there is no flash of English. */
export function initLanguage() {
  let seed: string | null = null;
  try { seed = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
  stored = seed;
  setLanguage(seed ?? 'en');
}

/**
 * The interface language of a page that opens without an account (the town
 * phone check): a `?lang=` override, else the app's language on this device,
 * else the browser's. Neither the override nor the browser's language is
 * stored as the app's; an override also holds when syncLanguage answers.
 */
export function pageLanguage(override?: string | null): Lang {
  if (override) pinned = true;
  const browser = typeof navigator !== 'undefined' ? navigator.language : '';
  setLanguage(override || stored || browser || 'en', !override && !!stored);
  return current;
}

let pairs: LanguagePairInfo[] = [];
let pairsLoaded = false;

/** The serialised language table from the server (empty until `/api/health` answers). */
export function knownPairs(): LanguagePairInfo[] {
  return pairs;
}

export function pairFor(code: string | undefined): LanguagePairInfo | undefined {
  const base = normaliseAny(code);
  return pairs.find((p) => p.target === base);
}

/** Load the pair table once; safe to call from several views. */
export async function loadPairs(): Promise<LanguagePairInfo[]> {
  if (pairsLoaded) return pairs;
  const h = await api.health();
  if (h.pairs?.length) pairs = h.pairs;
  pairsLoaded = true;
  return pairs;
}

/**
 * Resolve the interface language for whoever is using the app: the signed-in
 * learner's pair in beta mode, the instance's own pair in personal mode.
 */
export async function syncLanguage(): Promise<void> {
  let health: Awaited<ReturnType<typeof api.health>> | undefined;
  try {
    health = await api.health();
  } catch {
    return; // offline: keep the seeded language
  }
  if (health.pairs?.length) { pairs = health.pairs; pairsLoaded = true; }
  if (!health.beta) {
    setTargetLanguage(health.target);
    if (!pinned) setLanguage(health.interface ?? health.explain ?? 'en');
    return;
  }
  try {
    const { user } = await api.me();
    // Signed out (join/sign-in screens): the join form picks the language.
    if (!user) return;
    setTargetLanguage(user.target);
    if (!pinned) setLanguage(user.explain);
  } catch {
    // Signed out (join/sign-in screens): the join form picks the language.
  }
}
